import React from "react";
import autoBind from "react-autobind";
import _ from "lodash";
import Modal from "react-responsive-modal";
import { Button, ButtonToolbar } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { v4 as uuidv4 } from "uuid";

import env from "../../constants/env";
import { showConfirm } from "../../actions/alertActions";
import { setImageHotspots, setImageKenBurnsEffect, setImageKenBurnsEffectVariants } from "../../actions/actions";
import { getLs, setLs, removeLs } from "../../utils/utils";
import { isFutureEnabled } from "../../utils/featureFlags";
import ColorPicker from "../shared/colorPicker";
import PopoutWindowPortal from "../shared/popoutWindowPortal";
import AudioWaveform from "./AudioWaveform/AudioWaveform";
import {
	estimateImageSceneDurationMs,
	evaluateImageSceneAtTime,
	getArrowPoints,
	getRelevantCameraGuidsAtTime,
	getScheduledHotspotGuidsAtTime
} from "./mediaMarkupPreviewUtils";

const EDITOR_CANVAS_PADDING_DEFAULT = 220;
const EDITOR_CANVAS_PADDING_KEY = "mediaMarkupV2CanvasPadding";
const EDITOR_CANVAS_PADDING_OPTIONS = [0, 80, 120, 180, 220, 300, 420, 600, 900];
const MIN_ITEM_SIZE = 8;
const ZOOM_OPTIONS = [25, 33, 50, 66, 75, 90, 100, 110, 125, 150, 200];
const BORDER_WIDTH_OPTIONS = Array.from({ length: 17 }, (_, index) => index);
const BORDER_RADIUS_OPTIONS = Array.from({ length: 65 }, (_, index) => index);
const CAMERA_PAN_STEP = 40;
const CAMERA_ZOOM_IN_FACTOR = 0.9;
const CAMERA_ZOOM_OUT_FACTOR = 1.1;
const LAYOUT_MODE_KEY = "mediaMarkupV2LayoutMode";
const LEFT_PANEL_COLLAPSED_KEY = "mediaMarkupV2LeftPanelCollapsed";
const RIGHT_PANEL_COLLAPSED_KEY = "mediaMarkupV2RightPanelCollapsed";
const PREVIEW_DEVICE_PRESET_KEY = "mediaMarkupV2PreviewDevicePreset";
const PREVIEW_DEVICE_ORIENTATION_KEY = "mediaMarkupV2PreviewDeviceOrientation";
const ACTIVE_TIMELINE_FILTER_KEY = "mediaMarkupV2ShowOnlyActiveCanvasItems";
const AUDIO_MUTED_KEY = "mediaMarkupV2AudioMuted";
const EDIT_SCALE_KEY = "mediaMarkupV2EditScale";
const PREVIEW_SCALE_KEY = "mediaMarkupV2PreviewScale";
const OVERLAY_STYLE_PRESET_KEY = "mediaMarkupV2OverlayStylePreset";
const DEVICE_GUIDES_VISIBLE_KEY = "mediaMarkupV2DeviceGuidesVisible";
const DEVICE_GUIDES_SELECTED_KEY = "mediaMarkupV2DeviceGuidesSelected";
const DEVICE_GUIDES_PANEL_EXPANDED_KEY = "mediaMarkupV2DeviceGuidesPanelExpanded";
const CAMERA_VARIANT_KEY = "mediaMarkupV2CameraVariant";
const STORAGE_VERSION_KEY = "mediaMarkupV2StorageVersion";
const STORAGE_VERSION = "2026-05-07-v2-layout-reset-2";
const LAYOUT_MODE_SPLIT = "split";
const LAYOUT_MODE_STACKED_RIGHT_SPLIT = "stackedRightSplit";
const LAYOUT_MODE_STACKED_RIGHT_APPEND = "stackedRightAppend";
const LEGACY_LAYOUT_MODE_STACKED_RIGHT = "stackedRight";
const ITEM_LIST_PANEL_WIDTH_KEY = "mediaMarkupV2ItemListWidth";
const PROPERTIES_PANEL_WIDTH_KEY = "mediaMarkupV2PropertiesWidth";
const ITEM_LIST_PANEL_DEFAULT_WIDTH = 320;
const PROPERTIES_PANEL_DEFAULT_WIDTH = 360;
const ITEM_LIST_PANEL_MIN_WIDTH = 240;
const PROPERTIES_PANEL_MIN_WIDTH = 300;
const ITEM_LIST_PANEL_MAX_WIDTH = 520;
const PROPERTIES_PANEL_MAX_WIDTH = 560;
const WORKSPACE_CANVAS_MIN_WIDTH = 420;
const PANEL_RESIZER_WIDTH = 12;
const DOCUMENT_HISTORY_LIMIT = 100;
const RESETTABLE_STORAGE_KEYS = [
	EDITOR_CANVAS_PADDING_KEY,
	LAYOUT_MODE_KEY,
	LEFT_PANEL_COLLAPSED_KEY,
	RIGHT_PANEL_COLLAPSED_KEY,
	PREVIEW_DEVICE_PRESET_KEY,
	PREVIEW_DEVICE_ORIENTATION_KEY,
	ACTIVE_TIMELINE_FILTER_KEY,
	AUDIO_MUTED_KEY,
	EDIT_SCALE_KEY,
	PREVIEW_SCALE_KEY,
	OVERLAY_STYLE_PRESET_KEY,
	DEVICE_GUIDES_VISIBLE_KEY,
	DEVICE_GUIDES_SELECTED_KEY,
	DEVICE_GUIDES_PANEL_EXPANDED_KEY,
	CAMERA_VARIANT_KEY,
	ITEM_LIST_PANEL_WIDTH_KEY,
	PROPERTIES_PANEL_WIDTH_KEY,
	"mediaMarkupV2ShowLabels",
	"mediaMarkupV2WaveformCollapsed"
];
const CAMERA_STYLE = {
	borderColor: "rgba(15,118,110,1)",
	borderFillColor: "rgba(15,118,110,0.06)",
	borderWidth: 3,
	borderRadius: 0
};
const AUTHORING_CANVAS_BACKGROUND = {
	background: "linear-gradient(45deg, #eef2f7 25%, transparent 25%, transparent 75%, #eef2f7 75%, #eef2f7), linear-gradient(45deg, #eef2f7 25%, transparent 25%, transparent 75%, #eef2f7 75%, #eef2f7)",
	backgroundSize: "24px 24px",
	backgroundPosition: "0 0, 12px 12px"
};
const PREVIEW_DEVICE_PRESETS = [
	{ value: "responsive", label: "Responsive", width: 0, height: 0, kind: "responsive" },
	{ value: "desktop-hd", label: "Desktop HD", width: 1280, height: 720, kind: "desktop" },
	{ value: "desktop-full-hd", label: "Desktop Full HD", width: 1920, height: 1080, kind: "desktop" },
	{ value: "iphone-se", label: "iPhone SE", width: 375, height: 667, kind: "phone" },
	{ value: "iphone-14-pro", label: "iPhone 14 Pro", width: 393, height: 852, kind: "phone" },
	{ value: "iphone-14-pro-max", label: "iPhone 14 Pro Max", width: 430, height: 932, kind: "phone" },
	{ value: "pixel-7", label: "Pixel 7", width: 412, height: 915, kind: "phone" },
	{ value: "galaxy-s8-plus", label: "Galaxy S8+", width: 360, height: 740, kind: "phone" },
	{ value: "ipad-mini", label: "iPad Mini", width: 768, height: 1024, kind: "tablet" },
	{ value: "ipad-air", label: "iPad Air", width: 820, height: 1180, kind: "tablet" },
	{ value: "ipad-pro-11", label: "iPad Pro 11", width: 834, height: 1194, kind: "tablet" },
	{ value: "ipad-pro-12-9", label: "iPad Pro 12.9", width: 1024, height: 1366, kind: "tablet" }
];
const DEVICE_GUIDE_OPTIONS = [
	{ id: "desktop-16-9", label: "Desktop 16:9", width: 1280, height: 720, color: "#2563eb" },
	{ id: "phone-landscape", label: "Phone Landscape", width: 852, height: 393, color: "#16a34a" },
	{ id: "phone-portrait", label: "Phone Portrait", width: 393, height: 852, color: "#d97706" },
	{ id: "tablet-portrait", label: "Tablet Portrait", width: 820, height: 1180, color: "#7c3aed" }
];
const CAMERA_VARIANT_BASE = "base";
const CAMERA_VARIANT_PORTRAIT = "portrait";
const CAMERA_VARIANT_OPTIONS = [
	{ value: CAMERA_VARIANT_BASE, label: "Base", targetWidth: 1920, targetHeight: 1080 },
	{ value: CAMERA_VARIANT_PORTRAIT, label: "Portrait", targetWidth: 393, targetHeight: 852 }
];
const DEFAULT_DEVICE_GUIDE_IDS = ["desktop-16-9", "phone-landscape", "phone-portrait"];
const OVERLAY_SHAPE_OPTIONS = [
	{ value: "rectangle", label: "Rectangle" },
	{ value: "arrow", label: "Arrow" },
	{ value: "ellipse", label: "Ellipse" },
	{ value: "diamond", label: "Diamond" },
	{ value: "line", label: "Line" }
];
const OVERLAY_STYLE_PRESETS = {
	highlight: {
		label: "Highlight",
		borderColor: "blue",
		borderWidth: 5,
		borderRadius: 0,
		fillColor: "transparent"
	},
	hotspot: {
		label: "Hotspot",
		borderColor: "darkorange",
		borderWidth: 5,
		borderRadius: 10,
		fillColor: "transparent"
	},
	info: {
		label: "Info",
		borderColor: "rgba(14,165,233,1)",
		borderWidth: 5,
		borderRadius: 12,
		fillColor: "rgba(14,165,233,0.14)"
	},
	success: {
		label: "Success",
		borderColor: "rgba(22,163,74,1)",
		borderWidth: 5,
		borderRadius: 12,
		fillColor: "rgba(22,163,74,0.14)"
	},
	warning: {
		label: "Warning",
		borderColor: "rgba(234,88,12,1)",
		borderWidth: 5,
		borderRadius: 12,
		fillColor: "rgba(251,146,60,0.16)"
	}
};
const COLOR_PICK_TARGETS = {
	border: "border",
	fill: "fill"
};
const CAMERA_START_SOURCE_PREVIOUS_CAMERA_END = "previousCameraEnd";

function clampValue(value, min, max) {
	return Math.min(Math.max(value, min), max);
}

function normalizeLayoutMode(layoutMode = "") {
	if (layoutMode === LAYOUT_MODE_STACKED_RIGHT_APPEND) {
		return LAYOUT_MODE_STACKED_RIGHT_APPEND;
	}

	if (layoutMode === LAYOUT_MODE_STACKED_RIGHT_SPLIT || layoutMode === LEGACY_LAYOUT_MODE_STACKED_RIGHT) {
		return LAYOUT_MODE_STACKED_RIGHT_SPLIT;
	}

	return LAYOUT_MODE_SPLIT;
}

function normalizeRotation(rotationValue = 0) {
	const numericRotation = Number(rotationValue);
	if (!Number.isFinite(numericRotation)) {
		return 0;
	}

	let normalizedRotation = numericRotation % 360;
	if (normalizedRotation > 180) {
		normalizedRotation -= 360;
	} else if (normalizedRotation < -180) {
		normalizedRotation += 360;
	}

	return normalizedRotation;
}

function rotatePointAroundCenter(pointX, pointY, centerX, centerY, rotationDegrees = 0) {
	const radians = (Number(rotationDegrees || 0) * Math.PI) / 180;
	const cos = Math.cos(radians);
	const sin = Math.sin(radians);
	const translatedX = pointX - centerX;
	const translatedY = pointY - centerY;

	return {
		x: centerX + (translatedX * cos) - (translatedY * sin),
		y: centerY + (translatedX * sin) + (translatedY * cos)
	};
}

function getDefaultOverlayStylePreset(componentType = "highlight") {
	return componentType === "hotspot" ? "hotspot" : "highlight";
}

function normalizeOverlayStylePreset(value = "", componentType = "highlight") {
	if (value === "custom") {
		return "custom";
	}

	return OVERLAY_STYLE_PRESETS[value] ? value : getDefaultOverlayStylePreset(componentType);
}

function getOverlayStylePresetDefinition(componentType = "highlight", presetName = "") {
	const presetKey = normalizeOverlayStylePreset(presetName, componentType);
	const fallbackPresetKey = getDefaultOverlayStylePreset(componentType);
	const resolvedPresetKey = presetKey === "custom" ? fallbackPresetKey : presetKey;

	return Object.assign({
		key: resolvedPresetKey
	}, OVERLAY_STYLE_PRESETS[resolvedPresetKey] || OVERLAY_STYLE_PRESETS[fallbackPresetKey]);
}

function shapeSupportsBorderRadius(shape = "rectangle") {
	return shape === "rectangle";
}

function shapeSupportsFill(shape = "rectangle") {
	return shape !== "line";
}

function getShapeLabel(shape = "rectangle") {
	switch ((shape || "").toLowerCase()) {
	case "ellipse":
		return "Ellipse";
	case "diamond":
		return "Diamond";
	case "arrow":
		return "Arrow";
	case "line":
		return "Line";
	default:
		return "Rect";
	}
}

function getDiamondPoints(coords = {}) {
	const x = Number(coords.x) || 0;
	const y = Number(coords.y) || 0;
	const width = Number(coords.width) || 0;
	const height = Number(coords.height) || 0;
	const midX = x + (width * 0.5);
	const midY = y + (height * 0.5);

	return `${midX} ${y},${x + width} ${midY},${midX} ${y + height},${x} ${midY}`;
}

function normalizeColorToken(colorValue = "") {
	return `${colorValue || ""}`.trim().toLowerCase().replace(/\s+/g, "");
}

function colorsEquivalent(colorA = "", colorB = "") {
	if (isTransparentColor(colorA) && isTransparentColor(colorB)) {
		return true;
	}

	return normalizeColorToken(colorA) === normalizeColorToken(colorB);
}

function inferOverlayStylePresetFromStyle(style = {}) {
	const borderColor = _.get(style, "borderColor", "");
	const borderWidth = Number(_.get(style, "borderWidth", 0)) || 0;
	const borderRadius = Number(_.get(style, "borderRadius", 0)) || 0;
	const borderFillColor = _.get(style, "borderFillColor", "transparent");

	const presetKey = Object.keys(OVERLAY_STYLE_PRESETS).find((key) => {
		const preset = OVERLAY_STYLE_PRESETS[key];

		return colorsEquivalent(borderColor, preset.borderColor)
			&& borderWidth === (Number(preset.borderWidth) || 0)
			&& borderRadius === (Number(preset.borderRadius) || 0)
			&& colorsEquivalent(borderFillColor, preset.fillColor);
	});

	return presetKey || "custom";
}

function getDeviceGuideLabelMetrics(guideRect, guide, index = 0) {
	const labelText = `${guide.label} • ${guide.width} x ${guide.height}`;
	return {
		labelText,
		labelX: guideRect.x + 8,
		labelY: guideRect.y + 6 + (index * 18),
		labelWidth: Math.max(160, Math.round((labelText.length * 6.3) + 18)),
		labelHeight: 16
	};
}

function inferOverlayStylePreset(item = {}) {
	return inferOverlayStylePresetFromStyle({
		borderColor: _.get(item, "style.borderColor", ""),
		borderWidth: _.get(item, "style.borderWidth", 0),
		borderRadius: _.get(item, "style.borderRadius", 0),
		borderFillColor: _.get(item, "style.borderFillColor", "transparent")
	});
}

function getOverlayDefaults(componentType = "highlight", presetName = "") {
	const preset = getOverlayStylePresetDefinition(componentType, presetName);

	return {
		shape: "rectangle",
		borderColor: preset.borderColor,
		borderWidth: preset.borderWidth,
		borderRadius: preset.borderRadius,
		fillColor: preset.fillColor
	};
}

function renderOverlaySvgElement(shape = "rectangle", coords = {}, style = {}) {
	const normalizedShape = (shape || "rectangle").toLowerCase();
	const x = Number(coords.x) || 0;
	const y = Number(coords.y) || 0;
	const width = Number(coords.width) || 0;
	const height = Number(coords.height) || 0;
	const sharedProps = {
		strokeWidth: style.strokeWidth,
		stroke: style.stroke,
		fill: style.fill,
		opacity: style.opacity
	};

	if (normalizedShape === "ellipse") {
		return (
			<ellipse
				cx={x + (width * 0.5)}
				cy={y + (height * 0.5)}
				rx={Math.abs(width) * 0.5}
				ry={Math.abs(height) * 0.5}
				{...sharedProps}
			/>
		);
	}

	if (normalizedShape === "diamond") {
		return (
			<polygon
				points={getDiamondPoints(coords)}
				strokeLinejoin="round"
				{...sharedProps}
			/>
		);
	}

	if (normalizedShape === "arrow") {
		return (
			<polygon
				points={getArrowPoints(coords)}
				strokeLinejoin="round"
				{...sharedProps}
			/>
		);
	}

	if (normalizedShape === "line") {
		return (
			<line
				x1={x}
				y1={y}
				x2={x + width}
				y2={y + height}
				strokeLinecap="round"
				strokeLinejoin="round"
				{...sharedProps}
				fill="none"
			/>
		);
	}

	return (
		<rect
			x={x}
			y={y}
			width={width}
			height={height}
			rx={style.borderRadius}
			{...sharedProps}
		/>
	);
}

function isTransparentColor(colorValue = "") {
	const value = `${colorValue || ""}`.trim().toLowerCase();
	return !value || value === "transparent" || value === "rgba(0,0,0,0)" || value === "rgba(0, 0, 0, 0)";
}

function colorResultToRgba(colorResult, fallback = "rgba(0,0,0,1)") {
	if (typeof colorResult === "string" && colorResult.trim()) {
		return colorResult;
	}

	const rgb = colorResult && colorResult.rgb ? colorResult.rgb : null;
	if (!rgb) {
		return fallback;
	}

	const alpha = rgb.a !== undefined ? rgb.a : 1;
	return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
}

function readStoredBoolean(key, fallbackValue) {
	const storedValue = getLs(key);
	if (storedValue === null || storedValue === undefined || storedValue === "") {
		return fallbackValue;
	}
	return storedValue === "true";
}

function readStoredNumber(key, fallbackValue) {
	const storedValue = Number(getLs(key));
	return Number.isFinite(storedValue) && storedValue > 0 ? storedValue : fallbackValue;
}

function readStoredString(key, fallbackValue) {
	const storedValue = getLs(key);
	return _.isString(storedValue) && storedValue.trim() !== "" ? storedValue : fallbackValue;
}

function resetMarkupV2StoredUiStateIfNeeded() {
	const storedVersion = getLs(STORAGE_VERSION_KEY);
	if (storedVersion === STORAGE_VERSION) {
		return;
	}

	RESETTABLE_STORAGE_KEYS.forEach((key) => {
		removeLs(key);
	});
	setLs(STORAGE_VERSION_KEY, STORAGE_VERSION);
}

function normalizeDeviceGuideIds(value = "") {
	if (value === "__none__") {
		return [];
	}

	const requestedIds = `${value || ""}`
		.split(",")
		.map((id) => id.trim())
		.filter(Boolean);
	const allowedIds = new Set(DEVICE_GUIDE_OPTIONS.map((guide) => guide.id));
	const filteredIds = requestedIds.filter((id) => allowedIds.has(id));

	return filteredIds.length > 0 ? filteredIds : DEFAULT_DEVICE_GUIDE_IDS;
}

function normalizePreviewDevicePreset(value = "") {
	return PREVIEW_DEVICE_PRESETS.find((preset) => preset.value === value)
		? value
		: "responsive";
}

function normalizePreviewOrientation(value = "") {
	return value === "landscape" ? "landscape" : "portrait";
}

function normalizeCameraVariantKey(value = "") {
	return CAMERA_VARIANT_OPTIONS.find((option) => option.value === value)
		? value
		: CAMERA_VARIANT_BASE;
}

function getCameraVariantOption(variantKey = CAMERA_VARIANT_BASE) {
	return CAMERA_VARIANT_OPTIONS.find((option) => option.value === variantKey) || CAMERA_VARIANT_OPTIONS[0];
}

function getPreviewDevicePreset(value = "responsive") {
	return PREVIEW_DEVICE_PRESETS.find((preset) => preset.value === value) || PREVIEW_DEVICE_PRESETS[0];
}

function cloneArray(value) {
	return Array.isArray(value) ? _.cloneDeep(value) : [];
}

function normalizeLegacyKenBurnsEffects(imageHotspots = [], imageKenBurnsEffect = null) {
	if (Array.isArray(imageKenBurnsEffect)) {
		return _.cloneDeep(imageKenBurnsEffect);
	}

	if (!imageKenBurnsEffect || typeof imageKenBurnsEffect !== "object") {
		return [];
	}

	if (imageKenBurnsEffect.startTime === undefined && imageKenBurnsEffect.camera) {
		return [_.cloneDeep(imageKenBurnsEffect)];
	}

	const firstHotspot = Array.isArray(imageHotspots) && imageHotspots.length > 0 ? imageHotspots[0] : null;
	const fallbackCamera = firstHotspot && firstHotspot.coords
		? {
			end: {
				x: firstHotspot.coords.x - 75,
				y: firstHotspot.coords.y - 150,
				width: firstHotspot.coords.width + 150,
				height: firstHotspot.coords.height + 300
			}
		}
		: undefined;

	return [Object.assign({}, {
		id: "camera1",
		startTime: 0,
		endTime: 0,
		duration: 0,
		camera: fallbackCamera,
		guid: firstHotspot && firstHotspot.guid ? firstHotspot.guid : uuidv4()
	}, _.cloneDeep(imageKenBurnsEffect))];
}

function normalizeCameraVariantOverrides(value = null) {
	if (!_.isPlainObject(value)) {
		return {};
	}

	return Object.keys(value).reduce((result, variantKey) => {
		if (variantKey === CAMERA_VARIANT_BASE) {
			return result;
		}

		const variantEntry = value[variantKey];
		const cameraEntries = _.isPlainObject(variantEntry?.cameras)
			? variantEntry.cameras
			: (_.isPlainObject(variantEntry) ? variantEntry : null);
		if (!cameraEntries) {
			return result;
		}

		const normalizedCameras = Object.keys(cameraEntries).reduce((cameraResult, guid) => {
			const override = cameraEntries[guid];
			if (!_.isPlainObject(override)) {
				return cameraResult;
			}

			const normalizedOverride = {};
			["x", "y", "width", "height", "rotation"].forEach((field) => {
				const numericValue = Number(override[field]);
				if (Number.isFinite(numericValue)) {
					normalizedOverride[field] = numericValue;
				}
			});

			if (!_.isEmpty(normalizedOverride)) {
				cameraResult[guid] = normalizedOverride;
			}
			return cameraResult;
		}, {});

		if (!_.isEmpty(normalizedCameras)) {
			result[variantKey] = {
				cameras: normalizedCameras
			};
		}
		return result;
	}, {});
}

function buildCameraVariantStatePatch(variantState = {}, variantKey, nextCameras = {}) {
	const nextState = _.cloneDeep(variantState || {});
	if (_.isEmpty(nextCameras)) {
		delete nextState[variantKey];
		return nextState;
	}

	nextState[variantKey] = {
		cameras: nextCameras
	};
	return nextState;
}

function fitRectToAspect(rect = {}, targetAspectRatio = 1) {
	const safeTargetAspect = Number(targetAspectRatio) || 1;
	const width = Math.max(Number(rect.width) || 0, MIN_ITEM_SIZE);
	const height = Math.max(Number(rect.height) || 0, MIN_ITEM_SIZE);
	const centerX = (Number(rect.x) || 0) + (width / 2);
	const centerY = (Number(rect.y) || 0) + (height / 2);
	const currentAspect = width / Math.max(height, 1);
	let nextWidth = width;
	let nextHeight = height;

	if (currentAspect > safeTargetAspect) {
		nextHeight = width / safeTargetAspect;
	} else {
		nextWidth = height * safeTargetAspect;
	}

	return {
		x: Math.round(centerX - (nextWidth / 2)),
		y: Math.round(centerY - (nextHeight / 2)),
		width: Math.round(nextWidth),
		height: Math.round(nextHeight)
	};
}

function buildItemSummaryLabel(kind = "overlay", item = {}) {
	if (kind === "camera") {
		return item.id || "Camera";
	}

	const shapeLabel = getShapeLabel(item.shape || "rectangle");
	return `${item.id || "Overlay"} • ${shapeLabel}`;
}

function formatAspectRatioLabel(aspectRatio = 0) {
	const safeAspectRatio = Number(aspectRatio) || 0;
	if (!safeAspectRatio) {
		return "0.00:1";
	}

	return `${safeAspectRatio.toFixed(2)}:1`;
}

export default class MarkupImageModalV2 extends React.PureComponent {
	constructor(props) {
		super(props);
		autoBind(this);
		resetMarkupV2StoredUiStateIfNeeded();

		const initialComponentType = "highlight";
		const initialStylePreset = normalizeOverlayStylePreset(
			readStoredString(OVERLAY_STYLE_PRESET_KEY, getDefaultOverlayStylePreset(initialComponentType)),
			initialComponentType
		);
		const defaults = getOverlayDefaults(initialComponentType, initialStylePreset);
		const legacySharedScale = readStoredNumber(PREVIEW_SCALE_KEY, 100);
		const initialDeviceGuideIds = normalizeDeviceGuideIds(readStoredString(DEVICE_GUIDES_SELECTED_KEY, DEFAULT_DEVICE_GUIDE_IDS.join(",")));
		const initialCameraVariant = normalizeCameraVariantKey(readStoredString(CAMERA_VARIANT_KEY, CAMERA_VARIANT_BASE));
		const storedCanvasPadding = parseInt(getLs(EDITOR_CANVAS_PADDING_KEY), 10);
		const initialCanvasPadding = Number.isFinite(storedCanvasPadding)
			? clampValue(storedCanvasPadding, 0, 1600)
			: EDITOR_CANVAS_PADDING_DEFAULT;

			this.canvasSvgRef = React.createRef();
			this.canvasPaneRef = React.createRef();
			this.workspaceRef = React.createRef();
			this.inlineRenameInputRef = React.createRef();
			this.audioWaveformController = null;
			this.canvasPointerDrag = null;
			this.panelResizeDrag = null;
			this.colorSampleCanvas = null;
			this.colorSampleContext = null;
			this.colorSampleUrl = "";
			this.colorSamplePromise = null;
			this.previewAnimationFrame = null;
			this.previewPlaybackStartWallClock = 0;
			this.previewPlaybackStartTimeMs = 0;
		this.skipCanvasClick = false;
		this.pendingInlineRenameActivation = false;
		this.suppressNextAudioPauseStop = false;
		this.ignoreNextAudioCursorSync = false;
		this.ignoreNextAudioTimeSync = false;
		this.lastAudioTimeMs = 0;
		this.keyboardNudgeState = null;
		this.pendingDocumentHistorySnapshot = null;

			this.state = {
				imageSize: null,
				imageHotspots: cloneArray(props.imageHotspots),
				imageKenBurnsEffect: normalizeLegacyKenBurnsEffects(props.imageHotspots, props.imageKenBurnsEffect),
				imageKenBurnsEffectVariants: normalizeCameraVariantOverrides(props.imageKenBurnsEffectVariants),
				historyPast: [],
				historyFuture: [],
				selectedNumericDrafts: null,
				selectedItemKind: "",
				selectedItemGuid: "",
				renamingItemKind: "",
				renamingItemGuid: "",
				renamingSurface: "",
				renamingItemValue: "",
				layoutMode: normalizeLayoutMode(getLs(LAYOUT_MODE_KEY) || LAYOUT_MODE_SPLIT),
				leftPanelCollapsed: readStoredBoolean(LEFT_PANEL_COLLAPSED_KEY, false),
				rightPanelCollapsed: readStoredBoolean(RIGHT_PANEL_COLLAPSED_KEY, false),
				itemListPanelWidth: readStoredNumber(ITEM_LIST_PANEL_WIDTH_KEY, ITEM_LIST_PANEL_DEFAULT_WIDTH),
				propertiesPanelWidth: readStoredNumber(PROPERTIES_PANEL_WIDTH_KEY, PROPERTIES_PANEL_DEFAULT_WIDTH),
				componentType: initialComponentType,
				shape: defaults.shape,
				stylePreset: initialStylePreset,
				borderColor: defaults.borderColor,
				borderWidth: defaults.borderWidth,
			borderRadius: defaults.borderRadius,
			fillEnabled: false,
			fillColor: defaults.fillColor,
			colorPickTarget: "",
			autoCreateCamera: false,
			showLabels: readStoredBoolean("mediaMarkupV2ShowLabels", true),
			showOnlyActiveCanvasItems: readStoredBoolean(ACTIVE_TIMELINE_FILTER_KEY, false),
			audioMuted: readStoredBoolean(AUDIO_MUTED_KEY, false),
			showDeviceGuides: readStoredBoolean(DEVICE_GUIDES_VISIBLE_KEY, false),
			deviceGuidesPanelExpanded: readStoredBoolean(DEVICE_GUIDES_PANEL_EXPANDED_KEY, false),
			activeDeviceGuideIds: initialDeviceGuideIds,
			activeCameraVariant: initialCameraVariant,
			canvasPadding: initialCanvasPadding,
			editScale: readStoredNumber(EDIT_SCALE_KEY, legacySharedScale),
			previewScale: legacySharedScale,
			previewDevicePreset: normalizePreviewDevicePreset(readStoredString(PREVIEW_DEVICE_PRESET_KEY, "responsive")),
			previewDeviceOrientation: normalizePreviewOrientation(readStoredString(PREVIEW_DEVICE_ORIENTATION_KEY, "portrait")),
			waveformCollapsed: readStoredBoolean("mediaMarkupV2WaveformCollapsed", false),
			canvasMode: "edit",
			mouseX: 0,
			mouseY: 0,
			pointerInCanvas: false,
			drawing: false,
			drawStartX: 0,
			drawStartY: 0,
			drawCurrentX: 0,
			drawCurrentY: 0,
			currentCursorMs: 0,
			currentRegion: null,
			currentTimeMs: 0,
			audioDurationMs: 0,
			previewPlaying: false,
			audioReady: false,
			imageLoadError: "",
			imageLoading: false,
			workspaceRecoveryApplied: false,
			canvasPopoutOpen: false
		};

		this.imageLoadRequestId = 0;
		this.lastAutoRevealedCanvasUrl = "";
		this.workspaceRecoveryFrame = null;
		this.canvasPointerListenerWindow = null;
		this.canvasAuxWindow = null;
	}

	componentDidMount() {
		this.loadImageSize(this.props.url);
		window.addEventListener("keydown", this.handleWindowKeyDown);
		window.addEventListener("keyup", this.handleWindowKeyUp);
		window.addEventListener("blur", this.handleWindowBlur);
		this.syncCanvasAuxWindowListeners();
	}

	componentWillUnmount() {
		this.stopPreviewPlayback();
		this.stopKeyboardNudge();
		this.detachCanvasPointerListeners();
		this.detachPanelResizeListeners(false);
		if (this.workspaceRecoveryFrame && typeof window !== "undefined") {
			window.cancelAnimationFrame(this.workspaceRecoveryFrame);
			this.workspaceRecoveryFrame = null;
		}
		window.removeEventListener("keydown", this.handleWindowKeyDown);
		window.removeEventListener("keyup", this.handleWindowKeyUp);
		window.removeEventListener("blur", this.handleWindowBlur);
		this.detachCanvasAuxWindowListeners();
		if (this.audioWaveformController && this.audioWaveformController.pause) {
			this.audioWaveformController.pause();
		}
		this.clearColorSampleCache();
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevProps.url !== this.props.url) {
			this.lastAutoRevealedCanvasUrl = "";
			this.setState({
				workspaceRecoveryApplied: false
			}, () => {
				this.loadImageSize(this.props.url);
			});
		}

		if (
			prevProps.open !== this.props.open
			|| prevState.canvasPopoutOpen !== this.state.canvasPopoutOpen
			|| prevState.canvasMode !== this.state.canvasMode
		) {
			this.syncCanvasAuxWindowListeners();
			return;
		}

		this.syncCanvasAuxWindowListeners();
	}

	getCanvasOwnerWindow() {
		const canvasNode = (this.canvasPaneRef && this.canvasPaneRef.current)
			|| (this.canvasSvgRef && this.canvasSvgRef.current)
			|| null;
		const ownerWindow = canvasNode && canvasNode.ownerDocument && canvasNode.ownerDocument.defaultView
			? canvasNode.ownerDocument.defaultView
			: null;
		return ownerWindow || (typeof window !== "undefined" ? window : null);
	}

	syncCanvasAuxWindowListeners() {
		if (typeof window === "undefined") {
			return;
		}

		const targetWindow = this.props.open ? this.getCanvasOwnerWindow() : null;
		if (!targetWindow || targetWindow === window) {
			this.detachCanvasAuxWindowListeners();
			return;
		}

		if (this.canvasAuxWindow === targetWindow) {
			return;
		}

		this.detachCanvasAuxWindowListeners();
		targetWindow.addEventListener("keydown", this.handleWindowKeyDown);
		targetWindow.addEventListener("keyup", this.handleWindowKeyUp);
		targetWindow.addEventListener("blur", this.handleWindowBlur);
		this.canvasAuxWindow = targetWindow;
	}

	detachCanvasAuxWindowListeners() {
		if (!this.canvasAuxWindow) {
			return;
		}

		try {
			this.canvasAuxWindow.removeEventListener("keydown", this.handleWindowKeyDown);
			this.canvasAuxWindow.removeEventListener("keyup", this.handleWindowKeyUp);
			this.canvasAuxWindow.removeEventListener("blur", this.handleWindowBlur);
		} catch (err) {
			// Ignore listener cleanup failures for already-closed windows.
		}

		this.canvasAuxWindow = null;
	}

	handleToggleCanvasPopout() {
		if (!this.state.imageSize) {
			return;
		}

		if (this.state.canvasPopoutOpen) {
			this.handleCanvasPopoutClosed();
			return;
		}

		this.setState({
			canvasPopoutOpen: true
		});
	}

	handleCanvasPopoutClosed() {
		if (!this.state.canvasPopoutOpen) {
			return;
		}

		this.setState({
			canvasPopoutOpen: false
		}, () => {
			this.scheduleWorkspaceLayoutRecoveryCheck();
			this.ensureInitialCanvasImageVisibility();
		});
	}

	async loadImageSize(url) {
		const requestId = ++this.imageLoadRequestId;

		if (!url) {
			this.setState({
				imageSize: null,
				imageLoadError: "",
				imageLoading: false
			});
			return;
		}

		this.setState({
			imageLoadError: "",
			imageLoading: true
		});

		try {
			const imageSize = await new Promise((resolve, reject) => {
				let settled = false;
				let timeoutId = null;
				const img = document.createElement("img");

				const finish = (handler) => (value) => {
					if (settled) {
						return;
					}
					settled = true;
					if (timeoutId) {
						window.clearTimeout(timeoutId);
					}
					handler(value);
				};

				img.onload = finish(() => {
					resolve({
						width: img.naturalWidth,
						height: img.naturalHeight
					});
				});
				img.onerror = finish(() => {
					reject(new Error(`Failed to load image for media markup: ${url}`));
				});
				timeoutId = window.setTimeout(finish(() => {
					reject(new Error(`Timed out loading image for media markup: ${url}`));
				}), 15000);
				img.src = url;

				if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
					finish(() => {
						resolve({
							width: img.naturalWidth,
							height: img.naturalHeight
						});
					})();
				}
			});

			if (requestId !== this.imageLoadRequestId) {
				return;
			}

			this.setState({
				imageSize: {
					width: imageSize.width,
					height: imageSize.height
				},
				imageLoadError: "",
				imageLoading: false
			}, () => {
				this.ensureInitialCanvasImageVisibility();
				this.scheduleWorkspaceLayoutRecoveryCheck();
			});
		} catch (err) {
			console.warn(err);
			if (requestId !== this.imageLoadRequestId) {
				return;
			}
			this.setState({
				imageSize: null,
				imageLoadError: "Unable to load image preview for Media Markup V2.",
				imageLoading: false
			});
		}
	}

	clearColorSampleCache() {
		this.colorSampleCanvas = null;
		this.colorSampleContext = null;
		this.colorSampleUrl = "";
		this.colorSamplePromise = null;
	}

	getDocumentSnapshot(source = this.state) {
		return {
			imageHotspots: cloneArray(source.imageHotspots),
			imageKenBurnsEffect: cloneArray(source.imageKenBurnsEffect),
			imageKenBurnsEffectVariants: normalizeCameraVariantOverrides(source.imageKenBurnsEffectVariants)
		};
	}

	pushDocumentHistorySnapshot(snapshot, callback) {
		if (!snapshot) {
			if (callback) {
				callback();
			}
			return;
		}

		this.setState((prevState) => {
			const currentSnapshot = this.getDocumentSnapshot(prevState);
			if (_.isEqual(snapshot, currentSnapshot)) {
				return null;
			}

			const nextPast = [...prevState.historyPast, snapshot];
			if (nextPast.length > DOCUMENT_HISTORY_LIMIT) {
				nextPast.shift();
			}

			return {
				historyPast: nextPast,
				historyFuture: []
			};
		}, callback);
	}

	beginDocumentHistoryCapture() {
		if (!this.pendingDocumentHistorySnapshot) {
			this.pendingDocumentHistorySnapshot = this.getDocumentSnapshot();
		}
	}

	finalizeDocumentHistoryCapture(callback) {
		const snapshot = this.pendingDocumentHistorySnapshot;
		this.pendingDocumentHistorySnapshot = null;
		if (!snapshot) {
			if (callback) {
				callback();
			}
			return;
		}

		this.pushDocumentHistorySnapshot(snapshot, callback);
	}

	flushPendingDocumentHistory(callback) {
		if (!this.pendingDocumentHistorySnapshot) {
			if (callback) {
				callback();
			}
			return;
		}

		this.finalizeDocumentHistoryCapture(callback);
	}

	applyDocumentChange(updater, options = {}) {
		const {
			callback,
			commitHistory = true,
			historyBaseSnapshot = null
		} = options;

		this.setState((prevState) => {
			const result = updater(prevState);
			if (!result) {
				return null;
			}

			const nextImageHotspots = result.imageHotspots !== undefined ? result.imageHotspots : prevState.imageHotspots;
			const nextImageKenBurnsEffect = result.imageKenBurnsEffect !== undefined ? result.imageKenBurnsEffect : prevState.imageKenBurnsEffect;
			const nextImageKenBurnsEffectVariants = result.imageKenBurnsEffectVariants !== undefined ? result.imageKenBurnsEffectVariants : prevState.imageKenBurnsEffectVariants;
			const nextState = Object.assign({}, result, {
				imageHotspots: nextImageHotspots,
				imageKenBurnsEffect: nextImageKenBurnsEffect,
				imageKenBurnsEffectVariants: nextImageKenBurnsEffectVariants
			});

			if (commitHistory) {
				const baseSnapshot = historyBaseSnapshot || this.getDocumentSnapshot(prevState);
				const nextSnapshot = this.getDocumentSnapshot({
					imageHotspots: nextImageHotspots,
					imageKenBurnsEffect: nextImageKenBurnsEffect,
					imageKenBurnsEffectVariants: nextImageKenBurnsEffectVariants
				});
				if (!_.isEqual(baseSnapshot, nextSnapshot)) {
					const nextPast = [...prevState.historyPast, baseSnapshot];
					if (nextPast.length > DOCUMENT_HISTORY_LIMIT) {
						nextPast.shift();
					}
					nextState.historyPast = nextPast;
					nextState.historyFuture = [];
				}
			}

			return nextState;
		}, callback);
	}

	getSelectedItem() {
		const { selectedItemGuid, selectedItemKind, imageHotspots } = this.state;
		if (!selectedItemGuid || !selectedItemKind) {
			return null;
		}

		if (selectedItemKind === "camera") {
			return this.getEffectiveCameraItemByGuid(selectedItemGuid);
		}

		return imageHotspots.find((item) => item.guid === selectedItemGuid) || null;
	}

	getSelectedItemRect() {
		const selectedItem = this.getSelectedItem();
		const { selectedItemKind } = this.state;
		if (!selectedItem) {
			return null;
		}

		if (selectedItemKind === "camera") {
			return _.cloneDeep(_.get(selectedItem, "camera.end", null));
		}

		return _.cloneDeep(selectedItem.coords || null);
	}

	getSelectedItemRotation() {
		const selectedItem = this.getSelectedItem();
		const { selectedItemKind } = this.state;
		if (!selectedItem) {
			return 0;
		}

		return selectedItemKind === "camera"
			? normalizeRotation(_.get(selectedItem, "camera.end.rotation", 0))
			: normalizeRotation(selectedItem.rotation || 0);
	}

	getEditorCanvasPadding() {
		return clampValue(Math.round(Number(this.state.canvasPadding) || 0), 0, 1600);
	}

	getCanvasViewBox() {
		const { imageSize } = this.state;
		const canvasPadding = this.getEditorCanvasPadding();
		if (!imageSize) {
			return {
				x: -canvasPadding,
				y: -canvasPadding,
				width: canvasPadding * 2,
				height: canvasPadding * 2
			};
		}

		let minX = 0;
		let minY = 0;
		let maxX = imageSize.width;
		let maxY = imageSize.height;

		if (this.state.canvasMode === "edit" && this.state.showDeviceGuides) {
			const baseRect = this.getDeviceGuideReferenceRect();
			const activeGuides = this.getActiveDeviceGuides();
			activeGuides.forEach((guide, index) => {
				const guideRect = this.getDeviceGuideRect(baseRect, guide);
				if (!guideRect) {
					return;
				}

				const labelMetrics = getDeviceGuideLabelMetrics(guideRect, guide, index);
				minX = Math.min(minX, guideRect.x, labelMetrics.labelX);
				minY = Math.min(minY, guideRect.y, labelMetrics.labelY);
				maxX = Math.max(maxX, guideRect.x + guideRect.width, labelMetrics.labelX + labelMetrics.labelWidth);
				maxY = Math.max(maxY, guideRect.y + guideRect.height, labelMetrics.labelY + labelMetrics.labelHeight);
			});
		}

		return {
			x: minX - canvasPadding,
			y: minY - canvasPadding,
			width: (maxX - minX) + (canvasPadding * 2),
			height: (maxY - minY) + (canvasPadding * 2)
		};
	}

	getCanvasPaneNode() {
		return this.canvasPaneRef && this.canvasPaneRef.current
			? this.canvasPaneRef.current
			: null;
	}

	captureCanvasImageAnchor() {
		if (this.state.canvasMode !== "edit" || !this.state.imageSize) {
			return null;
		}

		const canvasPane = this.getCanvasPaneNode();
		if (!canvasPane) {
			return null;
		}

		const viewBox = this.getCanvasViewBox();
		const canvasScaleRatio = (this.state.editScale || 100) / 100;

		return {
			scrollLeft: canvasPane.scrollLeft,
			scrollTop: canvasPane.scrollTop,
			imageOriginLeft: (0 - viewBox.x) * canvasScaleRatio,
			imageOriginTop: (0 - viewBox.y) * canvasScaleRatio
		};
	}

	restoreCanvasImageAnchor(anchor) {
		if (!anchor || this.state.canvasMode !== "edit" || !this.state.imageSize) {
			return;
		}

		const canvasPane = this.getCanvasPaneNode();
		if (!canvasPane) {
			return;
		}

		const applyAnchor = () => {
			const viewBox = this.getCanvasViewBox();
			const canvasScaleRatio = (this.state.editScale || 100) / 100;
			const nextImageOriginLeft = (0 - viewBox.x) * canvasScaleRatio;
			const nextImageOriginTop = (0 - viewBox.y) * canvasScaleRatio;
			const nextScrollLeft = Math.max(0, anchor.scrollLeft + (nextImageOriginLeft - anchor.imageOriginLeft));
			const nextScrollTop = Math.max(0, anchor.scrollTop + (nextImageOriginTop - anchor.imageOriginTop));
			canvasPane.scrollLeft = nextScrollLeft;
			canvasPane.scrollTop = nextScrollTop;
		};

		if (typeof window !== "undefined" && window.requestAnimationFrame) {
			window.requestAnimationFrame(applyAnchor);
			return;
		}

		applyAnchor();
	}

	ensureInitialCanvasImageVisibility() {
		if (this.state.canvasMode !== "edit" || !this.state.imageSize || !this.props.url) {
			return;
		}

		if (this.lastAutoRevealedCanvasUrl === this.props.url) {
			return;
		}

		const canvasPane = this.getCanvasPaneNode();
		if (!canvasPane) {
			return;
		}

		const applyReveal = () => {
			const viewBox = this.getCanvasViewBox();
			const canvasScaleRatio = (this.state.editScale || 100) / 100;
			const imageOriginLeft = (0 - viewBox.x) * canvasScaleRatio;
			const imageOriginTop = (0 - viewBox.y) * canvasScaleRatio;
			canvasPane.scrollLeft = Math.max(0, Math.round(imageOriginLeft - 24));
			canvasPane.scrollTop = Math.max(0, Math.round(imageOriginTop - 24));
			this.lastAutoRevealedCanvasUrl = this.props.url;
		};

		if (typeof window !== "undefined" && window.requestAnimationFrame) {
			window.requestAnimationFrame(applyReveal);
			return;
		}

		applyReveal();
	}

	scheduleWorkspaceLayoutRecoveryCheck() {
		if (typeof window === "undefined") {
			return;
		}

		if (this.workspaceRecoveryFrame) {
			window.cancelAnimationFrame(this.workspaceRecoveryFrame);
			this.workspaceRecoveryFrame = null;
		}

		this.workspaceRecoveryFrame = window.requestAnimationFrame(() => {
			this.workspaceRecoveryFrame = window.requestAnimationFrame(() => {
				this.workspaceRecoveryFrame = null;
				this.ensureUsableWorkspaceLayout();
			});
		});
	}

	ensureUsableWorkspaceLayout() {
		if (!this.props.open || !this.state.imageSize || this.state.canvasPopoutOpen) {
			return;
		}

		const workspaceNode = this.workspaceRef && this.workspaceRef.current
			? this.workspaceRef.current
			: null;
		const canvasNode = this.canvasPaneRef && this.canvasPaneRef.current
			? this.canvasPaneRef.current
			: null;
		if (!workspaceNode || !canvasNode) {
			return;
		}

		const workspaceWidth = workspaceNode.clientWidth || 0;
		const workspaceHeight = workspaceNode.clientHeight || 0;
		const canvasWidth = canvasNode.clientWidth || 0;
		const canvasHeight = canvasNode.clientHeight || 0;
		const compactLayout = typeof window !== "undefined" && window.innerWidth < 1350;
		if (compactLayout) {
			return;
		}

		const layoutLooksBroken = workspaceWidth > 600
			&& (
				workspaceHeight < 120
				|| (
					workspaceHeight > 220
					&& (
						canvasWidth < 260
						|| canvasHeight < 120
						|| canvasWidth < (workspaceWidth * 0.18)
						|| canvasHeight < (workspaceHeight * 0.22)
					)
				)
			);

		if (!layoutLooksBroken || this.state.workspaceRecoveryApplied) {
			return;
		}

		setLs("mediaMarkupV2WaveformCollapsed", "false");
		this.setState({
			layoutMode: LAYOUT_MODE_SPLIT,
			leftPanelCollapsed: true,
			rightPanelCollapsed: true,
			editScale: 100,
			canvasPadding: EDITOR_CANVAS_PADDING_DEFAULT,
			waveformCollapsed: false,
			workspaceRecoveryApplied: true
		}, () => {
			this.ensureInitialCanvasImageVisibility();
		});
	}

	getCanvasBoundsForKind(kind = "overlay") {
		const { imageSize } = this.state;
		const canvasPadding = this.getEditorCanvasPadding();
		if (!imageSize) {
			return {
				minX: 0,
				minY: 0,
				maxX: 0,
				maxY: 0
			};
		}

		if (kind === "camera") {
			return {
				minX: -canvasPadding,
				minY: -canvasPadding,
				maxX: imageSize.width + canvasPadding,
				maxY: imageSize.height + canvasPadding
			};
		}

		return {
			minX: 0,
			minY: 0,
			maxX: imageSize.width,
			maxY: imageSize.height
		};
	}

	getCurrentImageResizeMode() {
		const normalizedResizeMode = _.toLower(_.trim(`${this.props.imageResizeMode || "contain"}`));
		if (normalizedResizeMode === "cover") {
			return "cover";
		}
		if (normalizedResizeMode === "fill" || normalizedResizeMode === "stretch") {
			return "fill";
		}
		return "contain";
	}

	getDeviceGuideReferenceRect() {
		const { imageSize, selectedItemKind, selectedItemGuid } = this.state;
		if (selectedItemKind === "camera" && selectedItemGuid) {
			const selectedCamera = this.getEffectiveCameraItemByGuid(selectedItemGuid);
			const cameraRect = _.get(selectedCamera, "camera.end", null);
			if (cameraRect && Number(cameraRect.width) > 0 && Number(cameraRect.height) > 0) {
				return {
					x: Number(cameraRect.x) || 0,
					y: Number(cameraRect.y) || 0,
					width: Number(cameraRect.width) || 0,
					height: Number(cameraRect.height) || 0,
					label: selectedCamera.id || "Selected Camera"
				};
			}
		}

		if (!imageSize) {
			return null;
		}

		return {
			x: 0,
			y: 0,
			width: imageSize.width,
			height: imageSize.height,
			label: "Full Image"
		};
	}

	getCameraVariantSummaryLabel(variantKey = this.state.activeCameraVariant) {
		return getCameraVariantOption(variantKey).label;
	}

	handleActiveCameraVariantChange(event) {
		const activeCameraVariant = normalizeCameraVariantKey(event && event.target ? event.target.value : CAMERA_VARIANT_BASE);
		setLs(CAMERA_VARIANT_KEY, activeCameraVariant);
		this.setState({
			activeCameraVariant
		});
	}

	getNormalizedCameraVariantOverride(override = {}) {
		const normalizedOverride = {};
		["x", "y", "width", "height", "rotation"].forEach((field) => {
			const numericValue = Number(override[field]);
			if (Number.isFinite(numericValue)) {
				normalizedOverride[field] = numericValue;
			}
		});
		return normalizedOverride;
	}

	buildCameraVariantOverrideFromRect(baseRect = {}, nextRect = {}) {
		const nextOverride = {};
		["x", "y", "width", "height", "rotation"].forEach((field) => {
			const baseValue = Number(baseRect[field]);
			const nextValue = Number(nextRect[field]);
			if (Number.isFinite(nextValue) && nextValue !== baseValue) {
				nextOverride[field] = nextValue;
			}
		});
		return nextOverride;
	}

	setCameraVariantOverrideByGuid(guid, nextOverride = {}, callback, options = {}) {
		const activeVariant = normalizeCameraVariantKey(this.state.activeCameraVariant);
		if (activeVariant === CAMERA_VARIANT_BASE) {
			if (callback) {
				callback();
			}
			return;
		}

		this.applyDocumentChange((prevState) => {
			const currentVariantCameras = _.cloneDeep(_.get(prevState.imageKenBurnsEffectVariants, [activeVariant, "cameras"], {}));
			if (_.isEmpty(nextOverride)) {
				delete currentVariantCameras[guid];
			} else {
				currentVariantCameras[guid] = this.getNormalizedCameraVariantOverride(nextOverride);
			}

			return {
				imageKenBurnsEffectVariants: buildCameraVariantStatePatch(prevState.imageKenBurnsEffectVariants, activeVariant, currentVariantCameras)
			};
		}, Object.assign({}, options, {
			callback
		}));
	}

	setSelectedCameraVariantRect(nextRect, callback, options = {}) {
		const selectedCamera = this.getSelectedItem();
		const baseCamera = this.getBaseCameraItemByGuid(this.state.selectedItemGuid);
		if (!selectedCamera || !baseCamera || !nextRect) {
			return;
		}

		const safeRect = this.clampRectToBounds(nextRect, "camera");
		if (this.isEditingBaseCameraVariant()) {
			this.updateItemByGuid("camera", selectedCamera.guid, (item) => {
				item.camera = item.camera || {};
				item.camera.end = Object.assign({}, item.camera.end || {}, safeRect);
				return item;
			}, callback, options);
			return;
		}

		const baseRect = Object.assign({ rotation: 0 }, _.get(baseCamera, "camera.end", null) || {});
		this.setCameraVariantOverrideByGuid(
			selectedCamera.guid,
			this.buildCameraVariantOverrideFromRect(baseRect, Object.assign({}, safeRect, {
				rotation: Number(_.get(selectedCamera, "camera.end.rotation", 0)) || 0
			})),
			callback,
			options
		);
	}

	setSelectedCameraVariantRotation(nextRotation, callback, options = {}) {
		const selectedCamera = this.getSelectedItem();
		const baseCamera = this.getBaseCameraItemByGuid(this.state.selectedItemGuid);
		if (!selectedCamera || !baseCamera) {
			return;
		}

		const safeRotation = normalizeRotation(nextRotation);
		if (this.isEditingBaseCameraVariant()) {
			this.updateItemByGuid("camera", selectedCamera.guid, (item) => {
				item.camera = item.camera || {};
				item.camera.end = Object.assign({}, item.camera.end || {}, {
					rotation: safeRotation
				});
				return item;
			}, callback, options);
			return;
		}

		const baseRect = Object.assign({ rotation: 0 }, _.get(baseCamera, "camera.end", null) || {});
		const effectiveRect = Object.assign({}, _.get(selectedCamera, "camera.end", null) || {}, {
			rotation: safeRotation
		});
		this.setCameraVariantOverrideByGuid(
			selectedCamera.guid,
			this.buildCameraVariantOverrideFromRect(baseRect, effectiveRect),
			callback,
			options
		);
	}

	getAutoFitCameraRectForVariant(cameraRect = {}, variantKey = this.state.activeCameraVariant) {
		const targetAspectRatio = this.getCameraVariantTargetAspectRatio(variantKey);
		if (!targetAspectRatio) {
			return this.clampRectToBounds(cameraRect, "camera");
		}

		return this.clampRectToBounds(fitRectToAspect(cameraRect, targetAspectRatio), "camera");
	}

	handleAutoFitSelectedCameraVariant() {
		if (this.state.selectedItemKind !== "camera") {
			return;
		}

		const baseCamera = this.getBaseCameraItemByGuid(this.state.selectedItemGuid);
		const baseRect = _.get(baseCamera, "camera.end", null);
		if (!baseRect || this.isEditingBaseCameraVariant()) {
			return;
		}

		const fittedRect = this.getAutoFitCameraRectForVariant(baseRect);
		this.setSelectedCameraVariantRect(Object.assign({}, fittedRect, {
			rotation: Number(_.get(baseRect, "rotation", 0)) || 0
		}));
	}

	handleResetSelectedCameraVariant() {
		if (this.state.selectedItemKind !== "camera" || this.isEditingBaseCameraVariant()) {
			return;
		}

		this.setCameraVariantOverrideByGuid(this.state.selectedItemGuid, {});
	}

	handleResetActiveCameraVariant() {
		const activeVariant = normalizeCameraVariantKey(this.state.activeCameraVariant);
		if (activeVariant === CAMERA_VARIANT_BASE) {
			return;
		}

		this.applyDocumentChange((prevState) => ({
			imageKenBurnsEffectVariants: buildCameraVariantStatePatch(prevState.imageKenBurnsEffectVariants, activeVariant, {})
		}));
	}

	handleAutoFitAllCamerasForVariant() {
		const activeVariant = normalizeCameraVariantKey(this.state.activeCameraVariant);
		if (activeVariant === CAMERA_VARIANT_BASE) {
			return;
		}

		this.applyDocumentChange((prevState) => {
			const nextVariantCameras = prevState.imageKenBurnsEffect.reduce((result, camera) => {
				const baseRect = _.get(camera, "camera.end", null);
				if (!baseRect) {
					return result;
				}

				const fittedRect = this.getAutoFitCameraRectForVariant(baseRect, activeVariant);
				const nextOverride = this.buildCameraVariantOverrideFromRect(
					Object.assign({ rotation: 0 }, baseRect),
					Object.assign({}, fittedRect, {
						rotation: Number(_.get(baseRect, "rotation", 0)) || 0
					})
				);
				if (!_.isEmpty(nextOverride)) {
					result[camera.guid] = nextOverride;
				}
				return result;
			}, {});

			return {
				imageKenBurnsEffectVariants: buildCameraVariantStatePatch(prevState.imageKenBurnsEffectVariants, activeVariant, nextVariantCameras)
			};
		});
	}

	getDeviceGuideRect(baseRect, guide) {
		if (!baseRect || !guide || !baseRect.width || !baseRect.height || !guide.width || !guide.height) {
			return null;
		}

		const resizeMode = this.getCurrentImageResizeMode();
		const targetAspect = guide.width / guide.height;
		const baseAspect = baseRect.width / baseRect.height;

		if (!Number.isFinite(targetAspect) || !Number.isFinite(baseAspect) || targetAspect <= 0 || baseAspect <= 0) {
			return null;
		}

		let nextX = baseRect.x;
		let nextY = baseRect.y;
		let nextWidth = baseRect.width;
		let nextHeight = baseRect.height;

		if (resizeMode === "cover") {
			if (baseAspect > targetAspect) {
				nextWidth = baseRect.height * targetAspect;
				nextHeight = baseRect.height;
				nextX = baseRect.x + ((baseRect.width - nextWidth) / 2);
			} else {
				nextWidth = baseRect.width;
				nextHeight = baseRect.width / targetAspect;
				nextY = baseRect.y + ((baseRect.height - nextHeight) / 2);
			}
		} else if (resizeMode === "contain") {
			if (baseAspect > targetAspect) {
				nextWidth = baseRect.width;
				nextHeight = baseRect.width / targetAspect;
				nextY = baseRect.y - ((nextHeight - baseRect.height) / 2);
			} else {
				nextWidth = baseRect.height * targetAspect;
				nextHeight = baseRect.height;
				nextX = baseRect.x - ((nextWidth - baseRect.width) / 2);
			}
		}

		return {
			x: Math.round(nextX),
			y: Math.round(nextY),
			width: Math.max(1, Math.round(nextWidth)),
			height: Math.max(1, Math.round(nextHeight)),
			fitMode: resizeMode
		};
	}

	getActiveDeviceGuides() {
		const selectedIds = new Set(this.state.activeDeviceGuideIds || []);
		return DEVICE_GUIDE_OPTIONS.filter((guide) => selectedIds.has(guide.id));
	}

	getEffectiveCameraGuideTargets() {
		const activeGuides = this.getActiveDeviceGuides();
		if (activeGuides.length > 0) {
			return activeGuides;
		}

		const defaultGuideIds = new Set(DEFAULT_DEVICE_GUIDE_IDS);
		return DEVICE_GUIDE_OPTIONS.filter((guide) => defaultGuideIds.has(guide.id));
	}

	getCameraAspectGuidanceForRect(cameraRect = null) {
		if (!cameraRect) {
			return null;
		}

		const cameraWidth = Math.max(Number(cameraRect.width) || 0, 1);
		const cameraHeight = Math.max(Number(cameraRect.height) || 0, 1);
		const cameraAspectRatio = cameraWidth / cameraHeight;
		const targetGuides = this.getEffectiveCameraGuideTargets()
			.map((guide) => Object.assign({}, guide, {
				aspectRatio: guide.width / guide.height
			}))
			.filter((guide) => Number.isFinite(guide.aspectRatio) && guide.aspectRatio > 0);

		if (targetGuides.length === 0) {
			return null;
		}

		const narrowestGuide = _.minBy(targetGuides, "aspectRatio");
		const widestGuide = _.maxBy(targetGuides, "aspectRatio");
		const closestGuide = _.minBy(targetGuides, (guide) => Math.abs(Math.log(cameraAspectRatio / guide.aspectRatio)));
		const ratioLabel = formatAspectRatioLabel(cameraAspectRatio);
		const rangeLabel = `${formatAspectRatioLabel(narrowestGuide.aspectRatio)} to ${formatAspectRatioLabel(widestGuide.aspectRatio)}`;

		if (cameraAspectRatio < narrowestGuide.aspectRatio) {
			return {
				severity: "warning",
				title: "Camera Is Taller Than Your Target Guide Set",
				meta: `Camera ${ratioLabel} • Target Range ${rangeLabel}`,
				body: "This camera is narrower/taller than your selected device guides. In contain mode the full camera box should stay visible, but it will likely letterbox heavily on wider screens. Consider widening it or creating a phone-specific variant.",
				closestGuideLabel: closestGuide ? closestGuide.label : ""
			};
		}

		if (cameraAspectRatio > widestGuide.aspectRatio) {
			return {
				severity: "warning",
				title: "Camera Is Wider Than Your Target Guide Set",
				meta: `Camera ${ratioLabel} • Target Range ${rangeLabel}`,
				body: "This camera is wider/shorter than your selected device guides. In contain mode the full camera box should stay visible, but it will likely letterbox heavily on taller devices. Consider making it taller or creating a desktop-specific variant.",
				closestGuideLabel: closestGuide ? closestGuide.label : ""
			};
		}

		return {
			severity: "info",
			title: "Camera Aspect Fits Current Guide Range",
			meta: `Camera ${ratioLabel} • Target Range ${rangeLabel}`,
			body: closestGuide
				? `This camera stays within the current guide range and is closest to ${closestGuide.label}. Keep important content padded inside the camera to preserve intent across devices.`
					: "This camera stays within the current guide range. Keep important content padded inside the camera to preserve intent across devices."
			};
		}

	getSelectedCameraAspectGuidance() {
		if (this.state.selectedItemKind !== "camera") {
			return null;
		}

		return this.getCameraAspectGuidanceForRect(this.getSelectedItemRect());
	}

	getColorPickTargetLabel(target = this.state.colorPickTarget) {
		return target === COLOR_PICK_TARGETS.fill ? "fill color" : "border color";
	}

	handleColorPickToggle(target = COLOR_PICK_TARGETS.border) {
		if (this.state.canvasMode !== "edit") {
			return;
		}

		this.setState((prevState) => ({
			colorPickTarget: prevState.colorPickTarget === target ? "" : target
		}));
	}

	async ensureColorSampleContext(url = this.props.url) {
		const sampleUrl = `${url || ""}`.trim();
		if (!sampleUrl) {
			throw new Error("No image is available to sample.");
		}

		if (this.colorSampleContext && this.colorSampleUrl === sampleUrl) {
			return this.colorSampleContext;
		}

		if (this.colorSamplePromise && this.colorSampleUrl === sampleUrl) {
			return this.colorSamplePromise;
		}

		this.clearColorSampleCache();
		this.colorSampleUrl = sampleUrl;
		this.colorSamplePromise = new Promise((resolve, reject) => {
			if (typeof window === "undefined" || typeof document === "undefined") {
				reject(new Error("Image sampling is unavailable in this environment."));
				return;
			}

			const image = new window.Image();
			image.crossOrigin = "anonymous";
			image.onload = () => {
				try {
					const canvas = document.createElement("canvas");
					const width = image.naturalWidth || image.width || _.get(this.state, "imageSize.width", 1) || 1;
					const height = image.naturalHeight || image.height || _.get(this.state, "imageSize.height", 1) || 1;
					canvas.width = width;
					canvas.height = height;
					const context = canvas.getContext("2d", { willReadFrequently: true });
					if (!context) {
						throw new Error("Image sampling is unavailable in this browser.");
					}
					context.clearRect(0, 0, width, height);
					context.drawImage(image, 0, 0, width, height);
					this.colorSampleCanvas = canvas;
					this.colorSampleContext = context;
					resolve(context);
				} catch (err) {
					reject(err);
				}
			};
			image.onerror = () => {
				reject(new Error("The image could not be loaded for color sampling."));
			};
			image.src = sampleUrl;
		});

		try {
			const context = await this.colorSamplePromise;
			this.colorSamplePromise = null;
			return context;
		} catch (err) {
			this.clearColorSampleCache();
			throw err;
		}
	}

	async sampleImageColorAtPoint(point = null) {
		const imageSize = this.state.imageSize;
		if (!point || !imageSize) {
			return "";
		}

		if (point.x < 0 || point.y < 0 || point.x >= imageSize.width || point.y >= imageSize.height) {
			return "";
		}

		const context = await this.ensureColorSampleContext(this.props.url);
		const sampleX = clampValue(Math.round(point.x), 0, Math.max(imageSize.width - 1, 0));
		const sampleY = clampValue(Math.round(point.y), 0, Math.max(imageSize.height - 1, 0));
		const imageData = context.getImageData(sampleX, sampleY, 1, 1);
		const rgba = imageData && imageData.data ? imageData.data : [0, 0, 0, 255];
		const alpha = Math.round((rgba[3] / 255) * 1000) / 1000;
		return `rgba(${rgba[0]},${rgba[1]},${rgba[2]},${alpha})`;
	}

	applySampledColorToTarget(target = COLOR_PICK_TARGETS.border, sampledColor = "") {
		if (!sampledColor) {
			return;
		}

		if (target === COLOR_PICK_TARGETS.fill) {
			const stylePreset = this.getInferredStylePresetFromControls({
				fillEnabled: true,
				fillColor: sampledColor
			});
			this.setState({
				fillEnabled: true,
				fillColor: sampledColor,
				stylePreset,
				colorPickTarget: ""
			}, () => {
				if (stylePreset !== "custom") {
					setLs(OVERLAY_STYLE_PRESET_KEY, stylePreset);
				}

				if (this.state.selectedItemKind !== "overlay") {
					return;
				}

				this.updateItemByGuid("overlay", this.state.selectedItemGuid, (item) => {
					item.style.borderFillColor = sampledColor;
					return item;
				});
			});
			return;
		}

		this.setState({
			colorPickTarget: ""
		}, () => {
			this.handleBorderColorChange(sampledColor);
		});
	}

	async handleColorPickFromPoint(point = null) {
		if (!this.state.colorPickTarget) {
			return;
		}

		try {
			const sampledColor = await this.sampleImageColorAtPoint(point);
			if (!sampledColor) {
				return;
			}
			this.applySampledColorToTarget(this.state.colorPickTarget, sampledColor);
		} catch (err) {
			console.warn(err);
			this.setState({
				colorPickTarget: ""
			});
		}
	}

	getCanvasPointerPosition(event) {
		const svg = this.canvasSvgRef.current;
		if (!svg) {
			return null;
		}

		const rect = svg.getBoundingClientRect();
		if (!rect.width || !rect.height) {
			return null;
		}

		const viewBox = this.getCanvasViewBox();
		const x = viewBox.x + (((event.clientX - rect.left) / rect.width) * viewBox.width);
		const y = viewBox.y + (((event.clientY - rect.top) / rect.height) * viewBox.height);

		return {
			x: Math.round(x),
			y: Math.round(y)
		};
	}

	getDraftTiming(forCamera = false) {
		if (this.state.currentRegion) {
			return {
				startTime: Math.round((this.state.currentRegion.start || 0) * 1000),
				endTime: Math.round((this.state.currentRegion.end || 0) * 1000)
			};
		}

		const startTime = Math.round(this.state.currentCursorMs || this.state.currentTimeMs || 0);
		return {
			startTime,
			endTime: forCamera ? startTime + 3000 : -1
		};
	}

	clampRectToBounds(rect, kind = "overlay") {
		const bounds = this.getCanvasBoundsForKind(kind);
		const totalWidth = Math.max(bounds.maxX - bounds.minX, MIN_ITEM_SIZE);
		const totalHeight = Math.max(bounds.maxY - bounds.minY, MIN_ITEM_SIZE);
		const nextWidth = clampValue(Math.round(Number(rect.width) || MIN_ITEM_SIZE), MIN_ITEM_SIZE, totalWidth);
		const nextHeight = clampValue(Math.round(Number(rect.height) || MIN_ITEM_SIZE), MIN_ITEM_SIZE, totalHeight);
		const nextX = clampValue(Math.round(Number(rect.x) || 0), bounds.minX, bounds.maxX - nextWidth);
		const nextY = clampValue(Math.round(Number(rect.y) || 0), bounds.minY, bounds.maxY - nextHeight);

		return {
			x: nextX,
			y: nextY,
			width: nextWidth,
			height: nextHeight
		};
	}

	getOverlayStyleStateForPreset(componentType = "highlight", presetName = "", shape = this.state.shape) {
		const defaults = getOverlayDefaults(componentType, presetName);
		const normalizedPreset = normalizeOverlayStylePreset(presetName, componentType);

		return {
			stylePreset: normalizedPreset,
			borderColor: defaults.borderColor,
			borderWidth: defaults.borderWidth,
			borderRadius: shapeSupportsBorderRadius(shape) ? defaults.borderRadius : 0,
			fillEnabled: shapeSupportsFill(shape) && !isTransparentColor(defaults.fillColor),
			fillColor: defaults.fillColor
		};
	}

	getInferredStylePresetFromControls(overrides = {}, componentType = this.state.componentType, shape = this.state.shape) {
		const borderColor = overrides.borderColor !== undefined ? overrides.borderColor : this.state.borderColor;
		const borderWidth = overrides.borderWidth !== undefined ? overrides.borderWidth : this.state.borderWidth;
		const borderRadius = shapeSupportsBorderRadius(shape)
			? (overrides.borderRadius !== undefined ? overrides.borderRadius : this.state.borderRadius)
			: 0;
		const fillEnabled = shapeSupportsFill(shape)
			? (overrides.fillEnabled !== undefined ? overrides.fillEnabled : this.state.fillEnabled)
			: false;
		const fillColor = overrides.fillColor !== undefined ? overrides.fillColor : this.state.fillColor;

		return inferOverlayStylePresetFromStyle({
			borderColor,
			borderWidth,
			borderRadius,
			borderFillColor: fillEnabled ? fillColor : "transparent"
		}, componentType);
	}

	applyOverlayStylePresetToItem(item, componentType = "highlight", presetName = "", shape = item && item.shape ? item.shape : this.state.shape) {
		const presetState = this.getOverlayStyleStateForPreset(componentType, presetName, shape);
		const nextItem = item;
		nextItem.style = Object.assign({}, nextItem.style || {}, {
			borderColor: presetState.borderColor,
			borderWidth: presetState.borderWidth,
			borderRadius: shapeSupportsBorderRadius(shape) ? presetState.borderRadius : 0,
			borderFillColor: presetState.fillEnabled ? presetState.fillColor : "transparent"
		});
		return nextItem;
	}

	buildOverlayItem(bounds, componentType = "highlight", shape = "rectangle") {
		const { startTime, endTime } = this.getDraftTiming(false);
		const overlayRect = this.clampRectToBounds(bounds, "overlay");
		const nextIdBase = shape === "arrow" ? "arrow" : componentType;
		const nextItemCount = this.state.imageHotspots.filter((item) => (
			shape === "arrow" ? item.shape === "arrow" : (item.type || "highlight") === componentType && item.shape !== "arrow"
		)).length + 1;
		const defaults = getOverlayDefaults(componentType, this.state.stylePreset);
		const supportsBorderRadius = shapeSupportsBorderRadius(shape);
		const supportsFill = shapeSupportsFill(shape);

		return {
			id: `${nextIdBase}${nextItemCount}`,
			type: componentType,
			startTime,
			endTime,
			shape,
			coords: overlayRect,
			rotation: 0,
			label: componentType === "hotspot" ? "" : undefined,
			labelVisible: componentType === "hotspot" ? false : undefined,
			style: {
				borderRadius: supportsBorderRadius ? this.state.borderRadius : 0,
				borderColor: this.state.borderColor || defaults.borderColor,
				borderWidth: Number(this.state.borderWidth) || defaults.borderWidth,
				borderFillColor: supportsFill && this.state.fillEnabled
					? (this.state.fillColor || defaults.fillColor)
					: "transparent"
			},
			startTimeTransition: {
				type: "fade",
				duration: 500,
				fromOpacity: 0,
				toOpacity: 1
			},
			endTimeTransition: {
				type: "fade",
				duration: 500,
				toOpacity: 0
			},
			action: componentType === "hotspot" ? "gotoNext" : "none",
			guid: uuidv4()
		};
	}

	buildCameraItem(bounds) {
		const { startTime, endTime } = this.getDraftTiming(true);
		const cameraRect = this.clampRectToBounds(bounds, "camera");

		return {
			id: `camera${this.state.imageKenBurnsEffect.length + 1}`,
			startTime,
			endTime,
			duration: 0,
			camera: {
				end: {
					x: cameraRect.x,
					y: cameraRect.y,
					width: cameraRect.width,
					height: cameraRect.height,
					rotation: 0
				},
				startZoomed: false
			},
			guid: uuidv4()
		};
	}

	buildCameraBoundsForOverlay(overlayRect) {
		return {
			x: overlayRect.x - 75,
			y: overlayRect.y - 75,
			width: overlayRect.width + 150,
			height: overlayRect.height + 150
		};
	}

	getActiveCameraVariantOption() {
		return getCameraVariantOption(this.state.activeCameraVariant);
	}

	isEditingBaseCameraVariant() {
		return normalizeCameraVariantKey(this.state.activeCameraVariant) === CAMERA_VARIANT_BASE;
	}

	getCameraVariantTargetAspectRatio(variantKey = this.state.activeCameraVariant) {
		const variantOption = getCameraVariantOption(variantKey);
		return variantOption.targetWidth && variantOption.targetHeight
			? (variantOption.targetWidth / variantOption.targetHeight)
			: 0;
	}

	getCameraVariantOverridesForKey(variantKey = this.state.activeCameraVariant, documentState = this.state) {
		if (!variantKey || normalizeCameraVariantKey(variantKey) === CAMERA_VARIANT_BASE) {
			return {};
		}

		return _.get(documentState.imageKenBurnsEffectVariants, [variantKey, "cameras"], {});
	}

	getCameraVariantOverrideByGuid(guid, variantKey = this.state.activeCameraVariant, documentState = this.state) {
		return _.get(this.getCameraVariantOverridesForKey(variantKey, documentState), [guid], null);
	}

	hasCameraVariantOverride(guid, variantKey = this.state.activeCameraVariant, documentState = this.state) {
		return !!this.getCameraVariantOverrideByGuid(guid, variantKey, documentState);
	}

	hasAnyCameraVariantOverrides(variantKey = this.state.activeCameraVariant, documentState = this.state) {
		if (!variantKey || normalizeCameraVariantKey(variantKey) === CAMERA_VARIANT_BASE) {
			return false;
		}

		return Object.keys(this.getCameraVariantOverridesForKey(variantKey, documentState)).length > 0;
	}

	getEffectiveCameraItem(baseCamera, variantKey = this.state.activeCameraVariant, documentState = this.state) {
		if (!baseCamera) {
			return null;
		}

		const activeVariant = normalizeCameraVariantKey(variantKey);
		if (activeVariant === CAMERA_VARIANT_BASE) {
			return baseCamera;
		}

		const override = this.getCameraVariantOverrideByGuid(baseCamera.guid, activeVariant, documentState);
		if (!override) {
			return baseCamera;
		}

		const nextCamera = _.cloneDeep(baseCamera);
		nextCamera.camera = nextCamera.camera || {};
		nextCamera.camera.end = Object.assign({}, nextCamera.camera.end || {}, override);
		nextCamera._variantOverrideActive = true;
		return nextCamera;
	}

	getEffectiveCameraItems(variantKey = this.state.activeCameraVariant, documentState = this.state) {
		return documentState.imageKenBurnsEffect.map((camera) => this.getEffectiveCameraItem(camera, variantKey, documentState));
	}

	getBaseCameraItemByGuid(guid, documentState = this.state) {
		return documentState.imageKenBurnsEffect.find((item) => item.guid === guid) || null;
	}

	getEffectiveCameraItemByGuid(guid, variantKey = this.state.activeCameraVariant, documentState = this.state) {
		const baseCamera = this.getBaseCameraItemByGuid(guid, documentState);
		return this.getEffectiveCameraItem(baseCamera, variantKey, documentState);
	}

	getCanvasItemRectForKind(kind, item) {
		if (!item) {
			return null;
		}

		return kind === "camera" ? _.get(item, "camera.end", null) : (item.coords || null);
	}

	isCanvasItemVisible(item) {
		return !item || item.canvasVisible !== false;
	}

	getStateKeyForKind(kind) {
		return kind === "camera" ? "imageKenBurnsEffect" : "imageHotspots";
	}

	getItemsForKind(kind, documentState = this.state) {
		return kind === "camera" ? this.getEffectiveCameraItems(this.state.activeCameraVariant, documentState) : documentState.imageHotspots;
	}

	getActiveTimelineCanvasGuidSets() {
		if (this.state.canvasMode !== "edit" || !this.state.showOnlyActiveCanvasItems) {
			return null;
		}

		const previewItem = this.buildPreviewItem();
		return {
			overlayGuids: new Set(getScheduledHotspotGuidsAtTime(previewItem, this.state.currentTimeMs)),
			cameraGuids: new Set(getRelevantCameraGuidsAtTime(previewItem, this.state.currentTimeMs, this.state.imageSize || { width: 0, height: 0 }))
		};
	}

	getEditorCanvasEntries() {
		const activeTimelineGuidSets = this.getActiveTimelineCanvasGuidSets();
		const allEntries = [
			...this.state.imageHotspots.map((item) => ({ kind: "overlay", item })),
			...this.getEffectiveCameraItems().map((item) => ({ kind: "camera", item }))
		].filter(({ kind, item }) => {
			if (!this.isCanvasItemVisible(item)) {
				return false;
			}

			if (!activeTimelineGuidSets) {
				return true;
			}

			const activeGuids = kind === "camera"
				? activeTimelineGuidSets.cameraGuids
				: activeTimelineGuidSets.overlayGuids;
			return activeGuids.has(item.guid);
		});

		const selectedEntries = [];
		const unselectedEntries = [];

		allEntries.forEach((entry) => {
			if (entry.kind === this.state.selectedItemKind && entry.item.guid === this.state.selectedItemGuid) {
				selectedEntries.push(entry);
				return;
			}

			unselectedEntries.push(entry);
		});

		return [...unselectedEntries, ...selectedEntries];
	}

	updateItemByGuid(kind, guid, updater, callback, options = {}) {
		const stateKey = kind === "camera" ? "imageKenBurnsEffect" : "imageHotspots";
		this.applyDocumentChange((prevState) => ({
			[stateKey]: prevState[stateKey].map((item) => {
				if (item.guid !== guid) {
					return item;
				}

				return updater(_.cloneDeep(item));
			})
		}), Object.assign({}, options, {
			callback
		}));
	}

	getSelectionState(kind, guid, documentState = this.state) {
		const sourceItems = kind === "camera"
			? this.getEffectiveCameraItems(this.state.activeCameraVariant, documentState)
			: documentState.imageHotspots;
		const selectedItem = sourceItems.find((item) => item.guid === guid);
		if (!selectedItem) {
			return null;
		}

		const nextState = {
			selectedNumericDrafts: null,
			selectedItemKind: kind,
			selectedItemGuid: guid,
			colorPickTarget: kind === "camera" ? "" : this.state.colorPickTarget,
			renamingItemKind: "",
			renamingItemGuid: "",
			renamingSurface: "",
			renamingItemValue: ""
		};

		if (kind === "camera") {
			nextState.componentType = "camera";
		} else {
			const itemType = selectedItem.type || (selectedItem.label !== undefined ? "hotspot" : "highlight");
			const itemShape = selectedItem.shape || "rectangle";
			const fillColor = _.get(selectedItem, "style.borderFillColor", "transparent");
			nextState.componentType = itemType;
			nextState.shape = itemShape;
			nextState.stylePreset = inferOverlayStylePreset(selectedItem, itemType);
			nextState.borderColor = _.get(selectedItem, "style.borderColor", getOverlayDefaults(itemType).borderColor);
			nextState.borderWidth = Number(_.get(selectedItem, "style.borderWidth", getOverlayDefaults(itemType).borderWidth)) || 0;
			nextState.borderRadius = shapeSupportsBorderRadius(itemShape)
				? (Number(_.get(selectedItem, "style.borderRadius", getOverlayDefaults(itemType).borderRadius)) || 0)
				: 0;
			nextState.fillEnabled = shapeSupportsFill(itemShape) && !isTransparentColor(fillColor);
			nextState.fillColor = !isTransparentColor(fillColor) ? fillColor : this.state.fillColor;
			}

		return nextState;
	}

	getSelectionStateForDocument(documentState = this.state, kind = this.state.selectedItemKind, guid = this.state.selectedItemGuid) {
		const nextSelectionState = this.getSelectionState(kind, guid, documentState);
		if (nextSelectionState) {
			return nextSelectionState;
		}

		return {
			selectedNumericDrafts: null,
			selectedItemKind: "",
			selectedItemGuid: "",
			renamingItemKind: "",
			renamingItemGuid: "",
			renamingSurface: "",
			renamingItemValue: ""
		};
	}

	selectItem(kind, guid) {
		const nextState = this.getSelectionState(kind, guid);
		if (!nextState) {
			return;
		}

		const canvasAnchor = this.state.showDeviceGuides ? this.captureCanvasImageAnchor() : null;
		this.setState(nextState, () => {
			this.restoreCanvasImageAnchor(canvasAnchor);
		});
	}

	clearSelection() {
		const canvasAnchor = this.state.showDeviceGuides ? this.captureCanvasImageAnchor() : null;
		this.setState({
			selectedNumericDrafts: null,
			selectedItemKind: "",
			selectedItemGuid: "",
			renamingItemKind: "",
			renamingItemGuid: "",
			renamingSurface: "",
			renamingItemValue: ""
		}, () => {
			this.restoreCanvasImageAnchor(canvasAnchor);
		});
	}

	handleComponentTypeChange(event) {
		const componentType = event && event.target ? event.target.value : "highlight";
		const nextShape = componentType === "camera" ? this.state.shape : (this.state.shape || "rectangle");
		const nextPreset = getDefaultOverlayStylePreset(componentType);
		const presetState = componentType === "camera"
			? {}
			: this.getOverlayStyleStateForPreset(componentType, nextPreset, nextShape);
		if (componentType !== "camera") {
			setLs(OVERLAY_STYLE_PRESET_KEY, nextPreset);
		}

		this.setState({
			componentType,
			shape: nextShape,
			stylePreset: componentType === "camera" ? this.state.stylePreset : presetState.stylePreset,
			borderColor: componentType === "camera" ? this.state.borderColor : presetState.borderColor,
			borderWidth: componentType === "camera" ? this.state.borderWidth : presetState.borderWidth,
			borderRadius: componentType === "camera" ? this.state.borderRadius : presetState.borderRadius,
			fillEnabled: componentType === "camera" ? this.state.fillEnabled : presetState.fillEnabled,
			fillColor: componentType === "camera" ? this.state.fillColor : presetState.fillColor,
			colorPickTarget: componentType === "camera" ? "" : this.state.colorPickTarget
		}, () => {
			if (this.state.selectedItemKind !== "overlay" || componentType === "camera") {
				return;
			}

			this.updateItemByGuid("overlay", this.state.selectedItemGuid, (item) => {
				const nextItem = item;
				nextItem.type = componentType;
				nextItem.action = componentType === "hotspot" ? (item.action || "gotoNext") : "none";
				if (componentType === "hotspot") {
					nextItem.label = item.label || "";
					nextItem.labelVisible = item.labelVisible === true;
				}
				return this.applyOverlayStylePresetToItem(nextItem, componentType, nextPreset, nextItem.shape || nextShape);
			});
		});
	}

	handleShapeChange(event) {
		const shape = event && event.target ? event.target.value : "rectangle";
		const supportsBorderRadius = shapeSupportsBorderRadius(shape);
		const supportsFill = shapeSupportsFill(shape);
		const fillEnabled = supportsFill ? this.state.fillEnabled : false;
		const stylePreset = this.getInferredStylePresetFromControls({
			borderRadius: supportsBorderRadius ? this.state.borderRadius : 0,
			fillEnabled
		}, this.state.componentType, shape);
		this.setState({
			shape,
			stylePreset,
			borderRadius: supportsBorderRadius ? this.state.borderRadius : 0,
			fillEnabled
		}, () => {
			if (stylePreset !== "custom") {
				setLs(OVERLAY_STYLE_PRESET_KEY, stylePreset);
			}

			if (this.state.selectedItemKind !== "overlay") {
				return;
			}

			this.updateItemByGuid("overlay", this.state.selectedItemGuid, (item) => {
				const nextItem = item;
				nextItem.shape = shape;
				nextItem.style.borderRadius = supportsBorderRadius ? nextItem.style.borderRadius : 0;
				if (!supportsFill) {
					nextItem.style.borderFillColor = "transparent";
				}
				return nextItem;
			});
		});
	}

	handleStylePresetChange(event) {
		const presetName = normalizeOverlayStylePreset(
			event && event.target ? event.target.value : getDefaultOverlayStylePreset(this.state.componentType),
			this.state.componentType
		);
		if (presetName === "custom") {
			return;
		}
		const presetState = this.getOverlayStyleStateForPreset(this.state.componentType, presetName, this.state.shape);
		setLs(OVERLAY_STYLE_PRESET_KEY, presetName);

		this.setState(presetState, () => {
			if (this.state.selectedItemKind !== "overlay") {
				return;
			}

			this.updateItemByGuid("overlay", this.state.selectedItemGuid, (item) => (
				this.applyOverlayStylePresetToItem(item, item.type || this.state.componentType, presetName, item.shape || this.state.shape)
			));
		});
	}

	handleBorderColorChange(colorResult) {
		const borderColor = colorResultToRgba(colorResult, this.state.borderColor);
		const stylePreset = this.getInferredStylePresetFromControls({ borderColor });
		this.setState({
			borderColor,
			stylePreset
		}, () => {
			if (stylePreset !== "custom") {
				setLs(OVERLAY_STYLE_PRESET_KEY, stylePreset);
			}

			if (this.state.selectedItemKind !== "overlay") {
				return;
			}

			this.updateItemByGuid("overlay", this.state.selectedItemGuid, (item) => {
				item.style.borderColor = borderColor;
				return item;
			});
		});
	}

	handleBorderWidthChange(event) {
		const borderWidth = clampValue(parseInt(event && event.target ? event.target.value : 0, 10) || 0, 0, 16);
		const stylePreset = this.getInferredStylePresetFromControls({ borderWidth });
		this.setState({
			borderWidth,
			stylePreset
		}, () => {
			if (stylePreset !== "custom") {
				setLs(OVERLAY_STYLE_PRESET_KEY, stylePreset);
			}

			if (this.state.selectedItemKind !== "overlay") {
				return;
			}

			this.updateItemByGuid("overlay", this.state.selectedItemGuid, (item) => {
				item.style.borderWidth = borderWidth;
				return item;
			});
		});
	}

	handleBorderRadiusChange(event) {
		const borderRadius = clampValue(parseInt(event && event.target ? event.target.value : 0, 10) || 0, 0, 64);
		const stylePreset = this.getInferredStylePresetFromControls({ borderRadius });
		this.setState({
			borderRadius,
			stylePreset
		}, () => {
			if (stylePreset !== "custom") {
				setLs(OVERLAY_STYLE_PRESET_KEY, stylePreset);
			}

			if (this.state.selectedItemKind !== "overlay") {
				return;
			}

			this.updateItemByGuid("overlay", this.state.selectedItemGuid, (item) => {
				item.style.borderRadius = shapeSupportsBorderRadius(item.shape || "rectangle") ? borderRadius : 0;
				return item;
			});
		});
	}

	handleFillEnabledChange(event) {
		const fillEnabled = shapeSupportsFill(this.state.shape) && !!(event && event.target ? event.target.checked : false);
		const stylePreset = this.getInferredStylePresetFromControls({ fillEnabled });
		this.setState({
			fillEnabled,
			stylePreset
		}, () => {
			if (stylePreset !== "custom") {
				setLs(OVERLAY_STYLE_PRESET_KEY, stylePreset);
			}

			if (this.state.selectedItemKind !== "overlay") {
				return;
			}

			this.updateItemByGuid("overlay", this.state.selectedItemGuid, (item) => {
				item.style.borderFillColor = fillEnabled
					? (this.state.fillColor || item.style.borderColor || this.state.borderColor)
					: "transparent";
				return item;
			});
		});
	}

	handleFillColorChange(colorResult) {
		const fillColor = colorResultToRgba(colorResult, this.state.fillColor);
		const stylePreset = this.getInferredStylePresetFromControls({ fillColor });
		this.setState({
			fillColor,
			stylePreset
		}, () => {
			if (stylePreset !== "custom") {
				setLs(OVERLAY_STYLE_PRESET_KEY, stylePreset);
			}

			if (this.state.selectedItemKind !== "overlay") {
				return;
			}

			this.updateItemByGuid("overlay", this.state.selectedItemGuid, (item) => {
				item.style.borderFillColor = this.state.fillEnabled ? fillColor : "transparent";
				return item;
			});
		});
	}

	handleAutoCreateCameraChange(event) {
		this.setState({
			autoCreateCamera: !!(event && event.target ? event.target.checked : false)
		});
	}

	handleToggleDeviceGuides() {
		const canvasAnchor = this.captureCanvasImageAnchor();
		this.setState((prevState) => {
			const nextValue = !prevState.showDeviceGuides;
			setLs(DEVICE_GUIDES_VISIBLE_KEY, nextValue ? "true" : "false");
			return {
				showDeviceGuides: nextValue
			};
		}, () => {
			this.restoreCanvasImageAnchor(canvasAnchor);
		});
	}

	handleToggleDeviceGuidesPanelExpanded() {
		this.setState((prevState) => {
			const nextValue = !prevState.deviceGuidesPanelExpanded;
			setLs(DEVICE_GUIDES_PANEL_EXPANDED_KEY, nextValue ? "true" : "false");

			return {
				deviceGuidesPanelExpanded: nextValue
			};
		});
	}

	handleToggleDeviceGuideSelection(guideId) {
		const canvasAnchor = this.captureCanvasImageAnchor();
		this.setState((prevState) => {
			const nextIds = prevState.activeDeviceGuideIds.includes(guideId)
				? prevState.activeDeviceGuideIds.filter((id) => id !== guideId)
				: [...prevState.activeDeviceGuideIds, guideId];
			setLs(DEVICE_GUIDES_SELECTED_KEY, nextIds.length > 0 ? nextIds.join(",") : "__none__");
			return {
				activeDeviceGuideIds: nextIds
			};
		}, () => {
			this.restoreCanvasImageAnchor(canvasAnchor);
		});
	}

	handleSelectAllDeviceGuides() {
		const canvasAnchor = this.captureCanvasImageAnchor();
		const nextIds = DEVICE_GUIDE_OPTIONS.map((guide) => guide.id);
		setLs(DEVICE_GUIDES_SELECTED_KEY, nextIds.join(","));
		setLs(DEVICE_GUIDES_VISIBLE_KEY, "true");
		this.setState({
			showDeviceGuides: true,
			activeDeviceGuideIds: nextIds
		}, () => {
			this.restoreCanvasImageAnchor(canvasAnchor);
		});
	}

	handleSelectPrimaryDeviceGuides() {
		const canvasAnchor = this.captureCanvasImageAnchor();
		setLs(DEVICE_GUIDES_SELECTED_KEY, DEFAULT_DEVICE_GUIDE_IDS.join(","));
		setLs(DEVICE_GUIDES_VISIBLE_KEY, "true");
		this.setState({
			showDeviceGuides: true,
			activeDeviceGuideIds: DEFAULT_DEVICE_GUIDE_IDS
		}, () => {
			this.restoreCanvasImageAnchor(canvasAnchor);
		});
	}

	handleToggleLabels() {
		this.setState((prevState) => {
			const nextValue = !prevState.showLabels;
			setLs("mediaMarkupV2ShowLabels", nextValue ? "true" : "false");
			return {
				showLabels: nextValue
			};
		});
	}

	handleToggleShowOnlyActiveCanvasItems(event) {
		const nextValue = !!(event && event.target ? event.target.checked : false);
		setLs(ACTIVE_TIMELINE_FILTER_KEY, nextValue ? "true" : "false");
		this.setState({
			showOnlyActiveCanvasItems: nextValue
		});
	}

	handleToggleAudioMuted(event) {
		const nextValue = !!(event && event.target ? event.target.checked : false);
		setLs(AUDIO_MUTED_KEY, nextValue ? "true" : "false");
		if (this.audioWaveformController && this.audioWaveformController.setMuted) {
			this.audioWaveformController.setMuted(nextValue);
		}
		this.setState({
			audioMuted: nextValue
		});
	}

	handleToggleWaveformCollapsed() {
		this.setState((prevState) => {
			const nextValue = !prevState.waveformCollapsed;
			setLs("mediaMarkupV2WaveformCollapsed", nextValue ? "true" : "false");
			return {
				waveformCollapsed: nextValue
			};
		});
	}

	handleLayoutModeChange(event) {
		const nextLayoutMode = normalizeLayoutMode(event && event.target ? event.target.value : LAYOUT_MODE_SPLIT);
		setLs(LAYOUT_MODE_KEY, nextLayoutMode);
		this.setState({
			layoutMode: nextLayoutMode
		});
	}

	handleToggleLeftPanelCollapsed() {
		this.setState((prevState) => {
			const nextValue = !prevState.leftPanelCollapsed;
			setLs(LEFT_PANEL_COLLAPSED_KEY, nextValue ? "true" : "false");
			return {
				leftPanelCollapsed: nextValue
			};
		});
	}

	handleToggleRightPanelCollapsed() {
		this.setState((prevState) => {
			const nextValue = !prevState.rightPanelCollapsed;
			setLs(RIGHT_PANEL_COLLAPSED_KEY, nextValue ? "true" : "false");
			return {
				rightPanelCollapsed: nextValue
			};
		});
	}

	handleEditScaleChange(event) {
		const editScale = clampValue(parseInt(event && event.target ? event.target.value : 100, 10) || 100, 25, 200);
		setLs(EDIT_SCALE_KEY, `${editScale}`);
		this.setState({
			editScale
		});
	}

	handleCanvasPaddingChange(event) {
		const nextPadding = clampValue(parseInt(event && event.target ? event.target.value : EDITOR_CANVAS_PADDING_DEFAULT, 10) || 0, 0, 1600);
		const canvasAnchor = this.captureCanvasImageAnchor();
		setLs(EDITOR_CANVAS_PADDING_KEY, `${nextPadding}`);
		this.setState({
			canvasPadding: nextPadding
		}, () => {
			this.restoreCanvasImageAnchor(canvasAnchor);
		});
	}

	handlePreviewScaleChange(event) {
		const previewScale = clampValue(parseInt(event && event.target ? event.target.value : 100, 10) || 100, 25, 200);
		setLs(PREVIEW_SCALE_KEY, `${previewScale}`);
		this.setState({
			previewScale
		});
	}

	handlePreviewDevicePresetChange(event) {
		const previewDevicePreset = normalizePreviewDevicePreset(event && event.target ? event.target.value : "responsive");
		setLs(PREVIEW_DEVICE_PRESET_KEY, previewDevicePreset);
		this.setState({
			previewDevicePreset
		});
	}

	handlePreviewDeviceOrientationToggle() {
		this.setState((prevState) => {
			const previewDeviceOrientation = prevState.previewDeviceOrientation === "landscape"
				? "portrait"
				: "landscape";
			setLs(PREVIEW_DEVICE_ORIENTATION_KEY, previewDeviceOrientation);
			return {
				previewDeviceOrientation
			};
		});
	}

	handleCanvasModeChange(canvasMode) {
		this.setState({
			canvasMode,
			drawing: canvasMode === "edit" ? this.state.drawing : false,
			colorPickTarget: canvasMode === "edit" ? this.state.colorPickTarget : "",
			pointerInCanvas: false
		});
	}

	handleCanvasMouseDown(event) {
		if (this.state.canvasMode !== "edit") {
			return;
		}

		if (event && typeof event.button === "number" && event.button !== 0) {
			return;
		}

		const point = this.getCanvasPointerPosition(event);
		if (!point) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		if (this.state.colorPickTarget) {
			this.setState({
				mouseX: point.x,
				mouseY: point.y,
				pointerInCanvas: true
			});
			return;
		}
		this.setState({
			drawing: true,
			drawStartX: point.x,
			drawStartY: point.y,
			drawCurrentX: point.x,
			drawCurrentY: point.y,
			mouseX: point.x,
			mouseY: point.y,
			pointerInCanvas: true
		});
		this.clearSelection();
	}

	handleCanvasMouseMove(event) {
		const point = this.getCanvasPointerPosition(event);
		if (!point) {
			return;
		}

		if (this.state.drawing) {
			this.setState({
				drawCurrentX: point.x,
				drawCurrentY: point.y,
				mouseX: point.x,
				mouseY: point.y,
				pointerInCanvas: true
			});
			return;
		}

		this.setState({
			mouseX: point.x,
			mouseY: point.y,
			pointerInCanvas: true
		});
	}

	handleCanvasMouseUp(event) {
		if (this.state.canvasMode !== "edit") {
			return;
		}

		const pointerPoint = this.getCanvasPointerPosition(event);
		if (this.state.colorPickTarget) {
			if (event) {
				event.preventDefault();
				event.stopPropagation();
			}
			if (pointerPoint) {
				this.handleColorPickFromPoint(pointerPoint);
			}
			return;
		}

		if (!this.state.drawing) {
			return;
		}

		const point = pointerPoint || {
			x: this.state.drawCurrentX,
			y: this.state.drawCurrentY
		};
		const x = Math.min(this.state.drawStartX, point.x);
		const y = Math.min(this.state.drawStartY, point.y);
		const width = Math.abs(point.x - this.state.drawStartX);
		const height = Math.abs(point.y - this.state.drawStartY);

		if (width < 2 || height < 2) {
			this.setState({
				drawing: false
			});
			return;
		}

		if (this.state.componentType === "camera") {
			const nextCamera = this.buildCameraItem({ x, y, width, height });
			this.skipCanvasClick = true;
			this.applyDocumentChange((prevState) => ({
				imageKenBurnsEffect: [...prevState.imageKenBurnsEffect, nextCamera],
				drawing: false,
				selectedItemKind: "camera",
				selectedItemGuid: nextCamera.guid,
				mouseX: point.x,
				mouseY: point.y
			}));
			return;
		}

		const nextOverlay = this.buildOverlayItem({ x, y, width, height }, this.state.componentType, this.state.shape);
		const nextState = {
			imageHotspots: [...this.state.imageHotspots, nextOverlay],
			drawing: false,
			selectedItemKind: "overlay",
			selectedItemGuid: nextOverlay.guid,
			mouseX: point.x,
			mouseY: point.y
		};

		if (this.state.autoCreateCamera) {
			const nextCamera = this.buildCameraItem(this.buildCameraBoundsForOverlay(nextOverlay.coords));
			nextState.imageKenBurnsEffect = [...this.state.imageKenBurnsEffect, nextCamera];
		}

		this.skipCanvasClick = true;
		this.applyDocumentChange(() => nextState);
	}

	handleCanvasMouseLeave(event) {
		this.handleCanvasMouseUp(event);
		this.setState({
			pointerInCanvas: false
		});
	}

	handleCanvasClick() {
		if (this.skipCanvasClick) {
			this.skipCanvasClick = false;
			return;
		}

		if (this.consumePendingInlineRenameActivation()) {
			return;
		}

		if (this.state.colorPickTarget) {
			return;
		}

		if (this.state.drawing || this.state.canvasMode !== "edit") {
			return;
		}

		this.clearSelection();
	}

	handleDeleteSelectedItem() {
		const selectedItem = this.getSelectedItem();
		if (!selectedItem) {
			return;
		}

		if (this.state.selectedItemKind === "camera") {
			this.applyDocumentChange((prevState) => ({
				imageKenBurnsEffect: prevState.imageKenBurnsEffect.filter((item) => item.guid !== selectedItem.guid),
				imageKenBurnsEffectVariants: Object.keys(prevState.imageKenBurnsEffectVariants || {}).reduce((result, variantKey) => {
					const variantCameras = _.cloneDeep(_.get(prevState.imageKenBurnsEffectVariants, [variantKey, "cameras"], {}));
					delete variantCameras[selectedItem.guid];
					return buildCameraVariantStatePatch(result, variantKey, variantCameras);
				}, {}),
				selectedItemKind: "",
				selectedItemGuid: ""
			}));
			return;
		}

		this.applyDocumentChange((prevState) => ({
			imageHotspots: prevState.imageHotspots.filter((item) => item.guid !== selectedItem.guid),
			selectedItemKind: "",
			selectedItemGuid: ""
		}));
	}

	handleCloneSelectedItem() {
		const selectedItem = this.getSelectedItem();
		if (!selectedItem) {
			return;
		}

		if (this.state.selectedItemKind === "camera") {
			const baseCamera = this.getBaseCameraItemByGuid(selectedItem.guid) || selectedItem;
			const clonedCamera = _.cloneDeep(baseCamera);
			clonedCamera.guid = uuidv4();
			clonedCamera.id = `camera${this.state.imageKenBurnsEffect.length + 1}`;
			clonedCamera.camera.end = Object.assign({}, clonedCamera.camera.end || {}, this.clampRectToBounds({
				x: _.get(clonedCamera, "camera.end.x", 0) + 24,
				y: _.get(clonedCamera, "camera.end.y", 0) + 24,
				width: _.get(clonedCamera, "camera.end.width", 0),
				height: _.get(clonedCamera, "camera.end.height", 0)
			}, "camera"));
			this.applyDocumentChange((prevState) => {
				let nextVariantState = prevState.imageKenBurnsEffectVariants;
				if (!this.isEditingBaseCameraVariant()) {
					const effectiveRect = _.get(selectedItem, "camera.end", null);
					if (effectiveRect) {
						const nextOverride = this.buildCameraVariantOverrideFromRect(
							Object.assign({ rotation: 0 }, clonedCamera.camera.end || {}),
							Object.assign({}, this.clampRectToBounds({
								x: Number(effectiveRect.x) + 24,
								y: Number(effectiveRect.y) + 24,
								width: Number(effectiveRect.width),
								height: Number(effectiveRect.height)
							}, "camera"), {
								rotation: Number(_.get(effectiveRect, "rotation", 0)) || 0
							})
						);
						const activeVariant = normalizeCameraVariantKey(this.state.activeCameraVariant);
						const currentVariantCameras = _.cloneDeep(_.get(prevState.imageKenBurnsEffectVariants, [activeVariant, "cameras"], {}));
						if (!_.isEmpty(nextOverride)) {
							currentVariantCameras[clonedCamera.guid] = nextOverride;
						}
						nextVariantState = buildCameraVariantStatePatch(prevState.imageKenBurnsEffectVariants, activeVariant, currentVariantCameras);
					}
				}

				return {
					imageKenBurnsEffect: [...prevState.imageKenBurnsEffect, clonedCamera],
					imageKenBurnsEffectVariants: nextVariantState,
					selectedItemKind: "camera",
					selectedItemGuid: clonedCamera.guid
				};
			});
			return;
		}

		const clonedOverlay = _.cloneDeep(selectedItem);
		const nextIdBase = clonedOverlay.shape === "arrow" ? "arrow" : (clonedOverlay.type || "highlight");
		clonedOverlay.guid = uuidv4();
		clonedOverlay.id = `${nextIdBase}${this.state.imageHotspots.length + 1}`;
		clonedOverlay.coords = this.clampRectToBounds({
			x: _.get(clonedOverlay, "coords.x", 0) + 24,
			y: _.get(clonedOverlay, "coords.y", 0) + 24,
			width: _.get(clonedOverlay, "coords.width", 0),
			height: _.get(clonedOverlay, "coords.height", 0)
		}, "overlay");
		this.applyDocumentChange((prevState) => ({
			imageHotspots: [...prevState.imageHotspots, clonedOverlay],
			selectedItemKind: "overlay",
			selectedItemGuid: clonedOverlay.guid
		}), () => {
			this.selectItem("overlay", clonedOverlay.guid);
		});
	}

	handleClearAll() {
		showConfirm(
			"Are you sure you want to remove all hotspots, highlights, and cameras from this media markup?",
			"Clear Media Markup",
			() => {
				this.stopPreviewPlayback();
				this.applyDocumentChange(() => ({
					imageHotspots: [],
					imageKenBurnsEffect: [],
					imageKenBurnsEffectVariants: {},
					selectedItemKind: "",
					selectedItemGuid: "",
					currentTimeMs: 0,
					currentCursorMs: 0,
					currentRegion: null,
					previewPlaying: false
				}));
				this.lastAudioTimeMs = 0;
				if (this.audioWaveformController && this.audioWaveformController.seekToSeconds) {
					this.audioWaveformController.seekToSeconds(0);
				}
			}
		);
	}

	handleToggleSelectedCanvasVisibility() {
		const selectedItem = this.getSelectedItem();
		if (!selectedItem) {
			return;
		}

		this.updateItemByGuid(this.state.selectedItemKind, selectedItem.guid, (item) => {
			item.canvasVisible = this.isCanvasItemVisible(item) ? false : true;
			return item;
		});
	}

	handleSetItemCanvasVisibility(kind, guid, visible) {
		this.updateItemByGuid(kind, guid, (item) => {
			item.canvasVisible = visible;
			return item;
		});
	}

	handleToggleItemCanvasVisibility(kind, guid) {
		const item = this.getItemsForKind(kind).find((entry) => entry.guid === guid);
		if (!item) {
			return;
		}

		this.handleSetItemCanvasVisibility(kind, guid, !this.isCanvasItemVisible(item));
	}

	handleToggleAllCanvasVisibility(kind) {
		const stateKey = this.getStateKeyForKind(kind);
		const items = this.getItemsForKind(kind);
		if (!items.length) {
			return;
		}

		const allVisible = items.every((item) => this.isCanvasItemVisible(item));
		this.applyDocumentChange((prevState) => ({
			[stateKey]: prevState[stateKey].map((item) => Object.assign({}, item, {
				canvasVisible: allVisible ? false : true
			}))
		}));
	}

	getSourceItemsForKind(kind, documentState = this.state) {
		return kind === "camera" ? documentState.imageKenBurnsEffect : documentState.imageHotspots;
	}

	getItemIndexByGuid(kind, guid, documentState = this.state) {
		return this.getSourceItemsForKind(kind, documentState).findIndex((item) => item.guid === guid);
	}

	handleReorderItem(kind, guid, action = "forward", event) {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}

		const stateKey = this.getStateKeyForKind(kind);
		this.applyDocumentChange((prevState) => {
			const sourceItems = [...this.getSourceItemsForKind(kind, prevState)];
			const currentIndex = sourceItems.findIndex((item) => item.guid === guid);
			if (currentIndex < 0 || sourceItems.length < 2) {
				return null;
			}

			let nextIndex = currentIndex;
			if (action === "forward") {
				nextIndex = Math.min(currentIndex + 1, sourceItems.length - 1);
			} else if (action === "backward") {
				nextIndex = Math.max(currentIndex - 1, 0);
			} else if (action === "front") {
				nextIndex = sourceItems.length - 1;
			} else if (action === "back") {
				nextIndex = 0;
			}

			if (nextIndex === currentIndex) {
				return null;
			}

			const [movedItem] = sourceItems.splice(currentIndex, 1);
			sourceItems.splice(nextIndex, 0, movedItem);
			return {
				[stateKey]: sourceItems
			};
		});
	}

	handleReorderSelectedItem(action = "forward") {
		if (!this.state.selectedItemKind || !this.state.selectedItemGuid) {
			return;
		}

		this.handleReorderItem(this.state.selectedItemKind, this.state.selectedItemGuid, action);
	}

	setSelectedItemRect(nextRect, options = {}) {
		const selectedItem = this.getSelectedItem();
		if (!selectedItem || !nextRect) {
			return;
		}

		const {
			callback,
			...documentOptions
		} = options || {};
		const kind = this.state.selectedItemKind;
		if (kind === "camera") {
			this.setSelectedCameraVariantRect(nextRect, callback, documentOptions);
			return;
		}

		const safeRect = this.clampRectToBounds(nextRect, kind);
		this.updateItemByGuid(kind, selectedItem.guid, (item) => {
			item.coords = safeRect;
			return item;
		}, callback, documentOptions);
	}

	setSelectedItemRotation(nextRotation, options = {}) {
		const selectedItem = this.getSelectedItem();
		if (!selectedItem) {
			return;
		}

		const {
			callback,
			...documentOptions
		} = options || {};
		const safeRotation = normalizeRotation(nextRotation);
		if (this.state.selectedItemKind === "camera") {
			this.setSelectedCameraVariantRotation(safeRotation, callback, documentOptions);
			return;
		}

		this.updateItemByGuid(this.state.selectedItemKind, selectedItem.guid, (item) => {
			item.rotation = safeRotation;
			return item;
		}, callback, documentOptions);
	}

	handleSelectedTimingChange(field, event) {
		const selectedItem = this.getSelectedItem();
		if (!selectedItem) {
			return;
		}

		const rawValue = event && event.target ? event.target.value : "";
		const parsedValue = rawValue === "" ? 0 : Math.round(Number(rawValue) || 0);
		this.updateItemByGuid(this.state.selectedItemKind, selectedItem.guid, (item) => {
			item[field] = field === "endTime" ? parsedValue : Math.max(parsedValue, 0);
			return item;
		});
	}

	handleSelectedIdChange(event) {
		const selectedItem = this.getSelectedItem();
		if (!selectedItem) {
			return;
		}

		const nextValue = event && event.target ? event.target.value : "";
		this.updateItemByGuid(this.state.selectedItemKind, selectedItem.guid, (item) => {
			item.id = nextValue;
			return item;
		});
	}

	startInlineRename(kind, guid, surface = "list") {
		const nextState = this.getSelectionState(kind, guid);
		if (!nextState) {
			return;
		}

		this.pendingInlineRenameActivation = true;
		this.setState({
			...nextState,
			renamingItemKind: kind,
			renamingItemGuid: guid,
			renamingSurface: surface,
			renamingItemValue: (
				(kind === "camera" ? this.state.imageKenBurnsEffect : this.state.imageHotspots)
					.find((entry) => entry.guid === guid)?.id
			) || ""
		}, this.focusInlineRenameInput);
	}

	focusInlineRenameInput() {
		window.requestAnimationFrame(() => {
			const input = this.inlineRenameInputRef && this.inlineRenameInputRef.current;
			if (input && typeof input.focus === "function") {
				input.focus();
				if (typeof input.select === "function") {
					input.select();
				}
			}

			window.requestAnimationFrame(() => {
				this.pendingInlineRenameActivation = false;
			});
		});
	}

	consumePendingInlineRenameActivation() {
		if (!this.pendingInlineRenameActivation) {
			return false;
		}

		this.pendingInlineRenameActivation = false;
		return true;
	}

	handleInlineRenameChange(event) {
		this.setState({
			renamingItemValue: event && event.target ? event.target.value : ""
		});
	}

	commitInlineRename() {
		this.pendingInlineRenameActivation = false;
		const {
			renamingItemKind,
			renamingItemGuid,
			renamingItemValue
		} = this.state;
		if (!renamingItemKind || !renamingItemGuid) {
			return;
		}

		const items = renamingItemKind === "camera" ? this.state.imageKenBurnsEffect : this.state.imageHotspots;
		const item = items.find((entry) => entry.guid === renamingItemGuid);
		if (!item) {
			this.setState({
				renamingItemKind: "",
				renamingItemGuid: "",
				renamingSurface: "",
				renamingItemValue: ""
			});
			return;
		}

		const nextValue = `${renamingItemValue || ""}`.trim() || item.id || "";
		this.updateItemByGuid(renamingItemKind, renamingItemGuid, (entry) => {
			entry.id = nextValue;
			return entry;
		}, () => {
			this.setState({
				renamingItemKind: "",
				renamingItemGuid: "",
				renamingSurface: "",
				renamingItemValue: ""
			});
		});
	}

	cancelInlineRename() {
		this.pendingInlineRenameActivation = false;
		this.setState({
			renamingItemKind: "",
			renamingItemGuid: "",
			renamingSurface: "",
			renamingItemValue: ""
		});
	}

	handleInlineRenameKeyDown(event) {
		if (!event) {
			return;
		}

		if (event.key === "Enter") {
			event.preventDefault();
			event.stopPropagation();
			this.commitInlineRename();
		} else if (event.key === "Escape") {
			event.preventDefault();
			event.stopPropagation();
			this.cancelInlineRename();
		}
	}

	getSelectedNumericDraftKey(kind = this.state.selectedItemKind, guid = this.state.selectedItemGuid) {
		return kind && guid ? `${kind}:${guid}` : "";
	}

	buildSelectedNumericDraftFields(selectedRect = this.getSelectedItemRect(), selectedRotation = this.getSelectedItemRotation()) {
		if (!selectedRect) {
			return null;
		}

		return {
			x: `${Math.round(Number(selectedRect.x) || 0)}`,
			y: `${Math.round(Number(selectedRect.y) || 0)}`,
			width: `${Math.round(Number(selectedRect.width) || 0)}`,
			height: `${Math.round(Number(selectedRect.height) || 0)}`,
			rotation: `${Math.round(Number(selectedRotation) || 0)}`
		};
	}

	ensureSelectedNumericDrafts() {
		const draftKey = this.getSelectedNumericDraftKey();
		if (!draftKey) {
			return;
		}

		this.setState((prevState) => {
			if (prevState.selectedNumericDrafts && prevState.selectedNumericDrafts.key === draftKey) {
				return null;
			}

			const nextFields = this.buildSelectedNumericDraftFields();
			if (!nextFields) {
				return null;
			}

			return {
				selectedNumericDrafts: {
					key: draftKey,
					fields: nextFields
				}
			};
		});
	}

	clearSelectedNumericDrafts() {
		this.setState({
			selectedNumericDrafts: null
		});
	}

	getSelectedNumericFieldValue(field, fallbackValue) {
		const draftKey = this.getSelectedNumericDraftKey();
		const draftState = this.state.selectedNumericDrafts;
		if (draftState && draftState.key === draftKey && draftState.fields && Object.prototype.hasOwnProperty.call(draftState.fields, field)) {
			return draftState.fields[field];
		}

		return `${fallbackValue}`;
	}

	parseSelectedNumericDraftValue(field, rawValue) {
		const normalizedValue = `${rawValue === undefined || rawValue === null ? "" : rawValue}`.trim();
		if (normalizedValue === "" || normalizedValue === "-" || normalizedValue === "+") {
			return null;
		}

		const parsedValue = Number(normalizedValue);
		if (!Number.isFinite(parsedValue)) {
			return null;
		}

		const roundedValue = Math.round(parsedValue);
		if (field === "width" || field === "height") {
			return Math.max(roundedValue, MIN_ITEM_SIZE);
		}

		if (field === "rotation") {
			return normalizeRotation(roundedValue);
		}

		return roundedValue;
	}

	handleSelectedNumericDraftChange(field, event) {
		const nextValue = event && event.target ? event.target.value : "";
		const draftKey = this.getSelectedNumericDraftKey();
		if (!draftKey) {
			return;
		}

		this.setState((prevState) => {
			const currentDraftFields = prevState.selectedNumericDrafts && prevState.selectedNumericDrafts.key === draftKey
				? prevState.selectedNumericDrafts.fields
				: this.buildSelectedNumericDraftFields();
			if (!currentDraftFields) {
				return null;
			}

			return {
				selectedNumericDrafts: {
					key: draftKey,
					fields: Object.assign({}, currentDraftFields, {
						[field]: nextValue
					})
				}
			};
		});
	}

	commitSelectedNumericDraftField(field) {
		const selectedRect = this.getSelectedItemRect();
		if (!selectedRect) {
			this.clearSelectedNumericDrafts();
			return;
		}

		const draftKey = this.getSelectedNumericDraftKey();
		const draftState = this.state.selectedNumericDrafts;
		if (!draftState || draftState.key !== draftKey) {
			return;
		}

		const rawValue = _.get(draftState, ["fields", field], "");
		const committedValue = field === "rotation"
			? this.getSelectedItemRotation()
			: selectedRect[field];
		const nextValue = this.parseSelectedNumericDraftValue(field, rawValue, committedValue);
		if (nextValue === null || Number(nextValue) === Number(committedValue)) {
			this.clearSelectedNumericDrafts();
			return;
		}

		if (field === "rotation") {
			this.setSelectedItemRotation(nextValue, {
				callback: this.clearSelectedNumericDrafts
			});
			return;
		}

		const nextRect = Object.assign({}, selectedRect, {
			[field]: nextValue
		});
		this.setSelectedItemRect(nextRect, {
			callback: this.clearSelectedNumericDrafts
		});
	}

	handleSelectedNumericDraftBlur(field) {
		this.commitSelectedNumericDraftField(field);
	}

	handleSelectedNumericDraftKeyDown(field, event) {
		if (!event) {
			return;
		}

		if (event.key === "Enter") {
			event.preventDefault();
			event.stopPropagation();
			this.commitSelectedNumericDraftField(field);
			return;
		}

		if (event.key === "Escape") {
			event.preventDefault();
			event.stopPropagation();
			this.clearSelectedNumericDrafts();
		}
	}

	handleSelectedRectFieldChange(field, event) {
		this.handleSelectedNumericDraftChange(field, event);
	}

	handleSelectedRotationInputChange(event) {
		this.handleSelectedNumericDraftChange("rotation", event);
	}

	handleSelectedLabelChange(event) {
		const selectedItem = this.getSelectedItem();
		if (!selectedItem || this.state.selectedItemKind !== "overlay") {
			return;
		}

		const nextValue = event && event.target ? event.target.value : "";
		this.updateItemByGuid("overlay", selectedItem.guid, (item) => {
			item.label = nextValue;
			return item;
		});
	}

	handleSelectedLabelVisibleChange(event) {
		const selectedItem = this.getSelectedItem();
		if (!selectedItem || this.state.selectedItemKind !== "overlay") {
			return;
		}

		const nextValue = !!(event && event.target ? event.target.checked : false);
		this.updateItemByGuid("overlay", selectedItem.guid, (item) => {
			item.labelVisible = nextValue;
			return item;
		});
	}

	handleSelectedActionChange(event) {
		const selectedItem = this.getSelectedItem();
		if (!selectedItem || this.state.selectedItemKind !== "overlay") {
			return;
		}

		const nextValue = event && event.target ? event.target.value : "none";
		this.updateItemByGuid("overlay", selectedItem.guid, (item) => {
			item.action = nextValue;
			return item;
		});
	}

	handleSelectedStartZoomedChange(event) {
		const selectedItem = this.getSelectedItem();
		if (!selectedItem || this.state.selectedItemKind !== "camera") {
			return;
		}

		const nextValue = !!(event && event.target ? event.target.checked : false);
		this.updateItemByGuid("camera", selectedItem.guid, (item) => {
			item.camera = item.camera || {};
			item.camera.startZoomed = nextValue;
			if (nextValue && !item.camera.start) {
				item.startTime = 0;
				item.endTime = 0;
				item.duration = 0;
			} else if (!nextValue) {
				delete item.camera.start;
				delete item.camera.startSource;
				delete item.camera.startSourceItemGuid;
				delete item.camera.startSourceCameraGuid;
			}
			return item;
		});
	}

	getPreviousCameraStartContext() {
		return _.isPlainObject(this.props.previousCameraStartContext)
			? this.props.previousCameraStartContext
			: null;
	}

	getPreviousCameraStartRect() {
		const previousContext = this.getPreviousCameraStartContext();
		if (!previousContext) {
			return null;
		}

		const activeVariant = normalizeCameraVariantKey(this.state.activeCameraVariant);
		const variantRect = activeVariant !== CAMERA_VARIANT_BASE
			? _.get(previousContext, ["cameraStartVariants", activeVariant], null)
			: null;
		return variantRect || previousContext.cameraStart || null;
	}

	getCameraStartSnapshot(rect = null) {
		if (!rect || !Object.prototype.hasOwnProperty.call(rect, "width") || !Object.prototype.hasOwnProperty.call(rect, "height")) {
			return null;
		}

		return Object.assign({}, this.clampRectToBounds(rect, "camera"), {
			rotation: normalizeRotation(rect.rotation || 0)
		});
	}

	isFirstCameraGuid(guid) {
		const firstCamera = Array.isArray(this.state.imageKenBurnsEffect) && this.state.imageKenBurnsEffect.length > 0
			? this.state.imageKenBurnsEffect[0]
			: null;
		return !!(guid && firstCamera && firstCamera.guid === guid);
	}

	handleApplyPreviousCameraStartToSelected(event) {
		if (event && event.preventDefault) {
			event.preventDefault();
		}

		const selectedItem = this.getSelectedItem();
		if (!selectedItem || this.state.selectedItemKind !== "camera") {
			return;
		}
		if (!this.isFirstCameraGuid(selectedItem.guid)) {
			return;
		}

		const previousStartRect = this.getCameraStartSnapshot(this.getPreviousCameraStartRect());
		if (!previousStartRect) {
			return;
		}

		const previousContext = this.getPreviousCameraStartContext() || {};
		this.updateItemByGuid("camera", selectedItem.guid, (item) => {
			item.camera = item.camera || {};
			item.camera.startZoomed = true;
			item.camera.start = previousStartRect;
			item.camera.end = Object.assign({}, item.camera.end || {}, previousStartRect);
			item.camera.startSource = CAMERA_START_SOURCE_PREVIOUS_CAMERA_END;
			item.camera.startSourceItemGuid = previousContext.sourceItemGuid || "";
			item.camera.startSourceCameraGuid = previousContext.sourceCameraGuid || "";
			item.startTime = 0;
			item.endTime = 0;
			item.duration = 0;
			return item;
		});
	}

	handleClearSelectedCameraStart(event) {
		if (event && event.preventDefault) {
			event.preventDefault();
		}

		const selectedItem = this.getSelectedItem();
		if (!selectedItem || this.state.selectedItemKind !== "camera") {
			return;
		}

		this.updateItemByGuid("camera", selectedItem.guid, (item) => {
			item.camera = item.camera || {};
			delete item.camera.start;
			delete item.camera.startSource;
			delete item.camera.startSourceItemGuid;
			delete item.camera.startSourceCameraGuid;
			return item;
		});
	}

	handleApplyCurrentCursorOrRegionToSelected() {
		const selectedItem = this.getSelectedItem();
		if (!selectedItem) {
			return;
		}

		const timing = this.getDraftTiming(this.state.selectedItemKind === "camera");
		this.updateItemByGuid(this.state.selectedItemKind, selectedItem.guid, (item) => {
			item.startTime = timing.startTime;
			item.endTime = timing.endTime;
			return item;
		});
	}

	startMoveDrag(kind, item, event) {
		const point = this.getCanvasPointerPosition(event);
		const startRect = kind === "camera" ? _.get(item, "camera.end", null) : item.coords;
		if (!point || !startRect) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		this.beginDocumentHistoryCapture();
		this.canvasPointerDrag = {
			type: "move",
			kind,
			guid: item.guid,
			startPoint: point,
			startRect: _.cloneDeep(startRect)
		};
		this.attachCanvasPointerListeners();
	}

	startRotateDrag(kind, item, event) {
		const point = this.getCanvasPointerPosition(event);
		const itemRect = kind === "camera" ? _.get(item, "camera.end", null) : item.coords;
		if (!point || !itemRect) {
			return;
		}

		const centerX = itemRect.x + (itemRect.width / 2);
		const centerY = itemRect.y + (itemRect.height / 2);

		event.preventDefault();
		event.stopPropagation();
		this.beginDocumentHistoryCapture();
		this.canvasPointerDrag = {
			type: "rotate",
			kind,
			guid: item.guid,
			centerX,
			centerY,
			startAngle: Math.atan2(point.y - centerY, point.x - centerX),
			startRotation: kind === "camera"
				? normalizeRotation(_.get(item, "camera.end.rotation", 0))
				: normalizeRotation(item.rotation || 0)
		};
		this.attachCanvasPointerListeners();
	}

	startResizeDrag(kind, item, handle, event) {
		const point = this.getCanvasPointerPosition(event);
		const itemRect = kind === "camera" ? _.get(item, "camera.end", null) : item.coords;
		if (!point || !itemRect) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		this.beginDocumentHistoryCapture();
		this.canvasPointerDrag = {
			type: "resize",
			kind,
			guid: item.guid,
			handle,
			startRect: _.cloneDeep(itemRect),
			rotation: kind === "camera"
				? normalizeRotation(_.get(item, "camera.end.rotation", 0))
				: normalizeRotation(item.rotation || 0)
		};
		this.attachCanvasPointerListeners();
	}

	attachCanvasPointerListeners() {
		const targetWindow = this.getCanvasOwnerWindow();
		if (!targetWindow) {
			return;
		}

		if (this.canvasPointerListenerWindow && this.canvasPointerListenerWindow !== targetWindow) {
			this.detachCanvasPointerListeners();
		}

		targetWindow.addEventListener("mousemove", this.handleWindowCanvasMouseMove);
		targetWindow.addEventListener("mouseup", this.handleWindowCanvasMouseUp);
		this.canvasPointerListenerWindow = targetWindow;
	}

	detachCanvasPointerListeners() {
		if (this.canvasPointerListenerWindow) {
			try {
				this.canvasPointerListenerWindow.removeEventListener("mousemove", this.handleWindowCanvasMouseMove);
				this.canvasPointerListenerWindow.removeEventListener("mouseup", this.handleWindowCanvasMouseUp);
			} catch (err) {
				// Ignore listener cleanup failures for already-closed windows.
			}
		}
		this.canvasPointerListenerWindow = null;
		this.canvasPointerDrag = null;
	}

	getWorkspacePixelWidth() {
		if (this.workspaceRef && this.workspaceRef.current) {
			return this.workspaceRef.current.clientWidth || 0;
		}

		if (typeof window !== "undefined") {
			return Math.max((window.innerWidth || 1280) - 120, 0);
		}

		return 0;
	}

	getClampedPanelWidths(nextItemListWidth = this.state.itemListPanelWidth, nextPropertiesWidth = this.state.propertiesPanelWidth) {
		const workspaceWidth = this.getWorkspacePixelWidth();
		if (!workspaceWidth) {
			return {
				itemListPanelWidth: clampValue(nextItemListWidth, ITEM_LIST_PANEL_MIN_WIDTH, ITEM_LIST_PANEL_MAX_WIDTH),
				propertiesPanelWidth: clampValue(nextPropertiesWidth, PROPERTIES_PANEL_MIN_WIDTH, PROPERTIES_PANEL_MAX_WIDTH)
			};
		}

		const layoutMode = normalizeLayoutMode(this.state.layoutMode);
		const usesLeftPanel = layoutMode === LAYOUT_MODE_SPLIT && !this.state.leftPanelCollapsed;
		const usesRightPanel = !this.state.rightPanelCollapsed;
		const singleResizerWidth = usesRightPanel || usesLeftPanel ? PANEL_RESIZER_WIDTH : 0;
		const dualResizerWidth = usesLeftPanel && usesRightPanel ? PANEL_RESIZER_WIDTH * 2 : singleResizerWidth;

		const maxItemListWidth = usesLeftPanel
			? Math.max(
				ITEM_LIST_PANEL_MIN_WIDTH,
				Math.min(
					ITEM_LIST_PANEL_MAX_WIDTH,
					workspaceWidth - WORKSPACE_CANVAS_MIN_WIDTH - (usesRightPanel ? PROPERTIES_PANEL_MIN_WIDTH : 0) - dualResizerWidth
				)
			)
			: ITEM_LIST_PANEL_MAX_WIDTH;
		const clampedItemListWidth = clampValue(nextItemListWidth, ITEM_LIST_PANEL_MIN_WIDTH, maxItemListWidth);
		const maxPropertiesWidth = usesRightPanel
			? Math.max(
				PROPERTIES_PANEL_MIN_WIDTH,
				Math.min(
					PROPERTIES_PANEL_MAX_WIDTH,
					workspaceWidth - WORKSPACE_CANVAS_MIN_WIDTH - (usesLeftPanel ? clampedItemListWidth : 0) - dualResizerWidth
				)
			)
			: PROPERTIES_PANEL_MAX_WIDTH;
		const clampedPropertiesWidth = clampValue(nextPropertiesWidth, PROPERTIES_PANEL_MIN_WIDTH, maxPropertiesWidth);

		return {
			itemListPanelWidth: clampedItemListWidth,
			propertiesPanelWidth: clampedPropertiesWidth
		};
	}

	persistPanelWidths(itemListPanelWidth = this.state.itemListPanelWidth, propertiesPanelWidth = this.state.propertiesPanelWidth) {
		setLs(ITEM_LIST_PANEL_WIDTH_KEY, `${Math.round(itemListPanelWidth)}`);
		setLs(PROPERTIES_PANEL_WIDTH_KEY, `${Math.round(propertiesPanelWidth)}`);
	}

	startPanelResize(side, event) {
		if (!event || (side !== "left" && side !== "right")) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		this.panelResizeDrag = {
			side,
			startClientX: event.clientX,
			startItemListWidth: this.state.itemListPanelWidth,
			startPropertiesWidth: this.state.propertiesPanelWidth
		};
		this.attachPanelResizeListeners();
	}

	attachPanelResizeListeners() {
		window.addEventListener("mousemove", this.handleWindowPanelResizeMouseMove);
		window.addEventListener("mouseup", this.handleWindowPanelResizeMouseUp);
	}

	detachPanelResizeListeners(shouldPersist = true) {
		window.removeEventListener("mousemove", this.handleWindowPanelResizeMouseMove);
		window.removeEventListener("mouseup", this.handleWindowPanelResizeMouseUp);
		if (shouldPersist && this.panelResizeDrag) {
			this.persistPanelWidths(
				this.panelResizeDrag.currentItemListWidth || this.state.itemListPanelWidth,
				this.panelResizeDrag.currentPropertiesWidth || this.state.propertiesPanelWidth
			);
		}
		this.panelResizeDrag = null;
	}

	handleWindowPanelResizeMouseMove(event) {
		const drag = this.panelResizeDrag;
		if (!drag) {
			return;
		}

		const deltaX = Number(event && event.clientX ? event.clientX : 0) - drag.startClientX;
		const nextItemListWidth = drag.side === "left"
			? drag.startItemListWidth + deltaX
			: drag.startItemListWidth;
		const nextPropertiesWidth = drag.side === "right"
			? drag.startPropertiesWidth - deltaX
			: drag.startPropertiesWidth;
		const clampedWidths = this.getClampedPanelWidths(nextItemListWidth, nextPropertiesWidth);
		this.panelResizeDrag = Object.assign({}, drag, {
			currentItemListWidth: clampedWidths.itemListPanelWidth,
			currentPropertiesWidth: clampedWidths.propertiesPanelWidth
		});

		this.setState(clampedWidths);
	}

	handleWindowPanelResizeMouseUp() {
		this.detachPanelResizeListeners(true);
	}

	getResizedRectFromHandle(startRect, handle, point, rotation) {
		const centerX = startRect.x + (startRect.width / 2);
		const centerY = startRect.y + (startRect.height / 2);
		const localPoint = rotatePointAroundCenter(point.x, point.y, centerX, centerY, -rotation);
		let left = startRect.x;
		let top = startRect.y;
		let right = startRect.x + startRect.width;
		let bottom = startRect.y + startRect.height;

		if (handle.indexOf("n") >= 0) {
			top = Math.min(localPoint.y, bottom - MIN_ITEM_SIZE);
		}
		if (handle.indexOf("s") >= 0) {
			bottom = Math.max(localPoint.y, top + MIN_ITEM_SIZE);
		}
		if (handle.indexOf("w") >= 0) {
			left = Math.min(localPoint.x, right - MIN_ITEM_SIZE);
		}
		if (handle.indexOf("e") >= 0) {
			right = Math.max(localPoint.x, left + MIN_ITEM_SIZE);
		}

		return {
			x: left,
			y: top,
			width: right - left,
			height: bottom - top
		};
	}

	handleWindowCanvasMouseMove(event) {
		const drag = this.canvasPointerDrag;
		if (!drag) {
			return;
		}

		const point = this.getCanvasPointerPosition(event);
		if (!point) {
			return;
		}

		this.setState({
			mouseX: point.x,
			mouseY: point.y
		});

		if (drag.type === "move") {
			const deltaX = point.x - drag.startPoint.x;
			const deltaY = point.y - drag.startPoint.y;
			this.setSelectedItemRect({
				x: drag.startRect.x + deltaX,
				y: drag.startRect.y + deltaY,
				width: drag.startRect.width,
				height: drag.startRect.height
			}, {
				commitHistory: false
			});
			return;
		}

		if (drag.type === "rotate") {
			const nextRotation = normalizeRotation(
				drag.startRotation + (((Math.atan2(point.y - drag.centerY, point.x - drag.centerX) - drag.startAngle) * 180) / Math.PI)
			);
			this.setSelectedItemRotation(nextRotation, {
				commitHistory: false
			});
			return;
		}

		if (drag.type === "resize") {
			this.setSelectedItemRect(this.getResizedRectFromHandle(drag.startRect, drag.handle, point, drag.rotation), {
				commitHistory: false
			});
		}
	}

	handleWindowCanvasMouseUp() {
		this.detachCanvasPointerListeners();
		this.finalizeDocumentHistoryCapture();
	}

	handleNudgeSelectedItem(deltaX = 0, deltaY = 0, options = {}) {
		const selectedRect = this.getSelectedItemRect();
		if (!selectedRect) {
			return;
		}

		this.setSelectedItemRect({
			x: selectedRect.x + deltaX,
			y: selectedRect.y + deltaY,
			width: selectedRect.width,
			height: selectedRect.height
		}, options);
	}

	handleRotateSelectedItem(deltaDegrees = 0, options = {}) {
		this.setSelectedItemRotation(this.getSelectedItemRotation() + deltaDegrees, options);
	}

	handleCameraPanSelectedItem(deltaX = 0, deltaY = 0) {
		if (this.state.selectedItemKind !== "camera") {
			return;
		}

		this.handleNudgeSelectedItem(deltaX, deltaY);
	}

	handleCameraScaleSelectedItem(scaleFactor = 1) {
		if (this.state.selectedItemKind !== "camera") {
			return;
		}

		const selectedRect = this.getSelectedItemRect();
		if (!selectedRect) {
			return;
		}

		const centerX = selectedRect.x + (selectedRect.width / 2);
		const centerY = selectedRect.y + (selectedRect.height / 2);
		const nextWidth = Math.max(Math.round(selectedRect.width * scaleFactor), MIN_ITEM_SIZE);
		const nextHeight = Math.max(Math.round(selectedRect.height * scaleFactor), MIN_ITEM_SIZE);
		this.setSelectedItemRect({
			x: Math.round(centerX - (nextWidth / 2)),
			y: Math.round(centerY - (nextHeight / 2)),
			width: nextWidth,
			height: nextHeight
		});
	}

	onAudioWaveformControllerReady(controller) {
		this.audioWaveformController = controller;
		if (!controller) {
			this.lastAudioTimeMs = 0;
			this.setState({
				audioReady: false,
				audioDurationMs: 0
			});
			return;
		}

		if (controller.setMuted) {
			controller.setMuted(this.state.audioMuted);
		}

		if (controller.getDurationSeconds) {
			const durationSeconds = controller.getDurationSeconds();
			if (Number.isFinite(durationSeconds) && durationSeconds >= 0) {
				this.setState({
					audioDurationMs: Math.round(durationSeconds * 1000),
					audioReady: true
				});
			}
		}
	}

	onAudioWaveformReady(info = {}) {
		const durationSeconds = Number(info.durationSeconds);
		this.setState({
			audioDurationMs: Number.isFinite(durationSeconds) && durationSeconds >= 0 ? Math.round(durationSeconds * 1000) : this.state.audioDurationMs,
			audioReady: true
		});
	}

	onAudioWaveformTimeChange(seconds) {
		const nextTimeMs = Math.round((Number(seconds) || 0) * 1000);
		this.lastAudioTimeMs = nextTimeMs;

		if (this.ignoreNextAudioTimeSync) {
			this.ignoreNextAudioTimeSync = false;
			return;
		}

		if (this.state.previewPlaying) {
			return;
		}

		this.setState({
			currentTimeMs: nextTimeMs,
			currentCursorMs: nextTimeMs
		});
	}

	onAudioWaveformPlayStateChange(isPlaying) {
		if (isPlaying) {
			this.suppressNextAudioPauseStop = false;
			if (!this.state.previewPlaying) {
				this.startPreviewPlayback(false);
			}
			return;
		}

		if (this.suppressNextAudioPauseStop) {
			this.suppressNextAudioPauseStop = false;
			return;
		}

		const previewDurationMs = this.getPreviewDurationMs();
		const audioFinished = this.state.audioDurationMs > 0
			&& this.lastAudioTimeMs >= (this.state.audioDurationMs - 80);
		if (this.state.previewPlaying && audioFinished && previewDurationMs > (this.state.audioDurationMs + 80)) {
			return;
		}

		this.stopPreviewPlayback();
		this.setState({
			previewPlaying: false
		});
	}

	onAudioWaveformFinish() {
		if (this.shouldKeepPreviewRunningWithoutAudio(Math.max(this.state.currentTimeMs, this.state.audioDurationMs))) {
			this.suppressNextAudioPauseStop = true;
			return;
		}

		this.stopPreviewPlayback();
		this.setState({
			previewPlaying: false
		});
	}

	onAudioWaveformRegionSelected(region) {
		this.setState({
			currentRegion: region || null
		});
	}

	onAudioWaveformCursorSelected(seconds) {
		const nextTimeMs = Math.round((Number(seconds) || 0) * 1000);
		this.lastAudioTimeMs = nextTimeMs;

		if (this.ignoreNextAudioCursorSync) {
			this.ignoreNextAudioCursorSync = false;
			return;
		}

		this.setState({
			currentCursorMs: nextTimeMs,
			currentTimeMs: nextTimeMs
		}, () => {
			if (this.state.previewPlaying) {
				this.previewPlaybackStartWallClock = window.performance.now();
				this.previewPlaybackStartTimeMs = nextTimeMs;
			}
		});
	}

	getPreviewViewportAspectRatio(stageWidth = 0, stageHeight = 0) {
		const previewViewportSpec = this.getPreviewViewportSpec(stageWidth, stageHeight);
		const fallbackWidth = _.get(this.state, "imageSize.width", 0);
		const fallbackHeight = _.get(this.state, "imageSize.height", 0);
		const viewportWidth = Number(previewViewportSpec.frameWidth) > 0 ? previewViewportSpec.frameWidth : fallbackWidth;
		const viewportHeight = Number(previewViewportSpec.frameHeight) > 0 ? previewViewportSpec.frameHeight : fallbackHeight;

		if (!(viewportWidth > 0) || !(viewportHeight > 0)) {
			return 0;
		}

		return viewportWidth / viewportHeight;
	}

	getAutoPreviewCameraVariantKey(stageWidth = 0, stageHeight = 0) {
		const previewAspectRatio = this.getPreviewViewportAspectRatio(stageWidth, stageHeight);
		if (!(previewAspectRatio > 0)) {
			return CAMERA_VARIANT_BASE;
		}

		let resolvedVariantKey = CAMERA_VARIANT_BASE;
		let closestDistance = Number.POSITIVE_INFINITY;

		CAMERA_VARIANT_OPTIONS.forEach((variantOption) => {
			if (!(variantOption.targetWidth > 0) || !(variantOption.targetHeight > 0)) {
				return;
			}

			const variantAspectRatio = variantOption.targetWidth / variantOption.targetHeight;
			const aspectDistance = Math.abs(Math.log(previewAspectRatio / variantAspectRatio));
			if (aspectDistance < closestDistance) {
				closestDistance = aspectDistance;
				resolvedVariantKey = variantOption.value;
			}
		});

		if (resolvedVariantKey !== CAMERA_VARIANT_BASE && !this.hasAnyCameraVariantOverrides(resolvedVariantKey)) {
			return CAMERA_VARIANT_BASE;
		}

		return resolvedVariantKey;
	}

	buildPreviewItem(variantKey = this.state.activeCameraVariant) {
		return {
			imageHotspots: this.state.imageHotspots,
			imageKenBurnsEffect: this.getEffectiveCameraItems(variantKey)
		};
	}

	getPreviewSceneState(options = {}) {
		const {
			variantKey = this.state.activeCameraVariant
		} = options;
		if (!this.state.imageSize) {
			return null;
		}

		return evaluateImageSceneAtTime({
			item: this.buildPreviewItem(variantKey),
			timeMs: this.state.currentTimeMs,
			imageSize: this.state.imageSize
		});
	}

	getPreviewDurationMs(variantKey = this.state.activeCameraVariant) {
		return estimateImageSceneDurationMs(this.buildPreviewItem(variantKey), this.state.audioDurationMs);
	}

	shouldKeepPreviewRunningWithoutAudio(timeMs = this.state.currentTimeMs) {
		const previewDurationMs = this.getPreviewDurationMs();
		return this.state.previewPlaying
			&& this.state.audioDurationMs > 0
			&& timeMs >= (this.state.audioDurationMs - 20)
			&& previewDurationMs > (this.state.audioDurationMs + 80);
	}

	stopPreviewPlayback() {
		this.suppressNextAudioPauseStop = false;
		if (this.previewAnimationFrame) {
			window.cancelAnimationFrame(this.previewAnimationFrame);
			this.previewAnimationFrame = null;
		}
	}

	startPreviewPlayback(syncAudio = false) {
		this.stopPreviewPlayback();
		const startTimeMs = Math.min(this.state.currentTimeMs, this.getPreviewDurationMs());
		this.previewPlaybackStartWallClock = window.performance.now();
		this.previewPlaybackStartTimeMs = startTimeMs;
		this.setState({
			previewPlaying: true,
			currentTimeMs: startTimeMs,
			currentCursorMs: startTimeMs
		}, () => {
			if (syncAudio && this.audioWaveformController) {
				if (!this.state.audioDurationMs || startTimeMs < (this.state.audioDurationMs - 20)) {
					this.syncAudioSeek(startTimeMs);
					if (this.audioWaveformController.play) {
						this.audioWaveformController.play();
					}
				} else if (this.audioWaveformController.pause) {
					this.suppressNextAudioPauseStop = !!(this.audioWaveformController.isPlaying && this.audioWaveformController.isPlaying());
					this.audioWaveformController.pause();
				}
			}
			this.previewAnimationFrame = window.requestAnimationFrame(this.handlePreviewPlaybackFrame);
		});
	}

	handlePreviewPlaybackFrame(now) {
		const durationMs = this.getPreviewDurationMs();
		const elapsedMs = now - this.previewPlaybackStartWallClock;
		const nextTimeMs = Math.min(this.previewPlaybackStartTimeMs + elapsedMs, durationMs);

		this.setState({
			currentTimeMs: nextTimeMs,
			currentCursorMs: nextTimeMs
		});

		if (nextTimeMs >= durationMs) {
			this.stopPreviewPlayback();
			if (this.audioWaveformController && this.audioWaveformController.pause) {
				this.audioWaveformController.pause();
			}
			this.setState({
				previewPlaying: false
			});
			return;
		}

		this.previewAnimationFrame = window.requestAnimationFrame(this.handlePreviewPlaybackFrame);
	}

	syncAudioSeek(nextTimeMs) {
		if (this.audioWaveformController && this.audioWaveformController.seekToSeconds) {
			this.audioWaveformController.seekToSeconds(nextTimeMs / 1000);
		}
	}

	handlePreviewPlayPause() {
		if (this.state.previewPlaying) {
			this.stopPreviewPlayback();
			if (this.audioWaveformController && this.audioWaveformController.pause) {
				this.audioWaveformController.pause();
			}
			this.setState({
				previewPlaying: false
			});
			return;
		}

		if (this.state.currentTimeMs >= this.getPreviewDurationMs()) {
			this.setState({
				currentTimeMs: 0,
				currentCursorMs: 0
			}, () => {
				this.startPreviewPlayback(!!this.props.audioUrl);
			});
			return;
		}

		this.startPreviewPlayback(!!this.props.audioUrl);
	}

	handlePreviewSeek(nextTimeMs, syncAudio = true) {
		const durationMs = this.getPreviewDurationMs();
		const safeTimeMs = clampValue(Math.round(Number(nextTimeMs) || 0), 0, durationMs);
		this.setState({
			currentTimeMs: safeTimeMs,
			currentCursorMs: safeTimeMs
		});
		if (this.state.previewPlaying) {
			this.previewPlaybackStartWallClock = window.performance.now();
			this.previewPlaybackStartTimeMs = safeTimeMs;
		}

		if (syncAudio && this.props.audioUrl) {
			if (this.audioWaveformController) {
				if (!this.state.audioDurationMs || safeTimeMs < (this.state.audioDurationMs - 20)) {
					this.syncAudioSeek(safeTimeMs);
				} else {
					this.ignoreNextAudioCursorSync = true;
					this.ignoreNextAudioTimeSync = true;
					this.syncAudioSeek(this.state.audioDurationMs);
				}

				if (this.state.previewPlaying && (!this.state.audioDurationMs || safeTimeMs < (this.state.audioDurationMs - 20))) {
					if (this.audioWaveformController.play) {
						this.audioWaveformController.play();
					}
				} else if (safeTimeMs >= (this.state.audioDurationMs - 20) && this.audioWaveformController.pause) {
					if (this.shouldKeepPreviewRunningWithoutAudio(safeTimeMs)) {
						this.suppressNextAudioPauseStop = !!(this.audioWaveformController.isPlaying && this.audioWaveformController.isPlaying());
					}
					this.audioWaveformController.pause();
				}
			}
		}
	}

	handlePreviewSeekToStart() {
		this.handlePreviewSeek(0, true);
	}

	handlePreviewStep(deltaMs) {
		this.handlePreviewSeek(this.state.currentTimeMs + deltaMs, true);
	}

	handlePreviewSliderChange(event) {
		this.handlePreviewSeek(event && event.target ? event.target.value : 0, true);
	}

	isKeyboardInputTarget(target) {
		if (!target) {
			return false;
		}

		if (target.isContentEditable) {
			return true;
		}

		const tagName = `${target.tagName || ""}`.toLowerCase();
		return ["input", "textarea", "select", "option", "button"].includes(tagName);
	}

	getKeyboardNudgeDirection(key = "") {
		switch (key) {
			case "ArrowLeft":
				return { deltaX: -1, deltaY: 0 };
			case "ArrowRight":
				return { deltaX: 1, deltaY: 0 };
			case "ArrowUp":
				return { deltaX: 0, deltaY: -1 };
			case "ArrowDown":
				return { deltaX: 0, deltaY: 1 };
			default:
				return null;
		}
	}

	stopKeyboardNudge(shouldFinalizeHistory = true) {
		if (!this.keyboardNudgeState) {
			return;
		}

		if (this.keyboardNudgeState.intervalId) {
			window.clearInterval(this.keyboardNudgeState.intervalId);
		}

		this.keyboardNudgeState = null;
		if (shouldFinalizeHistory) {
			this.finalizeDocumentHistoryCapture();
		}
	}

	runKeyboardNudge(deltaX = 0, deltaY = 0, amount = 1) {
		if (this.state.canvasMode !== "edit" || !this.getSelectedItemRect()) {
			this.stopKeyboardNudge(true);
			return;
		}

		this.handleNudgeSelectedItem(deltaX * amount, deltaY * amount, {
			commitHistory: false
		});
	}

	startKeyboardNudge(key, deltaX = 0, deltaY = 0) {
		this.stopKeyboardNudge(false);
		this.beginDocumentHistoryCapture();
		this.runKeyboardNudge(deltaX, deltaY, 1);

		const nudgeState = {
			key,
			deltaX,
			deltaY,
			count: 0,
			intervalId: null
		};

		nudgeState.intervalId = window.setInterval(() => {
			nudgeState.count += 1;
			this.runKeyboardNudge(deltaX, deltaY, 1);

			if (nudgeState.count > 5) {
				window.clearInterval(nudgeState.intervalId);
				nudgeState.intervalId = window.setInterval(() => {
					nudgeState.count += 1;
					this.runKeyboardNudge(deltaX, deltaY, nudgeState.count > 50 ? 20 : 5);
				}, 50);
			}
		}, 200);

		this.keyboardNudgeState = nudgeState;
	}

	isUndoShortcut(event) {
		if (!event) {
			return false;
		}

		const hasModifier = event.metaKey || event.ctrlKey;
		return hasModifier && !event.altKey && `${event.key || ""}`.toLowerCase() === "z";
	}

	isRedoShortcut(event) {
		if (!event) {
			return false;
		}

		const key = `${event.key || ""}`.toLowerCase();
		if (event.metaKey || event.ctrlKey) {
			return !event.altKey && (key === "y" || (key === "z" && event.shiftKey));
		}

		return false;
	}

	handleUndo() {
		this.stopKeyboardNudge(false);
		this.flushPendingDocumentHistory(() => {
			this.setState((prevState) => {
				if (!prevState.historyPast.length) {
					return null;
				}

				const nextSnapshot = prevState.historyPast[prevState.historyPast.length - 1];
				const currentSnapshot = this.getDocumentSnapshot(prevState);
				const nextHistoryFuture = [currentSnapshot, ...prevState.historyFuture];
				if (nextHistoryFuture.length > DOCUMENT_HISTORY_LIMIT) {
					nextHistoryFuture.pop();
				}
				const nextSelectionState = this.getSelectionStateForDocument(nextSnapshot);

				return Object.assign({}, nextSelectionState, {
					imageHotspots: cloneArray(nextSnapshot.imageHotspots),
					imageKenBurnsEffect: cloneArray(nextSnapshot.imageKenBurnsEffect),
					imageKenBurnsEffectVariants: normalizeCameraVariantOverrides(nextSnapshot.imageKenBurnsEffectVariants),
					historyPast: prevState.historyPast.slice(0, -1),
					historyFuture: nextHistoryFuture
				});
			});
		});
	}

	handleRedo() {
		this.stopKeyboardNudge(false);
		this.flushPendingDocumentHistory(() => {
			this.setState((prevState) => {
				if (!prevState.historyFuture.length) {
					return null;
				}

				const nextSnapshot = prevState.historyFuture[0];
				const currentSnapshot = this.getDocumentSnapshot(prevState);
				const nextHistoryPast = [...prevState.historyPast, currentSnapshot];
				if (nextHistoryPast.length > DOCUMENT_HISTORY_LIMIT) {
					nextHistoryPast.shift();
				}
				const nextSelectionState = this.getSelectionStateForDocument(nextSnapshot);

				return Object.assign({}, nextSelectionState, {
					imageHotspots: cloneArray(nextSnapshot.imageHotspots),
					imageKenBurnsEffect: cloneArray(nextSnapshot.imageKenBurnsEffect),
					imageKenBurnsEffectVariants: normalizeCameraVariantOverrides(nextSnapshot.imageKenBurnsEffectVariants),
					historyPast: nextHistoryPast,
					historyFuture: prevState.historyFuture.slice(1)
				});
			});
		});
	}

	handleWindowKeyDown(event) {
		if (this.isKeyboardInputTarget(event && event.target)) {
			return;
		}

		if (this.isRedoShortcut(event)) {
			event.preventDefault();
			this.handleRedo();
			return;
		}

		if (this.isUndoShortcut(event)) {
			event.preventDefault();
			this.handleUndo();
			return;
		}

		const direction = this.getKeyboardNudgeDirection(event && event.key ? event.key : "");
		if (!direction) {
			return;
		}

		if (this.state.canvasMode !== "edit" || !this.getSelectedItemRect()) {
			return;
		}

		event.preventDefault();

		if (this.keyboardNudgeState && this.keyboardNudgeState.key === event.key) {
			return;
		}

		this.startKeyboardNudge(event.key, direction.deltaX, direction.deltaY);
	}

	handleWindowKeyUp(event) {
		if (!this.keyboardNudgeState || !event || event.key !== this.keyboardNudgeState.key) {
			return;
		}

		this.stopKeyboardNudge();
	}

	handleWindowBlur() {
		this.stopKeyboardNudge(true);
	}

	async handleSave(callback) {
		await new Promise((resolve) => {
			this.flushPendingDocumentHistory(resolve);
		});

		setImageHotspots(this.state.imageHotspots);
		setImageKenBurnsEffect(this.state.imageKenBurnsEffect);
		setImageKenBurnsEffectVariants(this.state.imageKenBurnsEffectVariants);

		if (this.props.onSaveActivity) {
			await this.props.onSaveActivity();
		}

		if (callback) {
			callback();
		}
	}

	async handleSaveAndClose() {
		await this.handleSave(() => {
			if (this.props.onClose) {
				this.props.onClose();
			}
		});
	}

	getCanvasControlScale() {
		const editScale = Number(this.state.editScale) || 100;
		return clampValue(100 / editScale, 0.9, 3);
	}

	getCanvasControlMetrics() {
		const scale = this.getCanvasControlScale();
		return {
			scale,
			moveHandleSize: Math.round(14 * scale),
			moveHandleDotRadius: Math.max(2, Math.round(2.25 * scale)),
			labelHeight: Math.round(24 * scale),
			labelFontSize: Math.round(13 * scale),
			labelMinWidth: Math.round(66 * scale),
			labelPaddingLeft: Math.round(10 * scale),
			labelPaddingRight: Math.round(12 * scale),
			handleGap: Math.round(6 * scale),
			labelOffsetX: Math.round(8 * scale),
			labelOffsetY: Math.round(32 * scale),
			labelTextY: Math.round(15 * scale),
			rotateHandleOffset: Math.round(42 * scale),
			rotateHandleRadius: Math.round(18 * scale),
			resizeHandleSize: Math.round(14 * scale),
			deleteHandleRadius: Math.round(16 * scale),
			deleteHandleOffset: Math.round(18 * scale),
			actionGlyphSize: Math.round(18 * scale),
			rotateGlyphSize: Math.round(24 * scale),
			strokeWidth: Math.max(2, Math.round(2 * scale)),
			dashSize: Math.max(4, Math.round(4 * scale)),
			labelCornerRadius: Math.round(11 * scale)
		};
	}

	renderRotateHandleGlyph(x, y, controlMetrics) {
		return (
			<text
				x={x}
				y={y - Math.max(1, Math.round(controlMetrics.scale))}
				fill="#2563eb"
				fontSize={controlMetrics.rotateGlyphSize}
				fontWeight="bold"
				fontFamily="Arial, Helvetica, sans-serif"
				textAnchor="middle"
				dominantBaseline="middle"
				pointerEvents="none"
			>
				⟳
			</text>
		);
	}

	renderCameraOpeningViewMarker(item, controlMetrics) {
		const openingRect = _.get(item, "camera.start", null);
		if (!openingRect || !Object.prototype.hasOwnProperty.call(openingRect, "width") || !Object.prototype.hasOwnProperty.call(openingRect, "height")) {
			return null;
		}

		const x = Number(openingRect.x) || 0;
		const y = Number(openingRect.y) || 0;
		const width = Number(openingRect.width) || 0;
		const height = Number(openingRect.height) || 0;
		if (width <= 0 || height <= 0) {
			return null;
		}

		const rotation = normalizeRotation(openingRect.rotation || 0);
		const centerX = x + (width / 2);
		const centerY = y + (height / 2);
		const labelWidth = Math.max(88, Math.round(86 * controlMetrics.scale));
		const labelHeight = Math.max(22, Math.round(22 * controlMetrics.scale));
		const labelX = x;
		const labelY = y - labelHeight - Math.max(6, Math.round(6 * controlMetrics.scale));

		return (
			<g pointerEvents="none" opacity="0.92">
				<g transform={rotation ? `rotate(${rotation} ${centerX} ${centerY})` : undefined}>
					<rect
						x={x}
						y={y}
						width={width}
						height={height}
						rx={CAMERA_STYLE.borderRadius}
						fill="rgba(249, 115, 22, 0.08)"
						stroke="#f97316"
						strokeWidth={Math.max(2, controlMetrics.strokeWidth)}
						strokeDasharray="18,10"
						vectorEffect="non-scaling-stroke"
					/>
				</g>
				<rect
					x={labelX}
					y={labelY}
					width={labelWidth}
					height={labelHeight}
					rx={Math.round(labelHeight / 2)}
					fill="#f97316"
					stroke="rgba(255,255,255,0.92)"
					strokeWidth={Math.max(1, controlMetrics.strokeWidth - 1)}
				/>
				<text
					x={labelX + Math.round(10 * controlMetrics.scale)}
					y={labelY + Math.round(labelHeight * 0.66)}
					fill="#ffffff"
					fontSize={Math.max(11, Math.round(12 * controlMetrics.scale))}
					fontWeight="bold"
				>
					opening
				</text>
			</g>
		);
	}

	renderSelectionHandles(kind, itemRect, rotation, itemGuid) {
		if (!itemRect || this.state.canvasMode !== "edit") {
			return null;
		}

		const controlMetrics = this.getCanvasControlMetrics();
		const centerX = itemRect.x + (itemRect.width / 2);
		const centerY = itemRect.y + (itemRect.height / 2);
		const topCenter = rotatePointAroundCenter(itemRect.x + (itemRect.width / 2), itemRect.y, centerX, centerY, rotation);
		const rotateHandlePoint = rotatePointAroundCenter(itemRect.x + (itemRect.width / 2), itemRect.y - controlMetrics.rotateHandleOffset, centerX, centerY, rotation);
		const topRight = rotatePointAroundCenter(itemRect.x + itemRect.width, itemRect.y, centerX, centerY, rotation);
		const resizeHandles = [
			{ id: "nw", point: rotatePointAroundCenter(itemRect.x, itemRect.y, centerX, centerY, rotation) },
			{ id: "ne", point: rotatePointAroundCenter(itemRect.x + itemRect.width, itemRect.y, centerX, centerY, rotation) },
			{ id: "se", point: rotatePointAroundCenter(itemRect.x + itemRect.width, itemRect.y + itemRect.height, centerX, centerY, rotation) },
			{ id: "sw", point: rotatePointAroundCenter(itemRect.x, itemRect.y + itemRect.height, centerX, centerY, rotation) }
		];

		return (
			<>
				<line
					x1={topCenter.x}
					y1={topCenter.y}
						x2={rotateHandlePoint.x}
						y2={rotateHandlePoint.y}
						stroke="#2563eb"
						strokeWidth={controlMetrics.strokeWidth}
						strokeDasharray={`${controlMetrics.dashSize},${controlMetrics.dashSize}`}
						pointerEvents="none"
					/>
					<circle
						cx={rotateHandlePoint.x}
						cy={rotateHandlePoint.y}
						r={controlMetrics.rotateHandleRadius}
						fill="#ffffff"
						stroke="#2563eb"
						strokeWidth={controlMetrics.strokeWidth}
						style={{ cursor: "grab" }}
						onMouseDown={(event) => {
							const sourceItems = this.getItemsForKind(kind);
							const selectedItem = sourceItems.find((entry) => entry.guid === itemGuid);
							if (selectedItem) {
								this.startRotateDrag(kind, selectedItem, event);
						}
					}}
				/>
				{this.renderRotateHandleGlyph(rotateHandlePoint.x, rotateHandlePoint.y, controlMetrics)}
					{resizeHandles.map((handle) => (
						<rect
							key={`${itemGuid}-${handle.id}`}
							x={handle.point.x - (controlMetrics.resizeHandleSize / 2)}
							y={handle.point.y - (controlMetrics.resizeHandleSize / 2)}
							width={controlMetrics.resizeHandleSize}
							height={controlMetrics.resizeHandleSize}
							fill="#ffffff"
							stroke="#2563eb"
							strokeWidth={controlMetrics.strokeWidth}
							rx={2}
							style={{ cursor: "nwse-resize" }}
							onMouseDown={(event) => {
								const sourceItems = this.getItemsForKind(kind);
								const selectedItem = sourceItems.find((entry) => entry.guid === itemGuid);
								if (selectedItem) {
								this.startResizeDrag(kind, selectedItem, handle.id, event);
							}
						}}
					/>
				))}
					<circle
						cx={topRight.x + controlMetrics.deleteHandleOffset}
						cy={topRight.y - controlMetrics.deleteHandleOffset}
						r={controlMetrics.deleteHandleRadius}
						fill="#dc2626"
						stroke="#ffffff"
						strokeWidth={Math.max(3, controlMetrics.strokeWidth)}
						style={{ cursor: "pointer" }}
						onMouseDown={(event) => {
							event.preventDefault();
							event.stopPropagation();
						}}
						onClick={(event) => {
							event.preventDefault();
							event.stopPropagation();
							this.handleDeleteSelectedItem();
						}}
					/>
					<text
						x={topRight.x + controlMetrics.deleteHandleOffset}
						y={topRight.y - controlMetrics.deleteHandleOffset}
						fill="#ffffff"
						fontSize={controlMetrics.actionGlyphSize}
						fontWeight="bold"
						textAnchor="middle"
						dominantBaseline="central"
						pointerEvents="none"
				>
					×
				</text>
			</>
		);
	}

	renderEditorItem(kind, item, isDraft = false) {
		if (!item) {
			return null;
		}

		if (!isDraft && !this.isCanvasItemVisible(item)) {
			return null;
		}

		const itemRect = kind === "camera" ? _.get(item, "camera.end", null) : item.coords;
		if (!itemRect) {
			return null;
		}

		const rotation = kind === "camera"
			? normalizeRotation(_.get(item, "camera.end.rotation", 0))
			: normalizeRotation(item.rotation || 0);
		const centerX = itemRect.x + (itemRect.width / 2);
		const centerY = itemRect.y + (itemRect.height / 2);
		const isSelected = !isDraft && this.state.selectedItemKind === kind && this.state.selectedItemGuid === item.guid;
		const colorPickActive = !!this.state.colorPickTarget;
		const strokeColor = kind === "camera"
			? CAMERA_STYLE.borderColor
			: (_.get(item, "style.borderColor", "#2563eb") || "#2563eb");
		const strokeWidth = kind === "camera"
			? CAMERA_STYLE.borderWidth
			: Math.max(Number(_.get(item, "style.borderWidth", 1)) || 0, 1);
		const fill = kind === "camera"
			? "transparent"
			: (_.get(item, "style.borderFillColor", "transparent") || "transparent");
		const borderRadius = kind === "camera"
			? CAMERA_STYLE.borderRadius
			: Number(_.get(item, "style.borderRadius", 0)) || 0;
		const labelText = item.id || (kind === "camera" ? "camera" : "markup");
			const cameraAspectGuidance = kind === "camera" ? this.getCameraAspectGuidanceForRect(itemRect) : null;
			const showCameraWarningBadge = !isDraft && cameraAspectGuidance && cameraAspectGuidance.severity === "warning";
			const controlMetrics = this.getCanvasControlMetrics();
			const viewBox = this.getCanvasViewBox();
			const handleAnchor = rotatePointAroundCenter(itemRect.x, itemRect.y, centerX, centerY, rotation);
			const moveHandleSize = controlMetrics.moveHandleSize;
			const handleHeight = controlMetrics.labelHeight;
			const baseHandleWidth = this.state.showLabels && !isDraft
				? Math.max((labelText.length * (controlMetrics.labelFontSize * 0.72)) + controlMetrics.labelPaddingLeft + controlMetrics.labelPaddingRight, controlMetrics.labelMinWidth)
				: moveHandleSize;
			const warningBadgeWidth = showCameraWarningBadge ? Math.round(44 * controlMetrics.scale) : 0;
			const handleWidth = this.state.showLabels && !isDraft
				? baseHandleWidth + warningBadgeWidth
				: moveHandleSize;
			const handleX = clampValue(
				handleAnchor.x + controlMetrics.labelOffsetX,
				viewBox.x + 6,
				(viewBox.x + viewBox.width) - (moveHandleSize + (this.state.showLabels && !isDraft ? controlMetrics.handleGap + handleWidth : 0)) - 6
			);
			const handleY = clampValue(
				handleAnchor.y - controlMetrics.labelOffsetY,
				viewBox.y + 6,
				(viewBox.y + viewBox.height) - Math.max(moveHandleSize, handleHeight) - 6
			);
			const labelX = handleX + moveHandleSize + controlMetrics.handleGap;
			const handleFill = isSelected
				? (kind === "camera" ? "#0f766e" : (strokeColor || "#2563eb"))
				: "rgba(15,23,42,0.92)";
			const handleDotFill = kind === "camera" ? "#7dd3fc" : "#7CFC00";
		const isRenaming = !isDraft
			&& this.state.renamingSurface === "canvas"
			&& this.state.renamingItemKind === kind
			&& this.state.renamingItemGuid === item.guid;
		const showOpeningViewMarker = kind === "camera"
			&& isSelected
			&& this.isFirstCameraGuid(item.guid)
			&& _.get(item, "camera.startZoomed", false) === true
			&& _.get(item, "camera.start", null);

		return (
			<g key={item.guid || `draft-${kind}`}>
				{showOpeningViewMarker && this.renderCameraOpeningViewMarker(item, controlMetrics)}
				<g transform={rotation ? `rotate(${rotation} ${centerX} ${centerY})` : undefined} pointerEvents="none">
					{kind === "camera"
						? (
							<rect
								x={itemRect.x}
								y={itemRect.y}
								width={itemRect.width}
								height={itemRect.height}
								rx={borderRadius}
								stroke={strokeColor}
								strokeWidth={strokeWidth}
								fill={fill}
								strokeDasharray="10,6"
								opacity={isDraft ? 0.85 : 1}
							/>
						)
						: renderOverlaySvgElement(item.shape || "rectangle", itemRect, {
							borderRadius,
							stroke: strokeColor,
							strokeWidth,
							fill,
							opacity: isDraft ? 0.85 : 1
						})}
					</g>
					{!isDraft && this.state.canvasMode === "edit" && !colorPickActive && (
						<>
								{isRenaming
									? (
										<foreignObject
											x={labelX}
											y={handleY}
											width={Math.max(handleWidth, Math.round(150 * controlMetrics.scale))}
											height={handleHeight}
										onMouseDown={(event) => {
											event.stopPropagation();
										}}
										onClick={(event) => {
											event.stopPropagation();
										}}
									>
										<div xmlns="http://www.w3.org/1999/xhtml" style={{ width: "100%", height: "100%", display: "flex", alignItems: "center" }}>
											<input
												ref={this.inlineRenameInputRef}
												autoFocus
												type="text"
												value={this.state.renamingItemValue}
												onChange={this.handleInlineRenameChange}
												onBlur={this.commitInlineRename}
												onKeyDown={this.handleInlineRenameKeyDown}
												onMouseDown={(event) => {
													event.stopPropagation();
												}}
												onClick={(event) => {
													event.stopPropagation();
												}}
												style={{
													width: "100%",
													height: "100%",
													borderRadius: `${controlMetrics.labelCornerRadius}px`,
													border: "2px solid #2563eb",
													background: "#ffffff",
													padding: `0 ${Math.round(10 * controlMetrics.scale)}px`,
													fontSize: `${controlMetrics.labelFontSize}px`,
													fontWeight: 700,
													color: "#111827",
													boxSizing: "border-box"
												}}
											/>
										</div>
									</foreignObject>
									)
									: (
										<>
											<g
												onMouseDown={(event) => {
													event.preventDefault();
													event.stopPropagation();
													this.selectItem(kind, item.guid);
													this.startMoveDrag(kind, item, event);
												}}
												onClick={(event) => {
													event.preventDefault();
													event.stopPropagation();
													this.selectItem(kind, item.guid);
												}}
												style={{ cursor: "move" }}
											>
												<rect
													x={handleX}
													y={handleY}
													width={moveHandleSize}
													height={moveHandleSize}
													rx={Math.round(moveHandleSize / 2)}
													fill={handleFill}
													stroke="rgba(255,255,255,0.92)"
													strokeWidth={isSelected ? controlMetrics.strokeWidth : Math.max(1.5, controlMetrics.strokeWidth - 0.5)}
												/>
												<circle
													cx={handleX + (moveHandleSize / 2)}
													cy={handleY + (moveHandleSize / 2)}
													r={controlMetrics.moveHandleDotRadius}
													fill={handleDotFill}
												/>
											</g>
											{this.state.showLabels && (
												<g
													onMouseDown={(event) => {
														event.preventDefault();
														event.stopPropagation();
														this.selectItem(kind, item.guid);
														this.startMoveDrag(kind, item, event);
													}}
													onClick={(event) => {
														event.preventDefault();
														event.stopPropagation();
														if (this.consumePendingInlineRenameActivation()) {
															return;
														}
														this.selectItem(kind, item.guid);
													}}
													onDoubleClick={(event) => {
														event.preventDefault();
														event.stopPropagation();
														this.startInlineRename(kind, item.guid, "canvas");
													}}
													style={{ cursor: "move" }}
												>
													<rect
														x={labelX}
														y={handleY}
														width={handleWidth}
														height={handleHeight}
														rx={controlMetrics.labelCornerRadius}
														fill={handleFill}
														stroke="rgba(255,255,255,0.92)"
														strokeWidth={isSelected ? controlMetrics.strokeWidth : Math.max(1.5, controlMetrics.strokeWidth - 0.5)}
													/>
													<text
														x={labelX + controlMetrics.labelPaddingLeft}
														y={handleY + controlMetrics.labelTextY}
														fill="#ffffff"
														fontSize={controlMetrics.labelFontSize}
														fontWeight="bold"
													>
														{labelText}
													</text>
													{showCameraWarningBadge && (
														<>
															<rect
																x={labelX + handleWidth - warningBadgeWidth + Math.round(4 * controlMetrics.scale)}
																y={handleY + Math.round(4 * controlMetrics.scale)}
																width={warningBadgeWidth - Math.round(8 * controlMetrics.scale)}
																height={handleHeight - Math.round(8 * controlMetrics.scale)}
																rx={Math.round(8 * controlMetrics.scale)}
																fill="#f59e0b"
																opacity="0.96"
															/>
															<text
																x={labelX + handleWidth - (warningBadgeWidth / 2)}
																y={handleY + controlMetrics.labelTextY}
																fill="#ffffff"
																fontSize={Math.max(10, Math.round(11 * controlMetrics.scale))}
																fontWeight="bold"
																textAnchor="middle"
															>
																Warn
															</text>
														</>
													)}
												</g>
											)}
										</>
									)}
						</>
					)}
					{isSelected && !colorPickActive && this.renderSelectionHandles(kind, itemRect, rotation, item.guid)}
				</g>
			);
		}

	renderDeviceGuides() {
		if (this.state.canvasMode !== "edit" || !this.state.showDeviceGuides) {
			return null;
		}

		const baseRect = this.getDeviceGuideReferenceRect();
		const activeGuides = this.getActiveDeviceGuides();
		if (!baseRect || activeGuides.length === 0) {
			return null;
		}

		return activeGuides.map((guide, index) => {
			const guideRect = this.getDeviceGuideRect(baseRect, guide);
			if (!guideRect) {
				return null;
			}

			const { labelX, labelY, labelText, labelWidth, labelHeight } = getDeviceGuideLabelMetrics(guideRect, guide, index);
			const showCoverCropTint = guideRect.fitMode === "cover"
				&& (guideRect.width < baseRect.width || guideRect.height < baseRect.height);

			return (
				<g key={`device-guide-${guide.id}`} pointerEvents="none">
					{showCoverCropTint && (
						<>
							{guideRect.y > baseRect.y && (
								<rect
									x={baseRect.x}
									y={baseRect.y}
									width={baseRect.width}
									height={guideRect.y - baseRect.y}
									fill={guide.color}
									opacity="0.06"
								/>
							)}
							{guideRect.x > baseRect.x && (
								<rect
									x={baseRect.x}
									y={guideRect.y}
									width={guideRect.x - baseRect.x}
									height={guideRect.height}
									fill={guide.color}
									opacity="0.06"
								/>
							)}
							{guideRect.x + guideRect.width < baseRect.x + baseRect.width && (
								<rect
									x={guideRect.x + guideRect.width}
									y={guideRect.y}
									width={(baseRect.x + baseRect.width) - (guideRect.x + guideRect.width)}
									height={guideRect.height}
									fill={guide.color}
									opacity="0.06"
								/>
							)}
							{guideRect.y + guideRect.height < baseRect.y + baseRect.height && (
								<rect
									x={baseRect.x}
									y={guideRect.y + guideRect.height}
									width={baseRect.width}
									height={(baseRect.y + baseRect.height) - (guideRect.y + guideRect.height)}
									fill={guide.color}
									opacity="0.06"
								/>
							)}
						</>
					)}
					<rect
						x={guideRect.x}
						y={guideRect.y}
						width={guideRect.width}
						height={guideRect.height}
						rx="12"
						fill="none"
						stroke={guide.color}
						strokeWidth="3"
						strokeDasharray="12,8"
						opacity="0.92"
					/>
					<rect
						x={labelX}
						y={labelY}
						width={labelWidth}
						height={labelHeight}
						rx="8"
						fill={guide.color}
						opacity="0.92"
					/>
					<text
						x={labelX + 8}
						y={labelY + 11}
						fill="#ffffff"
						fontSize="10"
						fontWeight="bold"
					>
						{labelText}
					</text>
				</g>
			);
		});
	}

	renderDraftItem() {
		if (!this.state.drawing) {
			return null;
		}

		const x = Math.min(this.state.drawStartX, this.state.drawCurrentX);
		const y = Math.min(this.state.drawStartY, this.state.drawCurrentY);
		const width = Math.abs(this.state.drawCurrentX - this.state.drawStartX);
		const height = Math.abs(this.state.drawCurrentY - this.state.drawStartY);
		if (width < 2 || height < 2) {
			return null;
		}

		if (this.state.componentType === "camera") {
			return this.renderEditorItem("camera", {
				guid: "draft-camera",
				id: "camera",
				camera: {
					end: {
						x,
						y,
						width,
						height,
						rotation: 0
					}
				}
			}, true);
		}

		return this.renderEditorItem("overlay", this.buildOverlayItem({ x, y, width, height }, this.state.componentType, this.state.shape), true);
	}

	renderPreviewOverlayShape(hotspot) {
		const rotationTransform = hotspot.rotation
			? `rotate(${hotspot.rotation} ${hotspot.centerX} ${hotspot.centerY})`
			: null;
		const groupProps = {
			key: hotspot.guid || hotspot.id,
			opacity: hotspot.opacity
		};

		if (rotationTransform) {
			groupProps.transform = rotationTransform;
		}

		return (
			<g {...groupProps}>
				{renderOverlaySvgElement(hotspot.shape || "rectangle", hotspot.coords, {
					borderRadius: hotspot.style.borderRadius,
					strokeWidth: hotspot.style.borderWidth,
					stroke: hotspot.style.borderColor,
					fill: hotspot.style.borderFillColor
				})}
			</g>
		);
	}

	renderPreviewSurface(options = {}) {
		if (!this.state.imageSize) {
			return null;
		}

		const {
			width = "100%",
			height = "100%",
			borderRadius = "16px",
			background = "#0f172a",
			boxShadow = null,
			containerStyle = null,
			variantKey = this.state.activeCameraVariant
		} = options;
		const sceneState = this.getPreviewSceneState({ variantKey });
		if (!sceneState) {
			return null;
		}
		const resolvedWidth = typeof width === "number" ? `${width}px` : width;
		const resolvedHeight = typeof height === "number" ? `${height}px` : height;
		const previewContainerStyle = Object.assign({
			position: "relative",
			width: resolvedWidth,
			height: resolvedHeight,
			overflow: "hidden",
			background,
			borderRadius
		}, boxShadow ? { boxShadow } : null, containerStyle || {});

		if (sceneState.sceneMode === "image-ken-burns") {
			return (
				<div style={previewContainerStyle}>
					<img
						alt=""
						src={this.props.url}
						style={{
							width: "100%",
							height: "100%",
							objectFit: "contain",
							transform: `scale(${sceneState.simpleKenBurnsState.scaleX}, ${sceneState.simpleKenBurnsState.scaleY})`,
							transformOrigin: "center center"
						}}
					/>
				</div>
			);
		}

		return (
			<div style={previewContainerStyle}>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox={`${sceneState.cameraState.x} ${sceneState.cameraState.y} ${sceneState.cameraState.width} ${sceneState.cameraState.height}`}
					width="100%"
					height="100%"
					style={{
						display: "block",
						transform: sceneState.cameraState.rotation ? `rotate(${sceneState.cameraState.rotation}deg)` : "none",
						transformOrigin: "center center"
					}}
				>
					<image
						href={this.props.url}
						width={this.state.imageSize.width}
						height={this.state.imageSize.height}
					/>
					{sceneState.hotspots.map((hotspot) => this.renderPreviewOverlayShape(hotspot))}
				</svg>
			</div>
		);
	}

	renderPreviewCanvas(previewHeight) {
		const previewCameraVariantKey = this.getAutoPreviewCameraVariantKey();
		return this.renderPreviewSurface({
			width: "100%",
			height: previewHeight,
			borderRadius: "16px",
			variantKey: previewCameraVariantKey
		});
	}

	getPreviewViewportSpec(stageWidth = 0, stageHeight = 0) {
		const preset = getPreviewDevicePreset(this.state.previewDevicePreset);
		const orientation = normalizePreviewOrientation(this.state.previewDeviceOrientation);
		if (!this.state.imageSize || preset.kind === "responsive") {
			return {
				preset,
				orientation,
				isResponsive: true,
				frameWidth: Math.max(0, stageWidth),
				frameHeight: Math.max(0, stageHeight),
				borderRadius: 14,
				label: "Responsive",
				dimensionsLabel: this.state.imageSize
					? `${this.state.imageSize.width} x ${this.state.imageSize.height}`
					: "Auto"
			};
		}

		const viewportWidth = orientation === "landscape" ? preset.height : preset.width;
		const viewportHeight = orientation === "landscape" ? preset.width : preset.height;
		const frameScale = Math.min(
			stageWidth > 0 ? stageWidth / viewportWidth : 1,
			stageHeight > 0 ? stageHeight / viewportHeight : 1,
			1
		);
		const frameWidth = Math.max(1, Math.round(viewportWidth * frameScale));
		const frameHeight = Math.max(1, Math.round(viewportHeight * frameScale));

		return {
			preset,
			orientation,
			isResponsive: false,
			frameWidth,
			frameHeight,
			borderRadius: preset.kind === "desktop" ? 16 : 28,
			label: `${preset.label}${orientation === "landscape" ? " Landscape" : ""}`,
			dimensionsLabel: `${viewportWidth} x ${viewportHeight}`
		};
	}

	renderPreviewDeviceOptions() {
		const deviceGroups = [
			{
				label: "Responsive",
				items: PREVIEW_DEVICE_PRESETS.filter((preset) => preset.kind === "responsive")
			},
			{
				label: "Desktop",
				items: PREVIEW_DEVICE_PRESETS.filter((preset) => preset.kind === "desktop")
			},
			{
				label: "Phone",
				items: PREVIEW_DEVICE_PRESETS.filter((preset) => preset.kind === "phone")
			},
			{
				label: "Tablet",
				items: PREVIEW_DEVICE_PRESETS.filter((preset) => preset.kind === "tablet")
			}
		];

		return deviceGroups.map((group) => (
			<optgroup key={`media-markup-v2-preview-device-${group.label}`} label={group.label}>
				{group.items.map((preset) => (
					<option key={preset.value} value={preset.value}>
						{preset.kind === "responsive" ? preset.label : `${preset.label} (${preset.width} x ${preset.height})`}
					</option>
				))}
			</optgroup>
		));
	}

	renderPreviewCanvasControls() {
		const previewViewportSpec = this.getPreviewViewportSpec();
		const previewCameraVariantKey = this.getAutoPreviewCameraVariantKey();
		const previewCameraVariantLabel = this.getCameraVariantSummaryLabel(previewCameraVariantKey);

		return (
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "flex-end",
					gap: "12px",
					flexWrap: "wrap",
					padding: "10px 12px",
					border: "1px solid #e5e7eb",
					borderRadius: "12px",
					background: "#f8fafc"
				}}
			>
				<div style={{ display: "flex", alignItems: "flex-end", gap: "12px", flexWrap: "wrap" }}>
					<div style={{ height: 50, display: "flex", flexDirection: "column", alignItems: "center" }}>
						<div style={{ fontSize: "x-small" }}>Preview Device</div>
						<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
							<select
								style={{ borderRadius: "5px", border: "1px solid #cccccc", marginBottom: 0, minWidth: "220px" }}
								value={this.state.previewDevicePreset}
								onChange={this.handlePreviewDevicePresetChange}
							>
								{this.renderPreviewDeviceOptions()}
							</select>
							{this.state.previewDevicePreset !== "responsive" && (
								<Button bsSize="small" onClick={this.handlePreviewDeviceOrientationToggle} title="Toggle preview orientation">
									{this.state.previewDeviceOrientation === "landscape" ? "Portrait" : "Landscape"}
								</Button>
							)}
						</div>
					</div>
					<div style={{ height: 50, display: "flex", flexDirection: "column", alignItems: "center" }}>
						<div style={{ fontSize: "x-small" }}>Zoom</div>
						<select style={{ borderRadius: "5px", border: "1px solid #cccccc", marginBottom: 0 }} value={this.state.previewScale} onChange={this.handlePreviewScaleChange}>
							{ZOOM_OPTIONS.map((option) => <option key={`media-markup-v2-preview-zoom-${option}`} value={option}>{option}%</option>)}
						</select>
					</div>
				</div>
				<div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", justifyContent: "flex-end" }}>
					<div style={{ fontSize: "12px", color: "#334155", background: "#eef2ff", borderRadius: "8px", padding: "8px 10px", fontWeight: 600 }}>
						{`${previewViewportSpec.label} • ${previewViewportSpec.dimensionsLabel}`}
					</div>
					<div
						style={{
							fontSize: "12px",
							color: previewCameraVariantKey === CAMERA_VARIANT_BASE ? "#334155" : "#0f766e",
							background: previewCameraVariantKey === CAMERA_VARIANT_BASE ? "#e2e8f0" : "#dcfce7",
							borderRadius: "8px",
							padding: "8px 10px",
							fontWeight: 600
						}}
						title="Preview automatically uses the closest camera variant for the selected device/aspect ratio."
					>
						{`Auto Variant • ${previewCameraVariantLabel}`}
					</div>
				</div>
			</div>
		);
	}

	renderCanvasPreview(canvasDisplayWidth, canvasDisplayHeight, canvasScaleRatio) {
		if (!this.state.imageSize) {
			return null;
		}

		const viewBox = this.getCanvasViewBox();
		const stageLeft = (0 - viewBox.x) * canvasScaleRatio;
		const stageTop = (0 - viewBox.y) * canvasScaleRatio;
		const stageWidth = this.state.imageSize.width * canvasScaleRatio;
		const stageHeight = this.state.imageSize.height * canvasScaleRatio;
		const previewViewportSpec = this.getPreviewViewportSpec(stageWidth, stageHeight);
		const previewCameraVariantKey = this.getAutoPreviewCameraVariantKey(
			previewViewportSpec.isResponsive ? stageWidth : previewViewportSpec.frameWidth,
			previewViewportSpec.isResponsive ? stageHeight : previewViewportSpec.frameHeight
		);
		const frameWidth = previewViewportSpec.isResponsive ? stageWidth : previewViewportSpec.frameWidth;
		const frameHeight = previewViewportSpec.isResponsive ? stageHeight : previewViewportSpec.frameHeight;
		const frameLeft = stageLeft + Math.max(0, (stageWidth - frameWidth) / 2);
		const frameTop = stageTop + Math.max(0, (stageHeight - frameHeight) / 2);
		const deviceFrameStyle = previewViewportSpec.isResponsive
			? {
				borderRadius: "14px",
				boxShadow: "0 16px 40px rgba(15, 23, 42, 0.28), 0 0 0 1px rgba(148, 163, 184, 0.55)",
				overflow: "hidden"
			}
			: {
				borderRadius: `${previewViewportSpec.borderRadius}px`,
				background: previewViewportSpec.preset.kind === "desktop" ? "#111827" : "#0f172a",
				boxShadow: previewViewportSpec.preset.kind === "desktop"
					? "0 18px 44px rgba(15, 23, 42, 0.34), 0 0 0 1px rgba(51,65,85,0.45)"
					: "0 22px 48px rgba(15, 23, 42, 0.34), 0 0 0 2px rgba(15,23,42,0.18)",
				padding: previewViewportSpec.preset.kind === "desktop" ? "10px" : "12px",
				boxSizing: "border-box",
				overflow: "hidden"
			};
		const previewSurfaceBorderRadius = previewViewportSpec.isResponsive
			? "14px"
			: `${Math.max(18, previewViewportSpec.borderRadius - 10)}px`;

		return (
			<div
				style={{
					position: "relative",
					width: `${canvasDisplayWidth}px`,
					height: `${canvasDisplayHeight}px`,
					minWidth: `${canvasDisplayWidth}px`,
					minHeight: `${canvasDisplayHeight}px`,
					...AUTHORING_CANVAS_BACKGROUND
				}}
			>
				<div
					style={{
						position: "absolute",
						inset: 0,
						background: "rgba(15, 23, 42, 0.10)",
						pointerEvents: "none"
					}}
				/>
				<div
					style={{
						position: "absolute",
						left: `${frameLeft}px`,
						top: `${frameTop}px`,
						width: `${frameWidth}px`,
						height: `${frameHeight}px`,
						...deviceFrameStyle
					}}
				>
					{this.renderPreviewSurface({
						width: "100%",
						height: "100%",
						borderRadius: previewSurfaceBorderRadius,
						containerStyle: {
							border: "1px solid rgba(255, 255, 255, 0.18)"
						},
						variantKey: previewCameraVariantKey
					})}
				</div>
				<div
					style={{
						position: "absolute",
						left: `${frameLeft + 12}px`,
						top: `${Math.max(10, frameTop - 36)}px`,
						background: "rgba(15, 23, 42, 0.86)",
						color: "#f8fafc",
						fontSize: "12px",
						fontWeight: 700,
						letterSpacing: "0.04em",
						textTransform: "uppercase",
						borderRadius: "999px",
						padding: "6px 10px",
						boxShadow: "0 10px 24px rgba(15, 23, 42, 0.24)"
					}}
				>
					{`Preview • ${previewViewportSpec.label}`}
				</div>
				{!previewViewportSpec.isResponsive && (
					<div
						style={{
							position: "absolute",
							left: `${frameLeft + 12}px`,
							top: `${frameTop + frameHeight + 10}px`,
							background: "rgba(255, 255, 255, 0.94)",
							color: "#334155",
							fontSize: "12px",
							fontWeight: 700,
							borderRadius: "999px",
							padding: "5px 10px",
							boxShadow: "0 8px 18px rgba(15, 23, 42, 0.12)"
						}}
					>
						{previewViewportSpec.dimensionsLabel}
					</div>
				)}
			</div>
		);
	}

	renderPreviewPlaybackControls(options = {}) {
		const {
			showSlider = true,
			useBottomScrubberHint = false,
			showViewportInfo = this.state.canvasMode === "preview"
		} = options;
		const previewDurationMs = this.getPreviewDurationMs();
		const currentTimeLabel = `${Math.round(this.state.currentTimeMs || 0)} ms`;
		const previewViewportSpec = this.getPreviewViewportSpec();
		const previewCameraVariantKey = this.getAutoPreviewCameraVariantKey();

		return (
			<div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px" }}>
				<Button bsStyle="primary" onClick={this.handlePreviewPlayPause}>
					<FontAwesomeIcon icon={this.state.previewPlaying ? "pause" : "play"} /> {this.state.previewPlaying ? "Pause" : "Play"}
				</Button>
				<Button onClick={this.handlePreviewSeekToStart}>Start</Button>
				<Button onClick={() => { this.handlePreviewStep(-250); }}>-250 ms</Button>
				<Button onClick={() => { this.handlePreviewStep(250); }}>+250 ms</Button>
				{showSlider && (
					<div style={{ minWidth: "220px", flex: "1 1 220px" }}>
						<input type="range" min="0" max={previewDurationMs} step="10" value={Math.min(this.state.currentTimeMs, previewDurationMs)} onChange={this.handlePreviewSliderChange} onInput={this.handlePreviewSliderChange} style={{ width: "100%" }} />
					</div>
				)}
				<div style={{ fontFamily: "Courier", fontSize: "13px", color: "#0f172a", background: "#dbeafe", borderRadius: "8px", padding: "8px 10px", minWidth: "120px", textAlign: "center" }}>
					{currentTimeLabel}
				</div>
				{showViewportInfo && (
					<div style={{ fontSize: "12px", color: "#334155", background: "#eef2ff", borderRadius: "8px", padding: "8px 10px", fontWeight: 600 }}>
						{`${previewViewportSpec.label} • ${previewViewportSpec.dimensionsLabel}`}
					</div>
				)}
				{showViewportInfo && (
					<div style={{ fontSize: "12px", color: previewCameraVariantKey === CAMERA_VARIANT_BASE ? "#334155" : "#0f766e", background: previewCameraVariantKey === CAMERA_VARIANT_BASE ? "#e2e8f0" : "#dcfce7", borderRadius: "8px", padding: "8px 10px", fontWeight: 600 }}>
						{`${this.getCameraVariantSummaryLabel(previewCameraVariantKey)} Variant`}
					</div>
				)}
				<div style={{ fontSize: "12px", color: this.props.audioUrl ? "#0f766e" : "#6b7280", fontWeight: 600 }}>
					{this.props.audioUrl ? (this.state.audioReady ? "Audio Sync Active" : "Audio Loading") : "No Audio Sync"}
				</div>
				{this.state.canvasMode === "edit" && (
					<label
						style={{
							display: "flex",
							alignItems: "center",
							gap: "6px",
							margin: 0,
							padding: "6px 10px",
							borderRadius: "8px",
							background: "#f8fafc",
							border: "1px solid #cbd5e1",
							fontSize: "12px",
							fontWeight: 500,
							color: "#334155"
						}}
						title="Only show items on the edit canvas that are active at the current scrubber time"
					>
						<input type="checkbox" checked={this.state.showOnlyActiveCanvasItems} onChange={this.handleToggleShowOnlyActiveCanvasItems} />
						<span>Only Show Active At Time</span>
					</label>
				)}
				{this.props.audioUrl && (
					<label
						style={{
							display: "flex",
							alignItems: "center",
							gap: "6px",
							margin: 0,
							padding: "6px 10px",
							borderRadius: "8px",
							background: "#f8fafc",
							border: "1px solid #cbd5e1",
							fontSize: "12px",
							fontWeight: 500,
							color: "#334155"
						}}
						title="Mute preview audio while keeping scrubbing and timing active"
					>
						<input type="checkbox" checked={this.state.audioMuted} onChange={this.handleToggleAudioMuted} />
						<span>Mute Audio</span>
					</label>
				)}
				{useBottomScrubberHint && (
					<div style={{ fontSize: "12px", color: "#64748b", fontWeight: 500 }}>
						Use the bottom scrubber to seek.
					</div>
				)}
			</div>
		);
	}

	renderBottomTimelineSlider() {
		const previewDurationMs = this.getPreviewDurationMs();
		return (
			<div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "4px 2px 0" }}>
				<div style={{ fontSize: "11px", color: "#64748b", fontWeight: 700, minWidth: "58px", textTransform: "uppercase", letterSpacing: "0.04em" }}>
					Timeline
				</div>
				<input
					type="range"
					min="0"
					max={previewDurationMs}
					step="10"
					value={Math.min(this.state.currentTimeMs, previewDurationMs)}
					onChange={this.handlePreviewSliderChange}
					onInput={this.handlePreviewSliderChange}
					style={{ width: "100%", flex: "1 1 auto" }}
				/>
				<div style={{ fontFamily: "Courier", fontSize: "12px", color: "#0f172a", background: "#e2e8f0", borderRadius: "8px", padding: "6px 9px", minWidth: "96px", textAlign: "center" }}>
					{`${Math.round(this.state.currentTimeMs || 0)} ms`}
				</div>
			</div>
		);
	}

	renderSelectedItemPanel() {
		const selectedItem = this.getSelectedItem();
		const selectedRect = this.getSelectedItemRect();
		const selectedRotation = this.getSelectedItemRotation();

		if (!selectedItem || !selectedRect) {
			return (
				<div style={{ color: "#6b7280", fontSize: "13px", lineHeight: 1.5 }}>
					Select a hotspot, highlight, or camera to inspect timing and adjust it.
				</div>
			);
		}

		const isCamera = this.state.selectedItemKind === "camera";
		const selectedItemHidden = selectedItem.canvasVisible === false;
		const selectedSourceItems = this.getSourceItemsForKind(this.state.selectedItemKind);
		const selectedItemIndex = this.getItemIndexByGuid(this.state.selectedItemKind, selectedItem.guid);
		const selectedItemLayerNumber = selectedItemIndex >= 0 ? selectedSourceItems.length - selectedItemIndex : 0;
		const selectedItemCanMoveForward = selectedItemIndex >= 0 && selectedItemIndex < (selectedSourceItems.length - 1);
		const selectedItemCanMoveBackward = selectedItemIndex > 0;
		const labelStyle = { color: "#6b7280", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 };
		const inputStyle = { width: "100%" };
		const colorPopoverContainer = typeof document !== "undefined" ? document.body : undefined;
		const colorPopoverStyle = { zIndex: 210000 };
		const colorPickActive = !!this.state.colorPickTarget;
		const colorPickTargetLabel = this.getColorPickTargetLabel();
		const colorPickButtonStyle = { height: "32px", padding: "4px 8px" };
		const currentCursorLabel = `${Math.round(this.state.currentCursorMs || 0)} ms`;
		const currentRegionLabel = this.state.currentRegion
			? `${Math.round((this.state.currentRegion.start || 0) * 1000)} ms - ${Math.round((this.state.currentRegion.end || 0) * 1000)} ms`
			: "None";
		const cameraAspectGuidance = isCamera ? this.getSelectedCameraAspectGuidance() : null;
		const activeCameraVariant = normalizeCameraVariantKey(this.state.activeCameraVariant);
		const editingCameraVariantOverride = isCamera && activeCameraVariant !== CAMERA_VARIANT_BASE;
		const selectedCameraHasVariantOverride = editingCameraVariantOverride && this.hasCameraVariantOverride(selectedItem.guid, activeCameraVariant);
		const previousCameraStartContext = this.getPreviousCameraStartContext();
		const previousCameraStartRect = this.getPreviousCameraStartRect();
		const selectedCameraIsFirst = isCamera && this.isFirstCameraGuid(selectedItem.guid);
		const previousCameraStartAvailable = isCamera && selectedCameraIsFirst && !!previousCameraStartRect;
		const selectedCameraHasExplicitStart = isCamera && !!_.get(selectedItem, "camera.start", null);
		const selectedCameraStartRect = selectedCameraHasExplicitStart ? _.get(selectedItem, "camera.start", null) : null;
		const selectedCameraStartZoomed = isCamera && _.get(selectedItem, "camera.startZoomed", false) === true;
		const selectedCameraTimingDisabled = isCamera && selectedCameraStartZoomed && !selectedCameraHasExplicitStart;
		let previousCameraStartTitle = "No previous screen camera found";
		if (!selectedCameraIsFirst) {
			previousCameraStartTitle = "Previous Screen start can only be applied to the first camera";
		} else if (previousCameraStartContext) {
			previousCameraStartTitle = `Use final camera from ${previousCameraStartContext.sourceItemLabel || "previous screen"}`;
		}

		return (
			<div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
				{editingCameraVariantOverride && (
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "8px",
							padding: "10px 12px",
							borderRadius: "12px",
							border: "1px solid #c7d2fe",
							background: "#eef2ff",
							color: "#3730a3"
						}}
					>
						<div style={{ fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>
							{`Editing ${this.getCameraVariantSummaryLabel(activeCameraVariant)} Camera Override`}
						</div>
						<div style={{ fontSize: "13px", lineHeight: 1.5 }}>
							Camera name and timing stay shared with Base. Position, size, and rotation on this panel edit only the active variant.
						</div>
						<div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
							<Button bsSize="xsmall" onClick={this.handleAutoFitSelectedCameraVariant}>
								Auto-fit This Camera
							</Button>
							<Button bsSize="xsmall" onClick={this.handleResetSelectedCameraVariant} disabled={!selectedCameraHasVariantOverride}>
								Reset To Base
							</Button>
						</div>
					</div>
				)}
				{isCamera && cameraAspectGuidance && (
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "6px",
							padding: "10px 12px",
							borderRadius: "12px",
							border: cameraAspectGuidance.severity === "warning" ? "1px solid #f5c184" : "1px solid #bfdbfe",
							background: cameraAspectGuidance.severity === "warning" ? "#fff7ed" : "#eff6ff",
							color: cameraAspectGuidance.severity === "warning" ? "#9a3412" : "#1d4ed8"
						}}
					>
						<div style={{ fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>
							{cameraAspectGuidance.title}
						</div>
						<div style={{ fontSize: "12px", fontWeight: 600 }}>
							{cameraAspectGuidance.meta}
						</div>
						<div style={{ fontSize: "13px", lineHeight: 1.5 }}>
							{cameraAspectGuidance.body}
						</div>
					</div>
				)}
				<div>
					<div style={{ color: "#111827", fontWeight: 700 }}>{selectedItem.id}</div>
					<div style={{ color: "#6b7280", fontSize: "12px" }}>{buildItemSummaryLabel(this.state.selectedItemKind, selectedItem)}</div>
					<div style={{ color: "#4b5563", fontSize: "13px", lineHeight: 1.5 }}>
						layer: {selectedItemLayerNumber} of {selectedSourceItems.length} {selectedSourceItems.length > 1 ? "(top renders in front within this list)" : ""}
					</div>
				</div>
				<div>
					<div style={labelStyle}>Name</div>
					<input className="form-control" type="text" value={selectedItem.id || ""} onChange={this.handleSelectedIdChange} style={inputStyle} />
				</div>
				<div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
					<Button bsSize="small" onClick={this.handleCloneSelectedItem} title="Clone selected item">
						<FontAwesomeIcon icon="clone" />
					</Button>
					<Button bsSize="small" onClick={this.handleToggleSelectedCanvasVisibility} title={selectedItemHidden ? "Show on canvas" : "Hide on canvas"}>
						<FontAwesomeIcon icon={selectedItemHidden ? "eye-slash" : "eye"} />
					</Button>
					<Button bsSize="small" onClick={() => { this.handleReorderSelectedItem("front"); }} title="Bring selected item to front" disabled={!selectedItemCanMoveForward}>
						Front
					</Button>
					<Button bsSize="small" onClick={() => { this.handleReorderSelectedItem("forward"); }} title="Move selected item forward one layer" disabled={!selectedItemCanMoveForward}>
						+1 Layer
					</Button>
					<Button bsSize="small" onClick={() => { this.handleReorderSelectedItem("backward"); }} title="Move selected item backward one layer" disabled={!selectedItemCanMoveBackward}>
						-1 Layer
					</Button>
					<Button bsSize="small" onClick={() => { this.handleReorderSelectedItem("back"); }} title="Send selected item to back" disabled={!selectedItemCanMoveBackward}>
						Back
					</Button>
					<Button bsSize="small" onClick={() => { this.handleNudgeSelectedItem(-1, 0); }} title="Move left">
						<FontAwesomeIcon icon="arrow-left" />
					</Button>
					<Button bsSize="small" onClick={() => { this.handleNudgeSelectedItem(1, 0); }} title="Move right">
						<FontAwesomeIcon icon="arrow-right" />
					</Button>
					<Button bsSize="small" onClick={() => { this.handleNudgeSelectedItem(0, -1); }} title="Move up">
						<FontAwesomeIcon icon="arrow-up" />
					</Button>
					<Button bsSize="small" onClick={() => { this.handleNudgeSelectedItem(0, 1); }} title="Move down">
						<FontAwesomeIcon icon="arrow-down" />
					</Button>
					<Button bsSize="small" onClick={() => { this.handleRotateSelectedItem(-1); }} title="Rotate left">
						<FontAwesomeIcon icon="undo" />
					</Button>
					<Button bsSize="small" onClick={() => { this.handleRotateSelectedItem(1); }} title="Rotate right">
						<FontAwesomeIcon icon="redo" />
					</Button>
					<Button bsStyle="danger" bsSize="small" onClick={this.handleDeleteSelectedItem} title="Delete selected item">
						<FontAwesomeIcon icon="trash" />
					</Button>
				</div>
				{!isCamera && colorPickActive && (
					<div style={{ color: "#0f766e", fontSize: "13px", lineHeight: 1.5, fontWeight: 600 }}>
						Eyedropper active: click the image to sample {colorPickTargetLabel}.
					</div>
				)}
				<div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "10px" }}>
					<div>
						<div style={labelStyle}>Start</div>
						<input className="form-control" type="number" value={selectedItem.startTime || 0} onChange={(event) => { this.handleSelectedTimingChange("startTime", event); }} style={inputStyle} disabled={selectedCameraTimingDisabled} />
					</div>
					<div>
						<div style={labelStyle}>End</div>
						<input className="form-control" type="number" value={selectedItem.endTime !== undefined && selectedItem.endTime !== null ? selectedItem.endTime : -1} onChange={(event) => { this.handleSelectedTimingChange("endTime", event); }} style={inputStyle} disabled={selectedCameraTimingDisabled} />
					</div>
				</div>
				{selectedCameraTimingDisabled && (
					<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.4 }}>
						Start Zoomed opens immediately when no explicit opening view is saved. Timing applies when an opening view is set.
					</div>
				)}
				<div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
					<div style={{ color: "#4b5563", fontSize: "13px" }}>Cursor: {currentCursorLabel}</div>
					<div style={{ color: "#4b5563", fontSize: "13px" }}>Region: {currentRegionLabel}</div>
					<Button bsSize="small" onClick={this.handleApplyCurrentCursorOrRegionToSelected}>
						Use Current Cursor / Region
					</Button>
				</div>
				<div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "10px" }}>
					<div>
						<div style={labelStyle}>X</div>
						<input className="form-control" type="number" value={this.getSelectedNumericFieldValue("x", selectedRect.x)} onFocus={this.ensureSelectedNumericDrafts} onChange={(event) => { this.handleSelectedRectFieldChange("x", event); }} onBlur={() => { this.handleSelectedNumericDraftBlur("x"); }} onKeyDown={(event) => { this.handleSelectedNumericDraftKeyDown("x", event); }} style={inputStyle} />
					</div>
					<div>
						<div style={labelStyle}>Y</div>
						<input className="form-control" type="number" value={this.getSelectedNumericFieldValue("y", selectedRect.y)} onFocus={this.ensureSelectedNumericDrafts} onChange={(event) => { this.handleSelectedRectFieldChange("y", event); }} onBlur={() => { this.handleSelectedNumericDraftBlur("y"); }} onKeyDown={(event) => { this.handleSelectedNumericDraftKeyDown("y", event); }} style={inputStyle} />
					</div>
					<div>
						<div style={labelStyle}>Width</div>
						<input className="form-control" type="number" min={MIN_ITEM_SIZE} value={this.getSelectedNumericFieldValue("width", selectedRect.width)} onFocus={this.ensureSelectedNumericDrafts} onChange={(event) => { this.handleSelectedRectFieldChange("width", event); }} onBlur={() => { this.handleSelectedNumericDraftBlur("width"); }} onKeyDown={(event) => { this.handleSelectedNumericDraftKeyDown("width", event); }} style={inputStyle} />
					</div>
					<div>
						<div style={labelStyle}>Height</div>
						<input className="form-control" type="number" min={MIN_ITEM_SIZE} value={this.getSelectedNumericFieldValue("height", selectedRect.height)} onFocus={this.ensureSelectedNumericDrafts} onChange={(event) => { this.handleSelectedRectFieldChange("height", event); }} onBlur={() => { this.handleSelectedNumericDraftBlur("height"); }} onKeyDown={(event) => { this.handleSelectedNumericDraftKeyDown("height", event); }} style={inputStyle} />
					</div>
				</div>
				<div>
					<div style={labelStyle}>Rotation</div>
					<input className="form-control" type="number" value={this.getSelectedNumericFieldValue("rotation", selectedRotation)} onFocus={this.ensureSelectedNumericDrafts} onChange={this.handleSelectedRotationInputChange} onBlur={() => { this.handleSelectedNumericDraftBlur("rotation"); }} onKeyDown={(event) => { this.handleSelectedNumericDraftKeyDown("rotation", event); }} style={{ width: "120px" }} />
				</div>
				{!isCamera && (
					<div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
						<div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "10px" }}>
							<div>
								<div style={labelStyle}>Shape</div>
								<select className="form-control" value={this.state.shape} onChange={this.handleShapeChange}>
									{OVERLAY_SHAPE_OPTIONS.map((option) => (
										<option key={`selected-overlay-shape-${option.value}`} value={option.value}>{option.label}</option>
									))}
								</select>
							</div>
							<div>
								<div style={labelStyle}>Preset</div>
								<select className="form-control" value={this.state.stylePreset} onChange={this.handleStylePresetChange}>
									{this.state.stylePreset === "custom" && <option value="custom">Custom</option>}
									{Object.entries(OVERLAY_STYLE_PRESETS).map(([value, preset]) => (
										<option key={`selected-overlay-preset-${value}`} value={value}>{preset.label}</option>
									))}
								</select>
							</div>
						</div>
						<div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "10px" }}>
							<div>
								<div style={labelStyle}>Border Width</div>
								<select className="form-control" value={this.state.borderWidth} onChange={this.handleBorderWidthChange}>
									{BORDER_WIDTH_OPTIONS.map((option) => (
										<option key={`selected-overlay-border-width-${option}`} value={option}>{option}</option>
									))}
								</select>
							</div>
							<div>
								<div style={labelStyle}>Border Radius</div>
								<select
									className="form-control"
									value={shapeSupportsBorderRadius(this.state.shape) ? this.state.borderRadius : 0}
									onChange={this.handleBorderRadiusChange}
									disabled={!shapeSupportsBorderRadius(this.state.shape)}
								>
									{BORDER_RADIUS_OPTIONS.map((option) => (
										<option key={`selected-overlay-border-radius-${option}`} value={option}>{option}</option>
									))}
								</select>
							</div>
						</div>
						<div style={{ display: "grid", gridTemplateColumns: shapeSupportsFill(this.state.shape) && this.state.fillEnabled ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)", gap: "10px" }}>
							<div>
								<div style={labelStyle}>Border Color</div>
								<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
									<ColorPicker
										id="popover-media-markup-v2-selected-border-color"
										title="Markup Border Color"
										onChangeColor={this.handleBorderColorChange}
										onClearColor={() => {}}
										presetColors={env.defaultColors}
										color={this.state.borderColor}
										placement="bottom"
										hideClear={true}
										disableAlpha={false}
										container={colorPopoverContainer}
										popoverStyle={colorPopoverStyle}
									/>
									<Button
										bsSize="small"
										bsStyle={this.state.colorPickTarget === COLOR_PICK_TARGETS.border ? "info" : "default"}
										onClick={() => { this.handleColorPickToggle(COLOR_PICK_TARGETS.border); }}
										title={this.state.colorPickTarget === COLOR_PICK_TARGETS.border ? "Cancel border color eyedropper" : "Pick border color from image"}
										style={colorPickButtonStyle}
										disabled={this.state.canvasMode !== "edit"}
									>
										<FontAwesomeIcon icon="crosshairs" />
									</Button>
								</div>
							</div>
							{shapeSupportsFill(this.state.shape) && this.state.fillEnabled && (
								<div>
									<div style={labelStyle}>Fill Color</div>
									<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
										<ColorPicker
											id="popover-media-markup-v2-selected-fill-color"
											title="Markup Fill Color"
											onChangeColor={this.handleFillColorChange}
											onClearColor={() => {}}
											presetColors={env.defaultColors}
											color={this.state.fillColor}
											placement="bottom"
											hideClear={true}
											disableAlpha={false}
											container={colorPopoverContainer}
											popoverStyle={colorPopoverStyle}
										/>
										<Button
											bsSize="small"
											bsStyle={this.state.colorPickTarget === COLOR_PICK_TARGETS.fill ? "info" : "default"}
											onClick={() => { this.handleColorPickToggle(COLOR_PICK_TARGETS.fill); }}
											title={this.state.colorPickTarget === COLOR_PICK_TARGETS.fill ? "Cancel fill color eyedropper" : "Pick fill color from image"}
											style={colorPickButtonStyle}
											disabled={this.state.canvasMode !== "edit"}
										>
											<FontAwesomeIcon icon="crosshairs" />
										</Button>
									</div>
								</div>
							)}
						</div>
						<label style={{ display: "flex", alignItems: "center", gap: "8px", margin: 0, color: "#374151", fontWeight: 500, opacity: shapeSupportsFill(this.state.shape) ? 1 : 0.55 }}>
							<input type="checkbox" checked={shapeSupportsFill(this.state.shape) ? this.state.fillEnabled : false} onChange={this.handleFillEnabledChange} disabled={!shapeSupportsFill(this.state.shape)} />
							<span>Fill</span>
						</label>
					</div>
				)}
				{isCamera &&
					<>
						<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
							<div style={labelStyle}>Camera Quick Adjust</div>
							<div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
								<Button bsSize="small" onClick={() => { this.handleCameraScaleSelectedItem(CAMERA_ZOOM_IN_FACTOR); }} title="Zoom in selected camera">
									<FontAwesomeIcon icon="search-plus" />
								</Button>
								<Button bsSize="small" onClick={() => { this.handleCameraScaleSelectedItem(CAMERA_ZOOM_OUT_FACTOR); }} title="Zoom out selected camera">
									<FontAwesomeIcon icon="search-minus" />
								</Button>
								<Button bsSize="small" onClick={() => { this.handleCameraPanSelectedItem(-CAMERA_PAN_STEP, 0); }} title="Pan left">
									<FontAwesomeIcon icon="arrow-left" />
								</Button>
								<Button bsSize="small" onClick={() => { this.handleCameraPanSelectedItem(CAMERA_PAN_STEP, 0); }} title="Pan right">
									<FontAwesomeIcon icon="arrow-right" />
								</Button>
								<Button bsSize="small" onClick={() => { this.handleCameraPanSelectedItem(0, -CAMERA_PAN_STEP); }} title="Pan up">
									<FontAwesomeIcon icon="arrow-up" />
								</Button>
								<Button bsSize="small" onClick={() => { this.handleCameraPanSelectedItem(0, CAMERA_PAN_STEP); }} title="Pan down">
									<FontAwesomeIcon icon="arrow-down" />
								</Button>
							</div>
						</div>
						<label style={{ display: "flex", alignItems: "center", gap: "8px", margin: 0, color: "#374151", fontWeight: 500 }}>
							<input type="checkbox" checked={_.get(selectedItem, "camera.startZoomed", false) === true} onChange={this.handleSelectedStartZoomedChange} />
							<span>Start Zoomed</span>
						</label>
						<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
							<div style={labelStyle}>Opening View</div>
							<div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
								<Button bsSize="small" onClick={this.handleApplyPreviousCameraStartToSelected} disabled={!previousCameraStartAvailable} title={previousCameraStartTitle}>
									<FontAwesomeIcon icon="arrow-left" /> Previous Screen
								</Button>
								<Button bsSize="small" onClick={this.handleClearSelectedCameraStart} disabled={!selectedCameraHasExplicitStart} title="Clear explicit opening view">
									Clear Opening
								</Button>
							</div>
							<div style={{ color: selectedCameraHasExplicitStart ? "#0f766e" : "#6b7280", fontSize: "12px", lineHeight: 1.4 }}>
								{selectedCameraHasExplicitStart
									? "This screen opens from the saved frame below. Move the camera frame and set End timing to animate away from it."
									: "Without an explicit opening view, Start Zoomed opens at the camera frame above."}
							</div>
							{selectedCameraStartRect && (
								<div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "8px", padding: "8px", border: "1px solid #d1fae5", borderRadius: "8px", background: "#f0fdf4" }}>
									<div>
										<div style={labelStyle}>Opening X</div>
										<input className="form-control" type="number" value={Math.round(Number(selectedCameraStartRect.x) || 0)} readOnly />
									</div>
									<div>
										<div style={labelStyle}>Opening Y</div>
										<input className="form-control" type="number" value={Math.round(Number(selectedCameraStartRect.y) || 0)} readOnly />
									</div>
									<div>
										<div style={labelStyle}>Opening Width</div>
										<input className="form-control" type="number" value={Math.round(Number(selectedCameraStartRect.width) || 0)} readOnly />
									</div>
									<div>
										<div style={labelStyle}>Opening Height</div>
										<input className="form-control" type="number" value={Math.round(Number(selectedCameraStartRect.height) || 0)} readOnly />
									</div>
								</div>
							)}
						</div>
					</>
				}
				{!isCamera && (
					<>
						{(selectedItem.type || "highlight") === "hotspot" && (
							<>
								<div>
									<div style={labelStyle}>Hotspot Label</div>
									<input className="form-control" type="text" value={selectedItem.label || ""} onChange={this.handleSelectedLabelChange} />
								</div>
								<div>
									<div style={labelStyle}>Action</div>
									<select className="form-control" value={selectedItem.action || "none"} onChange={this.handleSelectedActionChange}>
										<option value="none">None</option>
										<option value="gotoNext">Go To Next</option>
									</select>
								</div>
								<label style={{ display: "flex", alignItems: "center", gap: "8px", margin: 0, color: "#374151", fontWeight: 500 }}>
									<input type="checkbox" checked={selectedItem.labelVisible === true} onChange={this.handleSelectedLabelVisibleChange} />
									<span>Show Label</span>
								</label>
							</>
						)}
					</>
				)}
			</div>
		);
	}

	renderItemListSection(kind, title, items, accentColor, activeTimelineGuidSets = null) {
		const allVisible = items.length > 0 && items.every((item) => this.isCanvasItemVisible(item));
		const activeGuids = activeTimelineGuidSets
			? (kind === "camera" ? activeTimelineGuidSets.cameraGuids : activeTimelineGuidSets.overlayGuids)
			: null;
		const orderedItems = [...items].reverse();
		const reorderDescription = kind === "camera"
			? "Top of this list renders in front among camera frames on the edit canvas."
			: "Top of this list renders in front on the canvas and in preview.";
		const layerButtonStyle = {
			minWidth: "0",
			padding: "2px 7px",
			fontSize: "11px",
			lineHeight: 1.2,
			color: "#64748b",
			borderColor: "#cbd5e1",
			background: "#f8fafc"
		};
		const visibilityButtonStyle = {
			minWidth: "32px",
			padding: "2px 7px",
			fontSize: "11px",
			lineHeight: 1.2,
			color: "#475569",
			borderColor: "#cbd5e1",
			background: "#f8fafc"
		};

		return (
			<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
					<div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
						<div style={{ width: "10px", height: "10px", borderRadius: "999px", background: accentColor, flex: "0 0 auto" }} />
						<div style={{ fontSize: "12px", color: "#374151", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>
							{title}
						</div>
						<div style={{ fontSize: "12px", color: "#6b7280" }}>
							{items.length}
						</div>
					</div>
					{items.length > 0 && (
						<Button bsSize="xsmall" onClick={() => { this.handleToggleAllCanvasVisibility(kind); }}>
							<FontAwesomeIcon icon={allVisible ? "eye-slash" : "eye"} /> {allVisible ? "Hide All" : "Show All"}
						</Button>
					)}
				</div>
				{items.length > 0 && (
					<div style={{ color: "#4b5563", fontSize: "12px", lineHeight: 1.5 }}>
						{reorderDescription}
					</div>
				)}
					{items.length === 0 && (
						<div style={{ color: "#9ca3af", fontSize: "12px", lineHeight: 1.5 }}>
							No items yet.
						</div>
					)}
					{orderedItems.map((item, listIndex) => {
						const isSelected = this.state.selectedItemKind === kind && this.state.selectedItemGuid === item.guid;
						const isRenaming = this.state.renamingSurface === "list"
							&& this.state.renamingItemKind === kind
							&& this.state.renamingItemGuid === item.guid;
						const itemIndex = items.length - 1 - listIndex;
						const isFrontmost = itemIndex === items.length - 1;
						const isBackmost = itemIndex === 0;
						const rect = this.getCanvasItemRectForKind(kind, item) || {};
						const hidden = !this.isCanvasItemVisible(item);
						const timelineFilteredHidden = !!activeGuids && !activeGuids.has(item.guid);
						const variantOverrideActive = kind === "camera" && !this.isEditingBaseCameraVariant() && this.hasCameraVariantOverride(item.guid);
						const cardBorder = isSelected
							? "1px solid #2563eb"
							: (timelineFilteredHidden ? "1px solid #f59e0b" : "1px solid #d1d5db");
						const cardBackground = isSelected
							? "#eff6ff"
							: (timelineFilteredHidden ? "#fffbeb" : "#ffffff");

						return (
							<div
								key={`${kind}-${item.guid}`}
								style={{
									display: "flex",
									flexDirection: "column",
									border: cardBorder,
									background: cardBackground,
									borderRadius: "10px",
									opacity: hidden ? 0.55 : (timelineFilteredHidden ? 0.88 : 1),
									overflow: "hidden"
								}}
							>
								<div
									role="button"
									tabIndex={0}
									onClick={(event) => {
										event.preventDefault();
										event.stopPropagation();
										if (this.consumePendingInlineRenameActivation()) {
											return;
										}
										if (isSelected) {
											this.clearSelection();
											return;
										}
										this.selectItem(kind, item.guid);
									}}
									onKeyDown={(event) => {
										if (event.key === "Enter" || event.key === " ") {
											event.preventDefault();
											if (isSelected) {
												this.clearSelection();
												return;
											}
											this.selectItem(kind, item.guid);
										}
									}}
									style={{
										textAlign: "left",
										padding: "10px 12px",
										color: "#111827",
										cursor: "pointer"
									}}
								>
									<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
										{isRenaming
											? (
												<input
													ref={this.inlineRenameInputRef}
													autoFocus
													type="text"
													value={this.state.renamingItemValue}
													onChange={this.handleInlineRenameChange}
													onBlur={this.commitInlineRename}
													onKeyDown={this.handleInlineRenameKeyDown}
													onMouseDown={(event) => {
														event.stopPropagation();
													}}
													onClick={(event) => {
														event.stopPropagation();
													}}
													style={{ minWidth: 0, flex: "1 1 auto", fontWeight: 600, height: "30px" }}
												/>
											)
												: (
													<div
														style={{ fontWeight: 600, minWidth: 0, userSelect: "none", cursor: "pointer" }}
														title={isSelected ? "Double-click to rename" : "Click to select"}
														onClick={(event) => {
															event.preventDefault();
															event.stopPropagation();
															if (this.consumePendingInlineRenameActivation()) {
																return;
															}
															if (!isSelected) {
																this.selectItem(kind, item.guid);
															}
														}}
														onDoubleClick={(event) => {
															event.preventDefault();
															event.stopPropagation();
															this.startInlineRename(kind, item.guid, "list");
														}}
													>
														{item.id}
												</div>
											)}
										<div style={{ fontSize: "11px", color: kind === "camera" ? "#0f766e" : "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>
											{timelineFilteredHidden
												? "inactive @ time"
												: (listIndex === 0 ? "front" : (listIndex === orderedItems.length - 1 ? "back" : `layer ${itemIndex + 1}`))}
										</div>
									</div>
									<div style={{ fontSize: "12px", color: "#6b7280" }}>
										{buildItemSummaryLabel(kind, item)} • {rect.width || 0} x {rect.height || 0}
										{variantOverrideActive ? ` • ${this.getCameraVariantSummaryLabel()} override` : ""}
										{timelineFilteredHidden ? " • hidden by timeline filter" : ""}
										{hidden ? " • hidden" : ""}
									</div>
								</div>
								<div
									style={{
										display: "flex",
										flexWrap: "wrap",
										gap: "4px",
										alignItems: "center",
										justifyContent: "flex-start",
										padding: "6px 10px",
										borderTop: "1px solid rgba(203, 213, 225, 0.8)",
										background: isSelected ? "rgba(219, 234, 254, 0.55)" : "rgba(248, 250, 252, 0.96)"
									}}
								>
									<Button
										bsSize="xsmall"
										onClick={(event) => { this.handleReorderItem(kind, item.guid, "front", event); }}
										title="Bring item to front"
										style={layerButtonStyle}
										disabled={isFrontmost}
									>
										Top
									</Button>
									<Button
										bsSize="xsmall"
										onClick={(event) => { this.handleReorderItem(kind, item.guid, "forward", event); }}
										title="Move item forward one layer"
										style={layerButtonStyle}
										disabled={isFrontmost}
									>
										Up
									</Button>
									<Button
										bsSize="xsmall"
										onClick={(event) => { this.handleReorderItem(kind, item.guid, "backward", event); }}
										title="Move item backward one layer"
										style={layerButtonStyle}
										disabled={isBackmost}
									>
										Down
									</Button>
									<Button
										bsSize="xsmall"
										onClick={(event) => { this.handleReorderItem(kind, item.guid, "back", event); }}
										title="Send item to back"
										style={layerButtonStyle}
										disabled={isBackmost}
									>
										Back
									</Button>
									<Button
										bsSize="xsmall"
										onClick={() => { this.handleToggleItemCanvasVisibility(kind, item.guid); }}
										title={hidden ? "Show on canvas" : "Hide on canvas"}
										style={visibilityButtonStyle}
									>
										<FontAwesomeIcon icon={hidden ? "eye-slash" : "eye"} />
									</Button>
								</div>
							</div>
						);
					})}
				</div>
			);
		}

	renderItemList() {
		const overlayItems = this.state.imageHotspots;
		const cameraItems = this.getEffectiveCameraItems();
		const allItems = [...overlayItems, ...cameraItems];
		const activeTimelineGuidSets = this.getActiveTimelineCanvasGuidSets();

		if (allItems.length === 0) {
			return (
				<div style={{ color: "#6b7280", fontSize: "13px", lineHeight: 1.5 }}>
					Drag anywhere on the canvas to create highlights, hotspots, arrows, and camera frames. Use the item handles or this panel to select and organize them.
				</div>
			);
		}

		return (
			<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
				{this.renderItemListSection("overlay", "Hotspots, Highlights & Overlays", overlayItems, "#2563eb", activeTimelineGuidSets)}
				{this.renderItemListSection("camera", "Cameras", cameraItems, "#0f766e", activeTimelineGuidSets)}
			</div>
		);
	}

	render() {
		if (!this.props.open) {
			return null;
		}

		const { imageSize } = this.state;
		const compactLayout = typeof window !== "undefined" && window.innerWidth < 1350;
		const viewportHeight = typeof window !== "undefined" ? window.innerHeight || 900 : 900;
		const compactEditorHeight = Math.min(Math.max(viewportHeight - (this.state.waveformCollapsed ? 330 : 430), 340), 700);
		const selectedItem = this.getSelectedItem();
		const selectedItemIsCamera = this.state.selectedItemKind === "camera";
		const showOverlayControls = this.state.componentType !== "camera";
		const colorPopoverContainer = typeof document !== "undefined" ? document.body : undefined;
		const colorPopoverStyle = { zIndex: 210000 };
			const viewBox = this.getCanvasViewBox();
			const canvasPaddingValue = this.getEditorCanvasPadding();
			const canvasScaleRatio = (this.state.canvasMode === "preview" ? this.state.previewScale : this.state.editScale) / 100;
			const canvasDisplayWidth = imageSize ? viewBox.width * canvasScaleRatio : 1;
			const canvasDisplayHeight = imageSize ? viewBox.height * canvasScaleRatio : 1;
			const panelWidths = this.getClampedPanelWidths();
			const itemListPanelWidth = panelWidths.itemListPanelWidth;
			const propertiesPanelWidth = panelWidths.propertiesPanelWidth;
			const layoutMode = normalizeLayoutMode(this.state.layoutMode);
			const isStackedRightLayout = layoutMode === LAYOUT_MODE_STACKED_RIGHT_SPLIT || layoutMode === LAYOUT_MODE_STACKED_RIGHT_APPEND;
			const showLeftPanel = compactLayout
				? !this.state.leftPanelCollapsed
				: layoutMode === LAYOUT_MODE_SPLIT && !this.state.leftPanelCollapsed;
			const showRightPanel = !this.state.rightPanelCollapsed;
			const canvasStatusLabel = this.state.canvasMode === "edit"
				? `${this.state.mouseX}, ${this.state.mouseY}`
				: `${Math.round(this.state.currentTimeMs || 0)} ms`;
			const imageSizeLabel = imageSize ? `${imageSize.width} x ${imageSize.height}` : "";
			const canUndo = this.state.historyPast.length > 0;
			const canRedo = this.state.historyFuture.length > 0;
			const allowCanvasPopout = isFutureEnabled(this.props.urlParams || null);
			const canvasPopoutOpen = !!(allowCanvasPopout && this.state.canvasPopoutOpen && imageSize);
			const toolbarRowStyle = {
				display: "flex",
				justifyContent: "flex-start",
				alignItems: "flex-end",
				gap: "8px",
				flexWrap: "nowrap",
				width: "100%",
				minWidth: "max-content"
			};
			const toolbarClusterStyle = {
				display: "flex",
				alignItems: "flex-end",
				gap: "8px",
				flexWrap: "nowrap",
				minWidth: "fit-content",
				maxWidth: "100%"
			};
			const toolbarTrailingClusterStyle = Object.assign({}, toolbarClusterStyle, {
				marginLeft: "auto",
				justifyContent: "flex-end"
			});
			const toolbarSectionStyle = {
				display: "flex",
				flexDirection: "row",
				alignItems: "flex-end",
				justifyContent: "flex-start",
				flexWrap: "nowrap",
				gap: "6px"
			};
			const toolbarButtonSectionStyle = {
				display: "flex",
				flexDirection: "row",
				alignItems: "center",
				justifyContent: "flex-start",
				flexWrap: "nowrap",
				gap: "4px",
				paddingBottom: "1px"
			};
			const toolbarFieldLabelStyle = {
				fontSize: "10px",
				lineHeight: 1.1
			};
			const toolbarFieldStyle = {
				height: 42,
				display: "flex",
				flexDirection: "column",
				gap: "3px",
				justifyContent: "flex-start",
				alignItems: "center"
			};
			const toolbarFieldStartStyle = Object.assign({}, toolbarFieldStyle, {
				alignItems: "flex-start"
			});
			const toolbarCheckboxFieldStyle = {
				height: 42,
				display: "flex",
				flexDirection: "column",
				gap: "3px",
				justifyContent: "flex-start",
				alignItems: "flex-start"
			};
			const toolbarFieldLabelPlaceholderStyle = Object.assign({}, toolbarFieldLabelStyle, {
				visibility: "hidden"
			});
			const toolbarCheckboxLabelStyle = {
				display: "flex",
				alignItems: "center",
				gap: "6px",
				margin: 0,
				fontSize: "11px",
				fontWeight: 500
			};
			const toolbarSectionDividerStyle = {
				width: "1px",
				height: "28px",
				alignSelf: "center",
				background: "rgba(107,114,128,0.6)",
				flex: "0 0 auto"
			};
			const canvasFrameToolbarStyle = {
				flex: "0 0 auto",
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				gap: "12px",
				padding: "2px 0 8px 0",
				borderBottom: "1px solid rgba(219, 226, 234, 0.9)"
			};
			const canvasFrameToolbarButtonSectionStyle = {
				display: "flex",
				alignItems: "center",
				gap: "6px",
				flexWrap: "nowrap"
			};
			const canvasFrameToolbarStatusStyle = {
				display: "flex",
				alignItems: "center",
				gap: "8px",
				marginLeft: "auto",
				flexWrap: "nowrap"
			};
			const canvasStatusBadgeStyle = {
				fontFamily: "Courier, monospace",
				fontSize: "11px",
				color: "#0f172a",
				background: "rgba(219, 234, 254, 0.96)",
				border: "1px solid rgba(96, 165, 250, 0.85)",
				borderRadius: "8px",
				padding: "5px 8px",
				minWidth: "96px",
				textAlign: "center",
				boxShadow: "0 4px 10px rgba(15, 23, 42, 0.12)"
			};
			const canvasImageSizeBadgeStyle = Object.assign({}, canvasStatusBadgeStyle, {
				color: "#334155",
				background: "rgba(226, 232, 240, 0.96)",
				border: "1px solid rgba(148, 163, 184, 0.5)",
				minWidth: "88px"
			});

		const headerStyle = {
			display: "grid",
			gridTemplateColumns: "1fr auto 1fr",
			alignItems: "center",
			gap: "12px"
		};
		const bodyStyle = {
			flex: "1 1 auto",
			minHeight: 0,
			overflow: "hidden",
			padding: "10px 14px 8px 14px",
			display: "flex",
			flexDirection: "column",
			gap: "10px"
		};
			const workspaceStyle = {
				flex: "1 1 0",
				minHeight: 0,
				height: "100%",
				maxHeight: "100%",
				display: "flex",
				flexDirection: compactLayout ? "column" : "row",
				gap: compactLayout ? "16px" : "0px",
				minWidth: 0,
				overflowX: "hidden",
				overflowY: compactLayout ? "auto" : "hidden",
				alignItems: "stretch"
			};
			const sidePanelBaseStyle = {
				display: "flex",
				flexDirection: "column",
				gap: "14px",
				minWidth: 0,
				minHeight: 0,
				height: compactLayout ? `${compactEditorHeight}px` : "100%",
				maxHeight: compactLayout ? `${compactEditorHeight}px` : "100%",
				overflowX: "hidden",
				overflowY: "auto",
				padding: compactLayout ? "0 0 42px 0" : "14px 14px 42px 14px",
				boxSizing: "border-box",
				scrollPaddingBottom: "42px",
				border: compactLayout ? "none" : "1px solid #e5e7eb",
				borderRadius: compactLayout ? 0 : "16px",
				background: compactLayout ? "transparent" : "#fbfcfe"
			};
			const canvasPaneStyle = {
				border: "1px solid #dbe2ea",
				borderRadius: "16px",
				background: "#f8fafc",
				minHeight: 0,
				height: compactLayout ? `${compactEditorHeight}px` : "100%",
				maxHeight: compactLayout ? `${compactEditorHeight}px` : "100%",
				overflow: "hidden",
				display: "flex",
				flexDirection: "column",
				gap: "10px",
				padding: "14px",
				boxSizing: "border-box",
				scrollPaddingBottom: "14px"
			};
			const activeCanvasPaneStyle = Object.assign({}, canvasPaneStyle, {
				flex: "1 1 auto",
				minWidth: 0
			});
			const canvasPopoutPaneStyle = {
				border: "1px solid #dbe2ea",
				borderRadius: "16px",
				background: "#f8fafc",
				minHeight: 0,
				minWidth: 0,
				width: "100%",
				height: "100%",
				maxHeight: "100%",
				overflow: "hidden",
				display: "flex",
				flexDirection: "column",
				gap: "10px",
				padding: "14px",
				boxSizing: "border-box",
				scrollPaddingBottom: "14px",
				flex: "1 1 auto"
			};
			const editCanvasScrollAreaStyle = {
				flex: "1 1 auto",
				minHeight: 0,
				overflowX: "auto",
				overflowY: "auto",
				paddingBottom: "6px",
				scrollPaddingBottom: "6px"
			};
			const previewCanvasBodyStyle = {
				flex: "1 1 auto",
				minHeight: 0,
				overflow: "hidden",
				display: "flex",
				flexDirection: "column",
				gap: "14px"
			};
			const previewCanvasScrollAreaStyle = {
				flex: "1 1 auto",
				minHeight: 0,
				overflowX: "auto",
				overflowY: "auto",
				paddingBottom: "6px",
				scrollPaddingBottom: "6px"
			};
			const previewCanvasScrollContentStyle = {
				display: "flex",
				flexDirection: "column",
				gap: "14px",
				width: "fit-content",
				minWidth: "100%"
			};
			const canvasEmptyStateStyle = {
				flex: "1 1 auto",
				minHeight: 0,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				padding: "24px",
				textAlign: "center",
				color: "#475569",
				fontSize: "14px",
				lineHeight: 1.5
			};
			const canvasPopoutViewportStyle = {
				position: "relative",
				width: "100%",
				height: "100%",
				minHeight: "100%",
				display: "flex",
				flexDirection: "column",
				padding: "12px",
				boxSizing: "border-box",
				background: "#e2e8f0"
			};
			const canvasPopoutHudStyle = {
				position: "absolute",
				top: "14px",
				right: "14px",
				display: "flex",
				alignItems: "center",
				gap: "8px",
				zIndex: 20,
				pointerEvents: "none"
			};
			const canvasPointerBadgeMaxLeft = Math.max(canvasDisplayWidth - 112, 12);
			const canvasPointerBadgeMaxTop = Math.max(canvasDisplayHeight - 34, 12);
			const canvasPointerBadgeStyle = {
				position: "absolute",
				left: `${clampValue(((this.state.mouseX - viewBox.x) * canvasScaleRatio) + 14, 12, canvasPointerBadgeMaxLeft)}px`,
				top: `${clampValue(((this.state.mouseY - viewBox.y) * canvasScaleRatio) + 14, 12, canvasPointerBadgeMaxTop)}px`,
				pointerEvents: "none",
				fontFamily: "Courier, monospace",
				fontSize: "11px",
				color: "#0f172a",
				background: "rgba(219, 234, 254, 0.96)",
				border: "1px solid rgba(96, 165, 250, 0.85)",
				borderRadius: "8px",
				padding: "4px 7px",
				boxShadow: "0 4px 10px rgba(15, 23, 42, 0.12)",
				whiteSpace: "nowrap"
			};
			const itemListPanelStyle = Object.assign({}, sidePanelBaseStyle, !compactLayout ? {
				flex: "0 0 auto",
				width: `${itemListPanelWidth}px`,
				minWidth: `${itemListPanelWidth}px`,
				maxWidth: `${itemListPanelWidth}px`
			} : null);
			const propertiesPanelStyle = Object.assign({}, sidePanelBaseStyle, !compactLayout ? {
				flex: "0 0 auto",
				width: `${propertiesPanelWidth}px`,
				minWidth: `${propertiesPanelWidth}px`,
				maxWidth: `${propertiesPanelWidth}px`
			} : null);
			const stackedRightPanelStyle = Object.assign({}, sidePanelBaseStyle, {
				overflowY: "hidden",
				padding: compactLayout ? "0 0 42px 0" : "14px"
			}, !compactLayout ? {
				flex: "0 0 auto",
				width: `${propertiesPanelWidth}px`,
				minWidth: `${propertiesPanelWidth}px`,
				maxWidth: `${propertiesPanelWidth}px`
			} : null);
			const stackedRightSectionStyle = {
				flex: "1 1 0",
				minHeight: 0,
				overflowX: "hidden",
				overflowY: "auto",
				padding: "14px",
				boxSizing: "border-box",
				scrollPaddingBottom: "30px",
				border: "1px solid #e5e7eb",
				borderRadius: "12px",
				background: "#ffffff"
			};
			const panelResizerStyle = {
				width: `${PANEL_RESIZER_WIDTH}px`,
				minWidth: `${PANEL_RESIZER_WIDTH}px`,
				maxWidth: `${PANEL_RESIZER_WIDTH}px`,
				alignSelf: "stretch",
				cursor: "col-resize",
				userSelect: "none",
				borderRadius: "999px",
				background: "linear-gradient(180deg, rgba(203,213,225,0.15) 0%, rgba(148,163,184,0.7) 50%, rgba(203,213,225,0.15) 100%)",
				boxShadow: "inset 0 0 0 1px rgba(148,163,184,0.28)"
			};
		const waveformDockStyle = {
			flex: "0 0 auto",
			borderTop: "1px solid #e5e7eb",
			paddingTop: "8px",
			display: "flex",
			flexDirection: "column",
			gap: this.state.waveformCollapsed ? "0" : "8px",
			backgroundColor: "#ffffff"
		};
			const footerStyle = {
				borderTop: "1px solid #e5e7eb",
				padding: "12px 22px 14px 22px",
				display: "flex",
			justifyContent: "space-between",
			gap: "12px",
				flexWrap: "wrap",
				backgroundColor: "#ffffff"
			};
			const canvasFrameToolbarContent = imageSize ? (
				<div style={canvasFrameToolbarStyle}>
					<div style={canvasFrameToolbarButtonSectionStyle}>
						<Button bsStyle={this.state.canvasMode === "edit" ? "primary" : "default"} bsSize="small" onClick={() => { this.handleCanvasModeChange("edit"); }} title="Edit mode">
							<FontAwesomeIcon icon="edit" /> Edit
						</Button>
						<Button bsStyle="success" bsSize="small" active={this.state.canvasMode === "preview"} onClick={() => { this.handleCanvasModeChange("preview"); }} title="Preview mode">
							<FontAwesomeIcon icon="play" /> Preview
						</Button>
					</div>
					<div style={canvasFrameToolbarStatusStyle}>
						{imageSizeLabel && (
							<div style={canvasImageSizeBadgeStyle}>
								{imageSizeLabel}
							</div>
						)}
						<div style={canvasStatusBadgeStyle}>
							{canvasStatusLabel}
						</div>
					</div>
				</div>
			) : null;
			const canvasViewportContent = (
				<>
					{!imageSize && (
						<div style={canvasEmptyStateStyle}>
							<div>
								<div style={{ fontSize: "15px", fontWeight: 600, color: "#0f172a", marginBottom: "6px" }}>
									{this.state.imageLoading ? "Loading image preview..." : "Image preview unavailable"}
								</div>
								<div>
									{this.state.imageLoading ? "Waiting for the source image so the markup canvas can initialize." : this.state.imageLoadError || "The source image could not be loaded for Media Markup V2."}
								</div>
							</div>
						</div>
					)}
					{imageSize && this.state.canvasMode === "edit" && (
						<div ref={this.canvasPaneRef} style={editCanvasScrollAreaStyle}>
							<div style={{ position: "relative", width: `${canvasDisplayWidth}px`, height: `${canvasDisplayHeight}px`, minWidth: `${canvasDisplayWidth}px`, minHeight: `${canvasDisplayHeight}px` }}>
								<svg
									ref={this.canvasSvgRef}
									viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`}
									width={canvasDisplayWidth}
									height={canvasDisplayHeight}
									style={{
										display: "block",
										background: "linear-gradient(45deg, #eef2f7 25%, transparent 25%, transparent 75%, #eef2f7 75%, #eef2f7), linear-gradient(45deg, #eef2f7 25%, transparent 25%, transparent 75%, #eef2f7 75%, #eef2f7)",
										backgroundSize: "24px 24px",
										backgroundPosition: "0 0, 12px 12px",
										boxShadow: "inset 0 0 0 1px rgba(15, 23, 42, 0.08)",
										cursor: "crosshair"
									}}
									onMouseDown={this.handleCanvasMouseDown}
									onMouseMove={this.handleCanvasMouseMove}
									onMouseUp={this.handleCanvasMouseUp}
									onMouseLeave={this.handleCanvasMouseLeave}
									onClick={this.handleCanvasClick}
								>
									<rect x={viewBox.x} y={viewBox.y} width={viewBox.width} height={viewBox.height} fill="#f8fafc" />
									{canvasPaddingValue > 0 && (
										<rect
											x={viewBox.x + 1}
											y={viewBox.y + 1}
											width={Math.max(viewBox.width - 2, 0)}
											height={Math.max(viewBox.height - 2, 0)}
											fill="transparent"
											stroke="rgba(148, 163, 184, 0.65)"
											strokeWidth="2"
											strokeDasharray="10 8"
											vectorEffect="non-scaling-stroke"
											pointerEvents="none"
										/>
									)}
									<rect x="0" y="0" width={imageSize.width} height={imageSize.height} fill="rgba(255,255,255,0.92)" stroke="rgba(15,23,42,0.30)" strokeWidth="2" />
									<image href={this.props.url} x="0" y="0" width={imageSize.width} height={imageSize.height} />
									<rect x="0" y="0" width={imageSize.width} height={imageSize.height} fill="transparent" stroke="rgba(15,23,42,0.46)" strokeWidth="3" pointerEvents="none" />
									{this.renderDeviceGuides()}
									{this.getEditorCanvasEntries().map(({ kind, item }) => this.renderEditorItem(kind, item))}
									{this.renderDraftItem()}
								</svg>
								{this.state.pointerInCanvas && (
									<div style={canvasPointerBadgeStyle}>
										{this.state.mouseX}, {this.state.mouseY}
									</div>
								)}
							</div>
						</div>
					)}
					{imageSize && this.state.canvasMode === "preview" && (
						<div style={previewCanvasBodyStyle}>
							<div style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", gap: "10px" }}>
								{this.renderPreviewCanvasControls()}
								<div style={{ color: "#6b7280", fontSize: "13px", lineHeight: 1.5 }}>
									{this.props.audioUrl && !this.state.waveformCollapsed
										? "Use the unified bottom scrubber to seek the preview. Device selection will auto-use the closest saved camera variant."
										: "Use the bottom controls to play and seek the preview. Device selection will auto-use the closest saved camera variant."}
								</div>
							</div>
							<div ref={this.canvasPaneRef} style={previewCanvasScrollAreaStyle}>
								<div style={previewCanvasScrollContentStyle}>
									<div style={{ width: `${canvasDisplayWidth}px`, height: `${canvasDisplayHeight}px`, minWidth: `${canvasDisplayWidth}px`, minHeight: `${canvasDisplayHeight}px` }}>
										{this.renderCanvasPreview(canvasDisplayWidth, canvasDisplayHeight, canvasScaleRatio)}
									</div>
								</div>
							</div>
						</div>
					)}
				</>
			);
			const canvasWorkspaceContent = (
				<div style={activeCanvasPaneStyle}>
					{canvasFrameToolbarContent}
					{canvasViewportContent}
				</div>
			);
			const itemListPanelBody = (
				<>
					<div>
						<div style={{ color: "#6b7280", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Item List</div>
						<div style={{ color: "#4b5563", fontSize: "13px", lineHeight: 1.5, marginTop: "8px" }}>
							Select items here, hide or show them, rename inline, and use list order to control stacking.
						</div>
					</div>
					<div style={{ paddingTop: "14px", borderTop: "1px solid #e5e7eb" }}>
						{this.renderItemList()}
					</div>
				</>
			);
			const propertiesPanelBody = (
				<>
					<div>
						<div style={{ color: "#6b7280", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Canvas</div>
						<div style={{ color: "#4b5563", fontSize: "13px", lineHeight: 1.5, marginTop: "8px" }}>
							{this.state.canvasMode === "edit"
								? "Drag on empty canvas space to create a new item. Use the smaller move nub or the properties here to refine it."
								: "Preview renders the player view on the same padded authoring surface so you can scrub timing without leaving the editor layout."}
						</div>
					</div>
					{this.state.canvasMode === "edit" && (
						<div style={{ paddingTop: "14px", borderTop: "1px solid #e5e7eb", display: "flex", flexDirection: "column", gap: "12px" }}>
							<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
								<Button
									bsSize="xsmall"
									onClick={this.handleToggleDeviceGuidesPanelExpanded}
									style={{ display: "flex", alignItems: "center", gap: "6px" }}
								>
									<FontAwesomeIcon icon={this.state.deviceGuidesPanelExpanded ? "chevron-down" : "caret-right"} />
									<span>Device Guides</span>
								</Button>
								<label style={{ display: "flex", alignItems: "center", gap: "8px", margin: 0, color: "#374151", fontWeight: 500 }}>
									<input type="checkbox" checked={this.state.showDeviceGuides} onChange={this.handleToggleDeviceGuides} />
									<span>Show</span>
								</label>
							</div>
							{this.state.deviceGuidesPanelExpanded && (
								<>
									<div style={{ color: "#4b5563", fontSize: "13px", lineHeight: 1.5 }}>
										Guides use {_.get(this.getDeviceGuideReferenceRect(), "label", "the image")} as the reference frame and reflect the current image resize mode: <strong>{this.getCurrentImageResizeMode()}</strong>.
									</div>
									<div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
										<Button bsSize="xsmall" onClick={this.handleSelectPrimaryDeviceGuides}>Primary Set</Button>
										<Button bsSize="xsmall" onClick={this.handleSelectAllDeviceGuides}>All Guides</Button>
									</div>
									<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
										{DEVICE_GUIDE_OPTIONS.map((guide) => {
											const checked = this.state.activeDeviceGuideIds.includes(guide.id);
											return (
												<label key={`device-guide-toggle-${guide.id}`} style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", alignItems: "center", gap: "10px", margin: 0, padding: "8px 10px", borderRadius: "10px", border: "1px solid #dbe2ea", background: checked ? "#f8fbff" : "#ffffff" }}>
													<input type="checkbox" checked={checked} onChange={() => { this.handleToggleDeviceGuideSelection(guide.id); }} />
													<div style={{ minWidth: 0 }}>
														<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
															<div style={{ width: "10px", height: "10px", borderRadius: "999px", background: guide.color, flex: "0 0 auto" }} />
															<div style={{ color: "#111827", fontWeight: 600, fontSize: "13px" }}>{guide.label}</div>
														</div>
													</div>
													<div style={{ color: "#6b7280", fontSize: "12px", whiteSpace: "nowrap" }}>{`${guide.width} x ${guide.height}`}</div>
												</label>
											);
										})}
									</div>
								</>
							)}
						</div>
					)}
					<div style={{ paddingTop: "14px", borderTop: "1px solid #e5e7eb" }}>
						<div style={{ color: "#6b7280", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600, marginBottom: "10px" }}>Selected Item</div>
						{this.renderSelectedItemPanel()}
					</div>
					{selectedItem && !selectedItemIsCamera && selectedItem.type === "hotspot" && (
						<div style={{ paddingTop: "14px", borderTop: "1px solid #e5e7eb", color: "#6b7280", fontSize: "13px", lineHeight: 1.5 }}>
							Hotspots keep the existing playback schema. `gotoNext` hotspots will still behave the same way in the players.
						</div>
					)}
				</>
			);
			const itemListPanelContent = (
				<div style={itemListPanelStyle}>
					{itemListPanelBody}
				</div>
			);
			const propertiesPanelContent = (
				<div style={propertiesPanelStyle}>
					{propertiesPanelBody}
				</div>
			);
			const stackedRightSplitPanelContent = (
				<div style={stackedRightPanelStyle}>
					<div style={stackedRightSectionStyle}>
						{itemListPanelBody}
					</div>
					<div style={stackedRightSectionStyle}>
						{propertiesPanelBody}
					</div>
				</div>
			);
			const stackedRightAppendPanelContent = (
				<div style={propertiesPanelStyle}>
					{itemListPanelBody}
					<div style={{ paddingTop: "14px", marginTop: "14px", borderTop: "1px solid #e5e7eb" }}>
						{propertiesPanelBody}
					</div>
				</div>
			);
			const canvasPopoutPlaceholderContent = (
				<div
					style={Object.assign({}, activeCanvasPaneStyle, {
						padding: "24px"
					})}
				>
					{canvasFrameToolbarContent}
					<div style={{ flex: "1 1 auto", minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
						<div style={{ maxWidth: "320px", display: "flex", flexDirection: "column", gap: "12px", alignItems: "center" }}>
						<div style={{ fontSize: "18px", fontWeight: 600, color: "#0f172a" }}>Canvas Popped Out</div>
						<div style={{ color: "#475569", fontSize: "14px", lineHeight: 1.5 }}>
							The live image canvas is open in a separate window. Close that window or return it here to continue editing in this modal.
						</div>
						<Button bsStyle="primary" onClick={this.handleCanvasPopoutClosed}>
							Return Canvas
						</Button>
						</div>
					</div>
				</div>
			);
			const inlineCanvasWorkspaceContent = canvasPopoutOpen
				? canvasPopoutPlaceholderContent
				: canvasWorkspaceContent;
			const canvasPopoutContent = (
				<div style={canvasPopoutPaneStyle}>
					{canvasViewportContent}
				</div>
			);

			return (
			<Modal
				open={this.props.open}
				onClose={this.props.onClose}
				closeOnEsc={false}
				classNames={{ overlay: "video-chunk-modal-overlay", modal: "video-chunk-modal-fullscreen" }}
				styles={{
					overlay: {
						padding: 0,
						margin: 0,
						display: "block",
						overflow: "hidden"
					},
					modal: {
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						width: "100%",
						height: "100%",
						maxWidth: "none",
						maxHeight: "none",
						margin: 0,
						padding: 0,
						borderRadius: 0,
						boxSizing: "border-box"
					}
				}}
				closeButton
			>
				<div style={{ width: "100%", maxWidth: "none", height: "100%", maxHeight: "100%", overflow: "hidden", display: "flex", flexDirection: "column", gap: "8px", minHeight: 0, padding: "10px 12px 8px", boxSizing: "border-box" }} onMouseDown={(event) => { event.stopPropagation(); }}>
					<div style={headerStyle}>
						<div />
						<div>
							<h3 style={{ marginTop: 0, marginBottom: 0, textAlign: "center" }}>Media Markup</h3>
						</div>
						<div style={{ paddingRight: "48px" }} />
					</div>
					<div style={bodyStyle}>
						<ButtonToolbar style={{ flex: "0 0 auto", backgroundColor: "#dddddd", padding: "6px 8px", borderRadius: "12px", width: "100%", overflowX: "auto", overflowY: "hidden" }}>
							<div style={toolbarRowStyle}>
								<div style={toolbarClusterStyle}>
									<div style={toolbarSectionStyle}>
										<div style={toolbarFieldStartStyle}>
											<div style={toolbarFieldLabelStyle}>Create</div>
											<select onChange={this.handleComponentTypeChange} value={this.state.componentType}>
												<option value="camera">Camera</option>
												<option value="highlight">Highlight</option>
												<option value="hotspot">Hotspot</option>
											</select>
										</div>
										{showOverlayControls && (
											<div style={toolbarCheckboxFieldStyle}>
												<div style={toolbarFieldLabelPlaceholderStyle}>Auto Camera</div>
												<label style={toolbarCheckboxLabelStyle}>
													<input type="checkbox" style={{ margin: 0 }} checked={this.state.autoCreateCamera} onChange={this.handleAutoCreateCameraChange} />
													<span>Auto Camera</span>
												</label>
											</div>
										)}
										{showOverlayControls && (
											<>
												<div style={toolbarFieldStyle}>
													<div style={toolbarFieldLabelStyle}>Shape</div>
													<select onChange={this.handleShapeChange} value={this.state.shape}>
														{OVERLAY_SHAPE_OPTIONS.map((option) => (
															<option key={`overlay-shape-${option.value}`} value={option.value}>{option.label}</option>
														))}
													</select>
												</div>
												<div style={toolbarFieldStyle}>
													<div style={toolbarFieldLabelStyle}>Preset</div>
													<select onChange={this.handleStylePresetChange} value={this.state.stylePreset}>
														{this.state.stylePreset === "custom" && <option value="custom">Custom</option>}
														{Object.entries(OVERLAY_STYLE_PRESETS).map(([value, preset]) => (
															<option key={`overlay-style-preset-${value}`} value={value}>{preset.label}</option>
														))}
													</select>
												</div>
												<div style={toolbarFieldStyle}>
													<div style={toolbarFieldLabelStyle}>Border Color</div>
													<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
														<ColorPicker
															id="popover-media-markup-v2-border-color"
															title="Markup Border Color"
															onChangeColor={this.handleBorderColorChange}
															onClearColor={() => {}}
															presetColors={env.defaultColors}
															color={this.state.borderColor}
															placement="bottom"
															hideClear={true}
															disableAlpha={false}
															container={colorPopoverContainer}
															popoverStyle={colorPopoverStyle}
														/>
														<Button
															bsSize="small"
															bsStyle={this.state.colorPickTarget === COLOR_PICK_TARGETS.border ? "info" : "default"}
															onClick={() => { this.handleColorPickToggle(COLOR_PICK_TARGETS.border); }}
															title={this.state.colorPickTarget === COLOR_PICK_TARGETS.border ? "Cancel border color eyedropper" : "Pick border color from image"}
															style={{ height: "32px", padding: "4px 8px" }}
															disabled={this.state.canvasMode !== "edit"}
														>
															<FontAwesomeIcon icon="crosshairs" />
														</Button>
													</div>
												</div>
												<div style={toolbarFieldStyle}>
													<div style={toolbarFieldLabelStyle}>Border Width</div>
													<select onChange={this.handleBorderWidthChange} value={this.state.borderWidth}>
														{BORDER_WIDTH_OPTIONS.map((option) => <option key={`markup-v2-border-width-${option}`} value={option}>{option}</option>)}
													</select>
												</div>
												<div style={toolbarFieldStyle}>
													<div style={toolbarFieldLabelStyle}>Border Radius</div>
													<select onChange={this.handleBorderRadiusChange} value={shapeSupportsBorderRadius(this.state.shape) ? this.state.borderRadius : 0} disabled={!shapeSupportsBorderRadius(this.state.shape)}>
														{BORDER_RADIUS_OPTIONS.map((option) => <option key={`markup-v2-border-radius-${option}`} value={option}>{option}</option>)}
													</select>
												</div>
												{shapeSupportsFill(this.state.shape) && this.state.fillEnabled && (
													<div style={toolbarFieldStyle}>
														<div style={toolbarFieldLabelStyle}>Fill Color</div>
														<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
															<ColorPicker
																id="popover-media-markup-v2-fill-color"
																title="Markup Fill Color"
																onChangeColor={this.handleFillColorChange}
																onClearColor={() => {}}
																presetColors={env.defaultColors}
																color={this.state.fillColor}
																placement="bottom"
																hideClear={true}
																disableAlpha={false}
																container={colorPopoverContainer}
																popoverStyle={colorPopoverStyle}
															/>
															<Button
																bsSize="small"
																bsStyle={this.state.colorPickTarget === COLOR_PICK_TARGETS.fill ? "info" : "default"}
																onClick={() => { this.handleColorPickToggle(COLOR_PICK_TARGETS.fill); }}
																title={this.state.colorPickTarget === COLOR_PICK_TARGETS.fill ? "Cancel fill color eyedropper" : "Pick fill color from image"}
																style={{ height: "32px", padding: "4px 8px" }}
																disabled={this.state.canvasMode !== "edit"}
															>
																<FontAwesomeIcon icon="crosshairs" />
															</Button>
														</div>
													</div>
												)}
												<div style={toolbarCheckboxFieldStyle}>
													<div style={toolbarFieldLabelPlaceholderStyle}>Fill</div>
													<label style={Object.assign({}, toolbarCheckboxLabelStyle, { gap: "8px", opacity: shapeSupportsFill(this.state.shape) ? 1 : 0.55 })}>
														<input type="checkbox" style={{ margin: 0 }} checked={shapeSupportsFill(this.state.shape) ? this.state.fillEnabled : false} onChange={this.handleFillEnabledChange} disabled={!shapeSupportsFill(this.state.shape)} />
														<span>Fill</span>
													</label>
												</div>
												{this.state.colorPickTarget && (
													<div style={{ color: "#0f766e", fontSize: "12px", fontWeight: 700, lineHeight: 1.5, maxWidth: "240px" }}>
														{`Eyedropper active: click the image to sample ${this.getColorPickTargetLabel()}.`}
													</div>
												)}
											</>
										)}
									</div>
									<div style={toolbarSectionDividerStyle} />
									<div style={toolbarButtonSectionStyle}>
										<Button bsStyle="default" bsSize="small" onClick={this.handleUndo} disabled={!canUndo} title="Undo (Cmd/Ctrl+Z)">
											<FontAwesomeIcon icon="undo" />
										</Button>
										<Button bsStyle="default" bsSize="small" onClick={this.handleRedo} disabled={!canRedo} title="Redo (Shift+Cmd/Ctrl+Z or Ctrl+Y)">
											<FontAwesomeIcon icon="redo" />
										</Button>
										<Button bsStyle={this.state.showLabels ? "primary" : "default"} bsSize="small" onClick={this.handleToggleLabels} title={this.state.showLabels ? "Hide item labels on canvas" : "Show item labels on canvas"}>
											<FontAwesomeIcon style={{ fontSize: "12px" }} icon={this.state.showLabels ? "align-slash" : "align-justify"} />
											<span style={{ fontSize: "12px", paddingLeft: "5px" }}>Labels</span>
										</Button>
										{this.state.canvasMode === "edit" && (
											<Button bsStyle={this.state.showDeviceGuides ? "info" : "default"} bsSize="small" onClick={this.handleToggleDeviceGuides} title="Toggle device aspect guides on the edit canvas">
												<FontAwesomeIcon icon="mobile-alt" />
												<span style={{ fontSize: "12px", paddingLeft: "5px" }}>Guides</span>
											</Button>
										)}
									</div>
								</div>
								<div style={toolbarTrailingClusterStyle}>
									<div style={toolbarSectionStyle}>
										<div style={toolbarFieldStyle}>
											<div style={toolbarFieldLabelStyle}>Device Layer</div>
											<select style={{ borderRadius: "5px", border: "1px solid #cccccc", marginBottom: 0, minWidth: "108px" }} value={this.state.activeCameraVariant} onChange={this.handleActiveCameraVariantChange}>
												{CAMERA_VARIANT_OPTIONS.map((option) => (
													<option key={`camera-variant-option-${option.value}`} value={option.value}>{option.label}</option>
												))}
											</select>
										</div>
										{this.state.canvasMode === "edit" && (
											<div style={toolbarFieldStyle}>
												<div style={toolbarFieldLabelStyle}>Zoom</div>
												<select style={{ borderRadius: "5px", border: "1px solid #cccccc", marginBottom: 0, minWidth: "50px" }} value={this.state.editScale} onChange={this.handleEditScaleChange}>
													{ZOOM_OPTIONS.map((option) => <option key={`media-markup-v2-edit-zoom-${option}`} value={option}>{option}%</option>)}
												</select>
											</div>
										)}
										{this.state.canvasMode === "edit" && (
											<div style={toolbarFieldStyle}>
												<div style={toolbarFieldLabelStyle}>Pad</div>
												<select style={{ borderRadius: "5px", border: "1px solid #cccccc", marginBottom: 0, minWidth: "64px" }} value={this.state.canvasPadding} onChange={this.handleCanvasPaddingChange} title="Extra authoring space around the image and camera overscan bounds">
													{EDITOR_CANVAS_PADDING_OPTIONS.map((option) => (
														<option key={`media-markup-v2-canvas-padding-${option}`} value={option}>{`${option}px`}</option>
													))}
												</select>
											</div>
										)}
										<div style={toolbarFieldStyle}>
											<div style={toolbarFieldLabelStyle}>Layout</div>
											<select style={{ borderRadius: "5px", border: "1px solid #cccccc", marginBottom: 0, minWidth: "112px" }} value={layoutMode} onChange={this.handleLayoutModeChange}>
												<option value={LAYOUT_MODE_SPLIT}>Split</option>
												<option value={LAYOUT_MODE_STACKED_RIGHT_SPLIT}>Right Split</option>
												<option value={LAYOUT_MODE_STACKED_RIGHT_APPEND}>Right Append</option>
											</select>
										</div>
										{!this.isEditingBaseCameraVariant() && (
											<div style={toolbarButtonSectionStyle}>
												<Button bsSize="small" onClick={this.handleAutoFitAllCamerasForVariant} title={`Auto-fit all cameras for ${this.getCameraVariantSummaryLabel()}`}>
													Auto-fit
												</Button>
												<Button bsSize="small" onClick={this.handleResetActiveCameraVariant} title={`Reset all ${this.getCameraVariantSummaryLabel()} camera overrides`}>
													Reset
												</Button>
											</div>
										)}
									</div>
									<div style={toolbarSectionDividerStyle} />
									<div style={toolbarButtonSectionStyle}>
										{(compactLayout || layoutMode === LAYOUT_MODE_SPLIT) && (
											<Button bsStyle={showLeftPanel ? "primary" : "default"} bsSize="small" onClick={this.handleToggleLeftPanelCollapsed} title={showLeftPanel ? "Hide item list" : "Show item list"}>
												List
											</Button>
										)}
										<Button bsStyle={showRightPanel ? "primary" : "default"} bsSize="small" onClick={this.handleToggleRightPanelCollapsed} title={showRightPanel ? (isStackedRightLayout ? "Hide sidebar" : "Hide inspector") : (isStackedRightLayout ? "Show sidebar" : "Show inspector")}>
											{isStackedRightLayout ? "Sidebar" : "Inspector"}
										</Button>
										{allowCanvasPopout && (
											<Button bsStyle={canvasPopoutOpen ? "info" : "default"} bsSize="small" onClick={this.handleToggleCanvasPopout} disabled={!imageSize} title={canvasPopoutOpen ? "Return the image canvas to this window" : "Open the image canvas in a separate window"}>
												{canvasPopoutOpen ? "Return Canvas" : "Canvas"}
											</Button>
										)}
									</div>
								</div>
							</div>
						</ButtonToolbar>

							<div ref={this.workspaceRef} style={workspaceStyle}>
								{compactLayout
									? (
										<>
											{inlineCanvasWorkspaceContent}
											{showLeftPanel && itemListPanelContent}
											{showRightPanel && propertiesPanelContent}
										</>
									)
									: (
										<>
											{layoutMode === LAYOUT_MODE_SPLIT && showLeftPanel && itemListPanelContent}
											{layoutMode === LAYOUT_MODE_SPLIT && showLeftPanel && (
												<div style={panelResizerStyle} onMouseDown={(event) => { this.startPanelResize("left", event); }} />
											)}
											{inlineCanvasWorkspaceContent}
											{showRightPanel && (
												<>
													<div style={panelResizerStyle} onMouseDown={(event) => { this.startPanelResize("right", event); }} />
													{layoutMode === LAYOUT_MODE_STACKED_RIGHT_SPLIT
														? stackedRightSplitPanelContent
														: layoutMode === LAYOUT_MODE_STACKED_RIGHT_APPEND
															? stackedRightAppendPanelContent
															: propertiesPanelContent}
												</>
											)}
										</>
									)}
							</div>

						<div style={waveformDockStyle}>
							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px", flexWrap: "wrap" }}>
								{imageSize
									? (
										<div style={{ flex: "1 1 720px", minWidth: "280px" }}>
											{this.renderPreviewPlaybackControls({
												showSlider: !this.props.audioUrl || this.state.waveformCollapsed,
												useBottomScrubberHint: !!this.props.audioUrl && !this.state.waveformCollapsed,
												showViewportInfo: this.state.canvasMode === "preview"
											})}
										</div>
									)
									: <div />}
								<div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginLeft: "auto" }}>
									<Button bsSize="small" onClick={this.handleToggleWaveformCollapsed}>
										<FontAwesomeIcon icon={this.state.waveformCollapsed ? "chevron-up" : "chevron-down"} /> {this.state.waveformCollapsed ? "Show Scrubber" : "Hide Scrubber"}
									</Button>
								</div>
							</div>
							{this.props.audioUrl
								? (
									<div
										aria-hidden={this.state.waveformCollapsed ? "true" : undefined}
										style={this.state.waveformCollapsed
											? {
												maxHeight: 0,
												overflow: "hidden",
												opacity: 0,
												pointerEvents: "none"
											}
											: undefined}
									>
										<AudioWaveform
											src={this.props.audioUrl}
											compact
											hideTransportControls
											onRegionSelected={this.onAudioWaveformRegionSelected}
											onCursorSelected={this.onAudioWaveformCursorSelected}
											onTimeChange={this.onAudioWaveformTimeChange}
											onPlayStateChange={this.onAudioWaveformPlayStateChange}
											onFinish={this.onAudioWaveformFinish}
											onReady={this.onAudioWaveformReady}
											onControllerReady={this.onAudioWaveformControllerReady}
										/>
									</div>
								)
								: (
									!this.state.waveformCollapsed && (
										<div style={{ padding: "12px 14px", border: "1px dashed #cbd5e1", borderRadius: "12px", background: "#f8fafc", color: "#6b7280", fontSize: "13px" }}>
											No synced audio file was found on this item. Preview still works, but waveform scrubbing is unavailable until the item has audio such as `label1AudioFile`.
										</div>
									)
								)}
							{!this.state.waveformCollapsed && imageSize && this.renderBottomTimelineSlider()}
						</div>
					</div>
					<div style={footerStyle}>
						<Button onClick={this.props.onClose}>Cancel</Button>
						<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
							<Button bsStyle="danger" onClick={this.handleClearAll} title="Remove every overlay and camera">
								<FontAwesomeIcon icon="trash" /> Clear All
							</Button>
							<Button bsStyle="primary" onClick={() => { this.handleSave(); }}>Save</Button>
							<Button bsStyle="success" onClick={this.handleSaveAndClose}>Save &amp; Close</Button>
						</div>
					</div>
					<PopoutWindowPortal
						open={!!(this.props.open && canvasPopoutOpen)}
						title="Media Markup Canvas"
						name="media-markup-v2-canvas-popout"
						onClose={this.handleCanvasPopoutClosed}
						backgroundColor="#e2e8f0"
						maximize
					>
						<div style={canvasPopoutViewportStyle}>
							{imageSize && (
								<div style={canvasPopoutHudStyle}>
									{imageSizeLabel && (
										<div style={canvasImageSizeBadgeStyle}>
											{imageSizeLabel}
										</div>
									)}
									<div style={canvasStatusBadgeStyle}>
										{canvasStatusLabel}
									</div>
								</div>
							)}
							{canvasPopoutContent}
						</div>
					</PopoutWindowPortal>
				</div>
			</Modal>
		);
	}
}
